import HearenField from '@/components/HearenField';
import { NativeAudio } from '@/native/nativeAudio';
import { nativeAudioPlayer } from '../native/nativeAudio/nativeAudioPlayer';
import { registerSessionStopHandler } from '@/services/sessionStopRegistry';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Pause, Volume2, Music, Star, RefreshCw, AudioWaveform, AlertCircle, Lock, Crown, CheckCircle, Plus, Square, ListMusic, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { API_BASE_URL, buildAudioUrl, reencodeAudioPath } from '@/services/httpClient';
import audioService from '@/services/audioService';
// BUILD 66: Import soundscapeService for iOS-compatible playback
import soundscapeService from '@/services/soundscapeService';
import TransitionDebug from '@/components/TransitionDebug';
// V1.1.1: Import presenceService for voice overlays
import presenceService from '@/services/presenceService';
import userManager from '@/services/userManager';
import { useTherapyTimer } from '@/hooks/useTherapyTimer';
import therapyTimer from '@/services/therapyTimer';
import HelpIcon from '@/components/HelpIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useFullscreen } from '@/contexts/FullscreenContext';
import CircularTherapyTimer from '@/components/CircularTherapyTimer';
import CoachTherapyBanner from '@/components/CoachTherapyBanner';
import FullscreenTimerModal from '@/components/FullscreenTimerModal';
import ContentBadge from '@/components/ContentBadge';
import { CANONICAL_THEMES, LEGACY_ID_MAP, normalizeThemeId, isThemeFree, FREE_THEME_IDS, FREE_SOUNDSCAPE_THEME_IDS, FREE_PRESENCE_THEME_IDS, FREE_TRACKS_PER_THEME, FREE_FAVORITES_LIMIT } from '@/config/themes';
import { resolveBackendId } from '@/config/themesV11';
import { AUDIO_BANDS_COUNT } from '@/config/audioProfile';
import { UnifiedPlayer } from '@/components/player';
import AudioProfileModal from '@/components/AudioProfileModal';
import ComfortMessage from '@/components/ComfortMessage';
import useLanguage from '@/hooks/useLanguage';
import useSafeListening from '@/hooks/useSafeListening';
import ThemeIcon from '@/components/ThemeIcon';
import { extToM4a } from '@/utils/audioUtils';
import { quantizeDb } from '@/utils/tinnitusDisplay';
import { useSwipeBack } from '@/hooks/useSwipeBack';
import { getCanonicalTinnitusProfile } from '@/utils/tinnitusProfile';
// BUILD 56: Performance - Skeletons and catalog service
import { TrackListSkeleton, TabsSkeleton, LoadingIndicator } from '@/components/ui/Skeletons';
import { useNowPlaying } from '@/contexts/NowPlayingContext';
import { 
  getSoundscapesTracks, 
  preloadSoundscapesCatalog,
  saveSoundscapesTheme,
  getLastSoundscapesTheme,
  isTracksCached
} from '@/services/catalogService';

const USE_NATIVE_AUDIO_PLAYER = true;

const TherapyPlayer = ({ t }) => {
  const navigate = useNavigate();
  const { lang: currentLang } = useLanguage(); // V1.1: Reactive language
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const playlistIdFromUrl = searchParams.get('playlist');
  const viewMode_param = searchParams.get('mode');
  const rawThemeFromUrl = searchParams.get('theme');

const normalizeRouteTheme = (themeId) => {
  const id = String(themeId || '').toLowerCase().trim();

  if (id === 'instrumental' || id === 'instrumentale' || id === 'classique') {
    return 'classical';
  }

  if (id === 'lofi' || id === 'chill' || id === 'chill_lofi') {
    return 'chill_lofi';
  }

  return id;
};

const themeFromUrl = normalizeRouteTheme(rawThemeFromUrl);
  console.warn('[THEME URL DEBUG]', {
    rawThemeFromUrl,
    themeFromUrl,
    canonicalThemes: CANONICAL_THEMES?.map(t => t.id),
  });
  const trackFromUrl = searchParams.get('track');
  const autoplayFromUrl = searchParams.get('autoplay') === '1';
  const resumeFromMiniPlayer = searchParams.get('resume') === '1' || location.state?.resumeFromMiniPlayer;

useEffect(() => {
  console.log('[THERAPY] Mounted/params', {
    pathname: location.pathname,
    search: location.search,
    playlistIdFromUrl,
    viewMode_param,
    themeFromUrl,
    trackFromUrl,
    autoplayFromUrl,
    resumeFromMiniPlayer
  });
}, [location.pathname, location.search]);



const { user } = useAuth();
const { setIsFullscreen } = useFullscreen();

const {
  nowPlaying,
  setTrack: setNowPlaying,
  clearTrack: clearNowPlaying
} = useNowPlaying();

const explicitStopRef = useRef(false);
const isHeraSpeakingRef = useRef(false);

const keepAudioAliveOnBackRef = useRef(false);

// ⭐ PREMIUM FEATURES
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';
  
  // V1.1: Determine if we should show the Playlists entry button
  // Hide when coming from Home (mode=soundscapes or mode=presence or theme=X), show when coming from TabBar Audio
  // V2 GATING: Show for free users too, but locked (redirect to /premium)
  const showPlaylistsEntry = !viewMode_param && !themeFromUrl;
  
  // V1.1: Voice ON/OFF toggle — session only, default ON
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const voiceEnabledRef = useRef(voiceEnabled);
  const voiceToggleLockRef = useRef(false);
  
  
  // Get Coach session info from navigation state or sessionStorage
  const coachSessionFromNav = location.state || {};
  
  // Try to restore from sessionStorage if not in navigation state
  const [coachSession, setCoachSession] = useState(() => {
    if (coachSessionFromNav.fromCoach) {
      // Store in sessionStorage for persistence across navigation
      sessionStorage.setItem('activeCoachSession', JSON.stringify(coachSessionFromNav));
      return coachSessionFromNav;
    }
    
    // Try to restore from sessionStorage
    const stored = sessionStorage.getItem('activeCoachSession');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if session is still valid (not older than 1 hour)
        if (parsed.sessionDate && new Date(parsed.sessionDate).getTime() > Date.now() - 3600000) {
          return parsed;
        }
      } catch (e) {
        console.error('Failed to parse stored coach session:', e);
      }
    }
    
    return {};
  });
  
  const { recommendedDuration, sessionDate, sessionProtocol, fromCoach } = coachSession;
  
  // SOS Calm mode from navigation
  const sosMode = location.state?.mode === 'sos_calm';
  const sosSound = location.state?.sound;
  const sosAutoPlay = location.state?.autoPlay;
  const sosVolume = location.state?.volume;
  
  // V1.1.1: Guided mode from navigation (Home -> Guided card)
  // V1.1: Also activate presence mode when voice is ON (from any entry point)
  const presenceModeFromNav = voiceEnabled || location.state?.mode === 'presence' || searchParams.get('mode') === 'presence' 
    || location.state?.mode === 'guided' || searchParams.get('mode') === 'guided'; // backward compat
  
  // Navigation state: 'selection' or 'settings'
  // If loading a playlist directly, skip selection screen
  // V1.2 FIX: If resuming from mini player AND audio is active, start in 'player' mode immediately
  const _audioActiveOnMount = resumeFromMiniPlayer && (soundscapeService.playbackState === 'PLAYING' || soundscapeService.playbackState === 'PAUSED');
  const [viewMode, setViewMode] = useState(() => {
  if (resumeFromMiniPlayer) return 'player'; // 👈 correction clé

  if (_audioActiveOnMount) return 'player';
  if (playlistIdFromUrl) return 'settings';
  if (themeFromUrl && autoplayFromUrl) return 'player';
  return 'selection';
});
console.log('[THERAPY] Initial viewMode computed', {
  viewMode,
  themeFromUrl,
  autoplayFromUrl,
  playlistIdFromUrl,
  resumeFromMiniPlayer
});
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState(!!playlistIdFromUrl);
  const [showSpectrumPanel, setShowSpectrumPanel] = useState(false);
  
  // BUILD 56: Loading states for progressive rendering
  const [isLoadingSounds, setIsLoadingSounds] = useState(true);
  const [isLoadingTracks, setIsLoadingTracks] = useState(false);
  
  // BUILD 64: Loading state for category change (prevents "No sounds" during load)
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const [categoryLoadError, setCategoryLoadError] = useState(null);
  
  // BUILD 65: Loading state for playback (controls button spinner)
  const [isLoadingPlayback, setIsLoadingPlayback] = useState(false);
  
  // Scroll to top when entering selection mode to show first category
  useEffect(() => {
    if (viewMode === 'selection') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [viewMode]);

  useEffect(() => {
  const listener = NativeAudio.addListener('playbackStateChanged', (event) => {
    console.log('[NATIVE EVENT]', event);

    const playing = event.isPlaying === true;

    setIsPlaying(playing);

    setNowPlaying({
      isPlaying: playing,
      playerState: {
        ...(nowPlaying?.playerState || {}),
        isPlaying: playing,
      },
    });
  });

  return () => {
    listener.remove();
  };
}, [nowPlaying, setNowPlaying]);
  
  const [selectedStyle, setSelectedStyle] = useState(() => {
    if (resumeFromMiniPlayer) {
      const ps = location.state?.playerState || nowPlaying?.playerState;
      if (ps?.selectedStyle) return ps.selectedStyle;

      const ct = soundscapeService.currentTrack;
      if (ct?.category) return ct.category;
    }

    if (themeFromUrl) {
      if (['favorites', 'playlist', 'custom'].includes(themeFromUrl)) {
        return themeFromUrl;
      }

      const backendId = resolveBackendId(themeFromUrl);
      if (backendId) return backendId;
    }

    return 'nature';
  });
  useEffect(() => {
    setVoiceEnabled(true);
    voiceEnabledRef.current = true;

    setEqualizerEnabled(true);
    setNotchEnabled(true);

    console.log('[Hearen] Theme changed → Voice / Audio profile / Tinnitus ON:', selectedStyle);
  }, [selectedStyle]);
  
  // Music volume: always 80% default, non-persistent (resets each session)
  const [volume, setVolume] = useState(10);
  const [volumeInitialized, setVolumeInitialized] = useState(false);
  // V1.2 FIX: Pre-initialize isPlaying and selectedSound when resuming from mini player
  const [isPlaying, setIsPlaying] = useState(() => {
    if (_audioActiveOnMount && soundscapeService.playbackState === 'PLAYING') return true;
    return false;
  });
  const [selectedSound, setSelectedSound] = useState(() => {

  if (resumeFromMiniPlayer) {
    const ps = location.state?.playerState || nowPlaying?.playerState;
    const serviceTrack = soundscapeService.currentTrack;
    return serviceTrack || ps?.selectedSound || null;
  }
  return null;
});
  const [customSounds, setCustomSounds] = useState([]);
  const [currentAudioSource, setCurrentAudioSource] = useState(null);
  
  // V1.1.1: Guided mode state
  const [isPresenceMode, setIsPresenceMode] = useState(false);
  const isPresenceModeRef = useRef(false); // Ref for timer closures
  const playlistWatcherRef = useRef(null); // V2.2.5: Polling watcher for playlist auto-advance
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [presenceVoiceCount, setPresenceVoiceCount] = useState(0);
  
  // V1.1.1: Voice volume state (default 40%, persistent via localStorage)
  const [voiceVolume, setVoiceVolume] = useState(() => {
    // Initialize from presenceService which handles localStorage
    return Math.round(presenceService.getVoiceVolume() * 100);
  });
  
  // V1.1: Non Persist voice toggle and sync ref
  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);
  
  // V1.1: If theme from URL, go directly to settings (skip selection)
useEffect(() => {
  const syncThemeFromUrl = async () => {
    // V1.2 FIX: Resume from Mini Player — robust restoration
    // Use multiple sources: location.state, context, and soundscapeService singleton
    const isAudioActive =
      soundscapeService.playbackState === 'PLAYING' ||
      soundscapeService.playbackState === 'PAUSED';

    if (resumeFromMiniPlayer && isAudioActive) {
      // If user explicitly chose a DIFFERENT theme, don't resume — load new theme
      const requestedTheme = themeFromUrl ? (resolveBackendId(themeFromUrl) || themeFromUrl) : null;
      const serviceTrack = soundscapeService.currentTrack;
      const ps = location.state?.playerState || nowPlaying?.playerState;
      const currentTheme = ps?.selectedStyle || serviceTrack?.category;

    if (requestedTheme && requestedTheme !== currentTheme) {
      // User chose a different theme — stop current playback, load new theme
      console.log('[ThemeSwitch] Stopping current audio for new theme', {
        from: currentTheme,
        to: requestedTheme
      });

      // FIX: prevent old track flash during theme switch
      setSelectedSound(null);
      setIsPlaying(false);
      currentPlayingSoundRef.current = null;
      currentPlayingIndexRef.current = -1;
      preloadedNextRef.current = null;
      isTransitioningRef.current = false;

      await performSessionTeardown('theme-switch');

      // ✅ Re-arm playback for the new theme after canonical stop
      explicitStopRef.current = false;
      autoplayTriggeredRef.current = false;
      themeSwitchInProgressRef.current = true;
      allowNextAutoplayRef.current = true;

        // ✅ CRUCIAL: start a fresh session namespace after canonical stop
        playbackSessionRef.current += 1;
        console.log('[Session] NEW session →', playbackSessionRef.current);

        sessionThemeRef.current = requestedTheme;
        setSelectedStyle(requestedTheme);
        setSelectedSound(null);
        setIsPlaying(false);
        setViewMode(trackFromUrl || autoplayFromUrl ? 'player' : 'settings');
        return;
      }

      const restoredSound = serviceTrack || ps?.selectedSound;
      const restoredStyle = ps?.selectedStyle || serviceTrack?.category || themeFromUrl;

      if (restoredSound) {
        setSelectedSound(restoredSound);
        if (restoredStyle) setSelectedStyle(restoredStyle);
        setViewMode('player');
        setIsPlaying(soundscapeService.playbackState === 'PLAYING');
        setIsFullscreen(true);
        console.log('[MiniPlayer Resume] Restored player view', {
          restoredSound: restoredSound?.name,
          restoredStyle
        });
        return;
      }
    }

    // Legacy path: restore from context when audio is playing (e.g., forward navigation)
    // BUT: if user explicitly picked a DIFFERENT theme from Home, stop current and load new
    if (soundscapeService.playbackState === 'PLAYING' && nowPlaying?.playerState) {
      const ps = nowPlaying.playerState;
      const requestedTheme = themeFromUrl ? (resolveBackendId(themeFromUrl) || themeFromUrl) : null;
      const currentTheme = ps.selectedStyle;

    if (requestedTheme && requestedTheme !== currentTheme) {
      console.log('[ThemeSwitch] MiniPlayer resume overridden by new theme', {
        from: currentTheme,
        to: requestedTheme
      });

      // FIX: prevent old track flash during theme switch
      setSelectedSound(null);
      setIsPlaying(false);
      currentPlayingSoundRef.current = null;
      currentPlayingIndexRef.current = -1;
      preloadedNextRef.current = null;
      isTransitioningRef.current = false;

      await performSessionTeardown('theme-switch');

      // ✅ Re-arm playback for the new theme after canonical stop
      explicitStopRef.current = false;
      autoplayTriggeredRef.current = false;
      themeSwitchInProgressRef.current = true;
      allowNextAutoplayRef.current = true;

        // ✅ CRUCIAL: start a fresh session namespace after canonical stop
        playbackSessionRef.current += 1;
        console.log('[Session] NEW session →', playbackSessionRef.current);

        sessionThemeRef.current = requestedTheme;
        setSelectedStyle(requestedTheme);
        setSelectedSound(null);
        setIsPlaying(false);
        setViewMode(trackFromUrl || autoplayFromUrl ? 'player' : 'settings');
        return;
      }

      if (ps.selectedSound) setSelectedSound(ps.selectedSound);
      if (ps.selectedStyle) setSelectedStyle(ps.selectedStyle);
      setViewMode('player');
      setIsPlaying(true);
      setIsFullscreen(true);
      return;
    }

    if (themeFromUrl && !themeInitRef.current) {
  const isSpecialMode = ['favorites', 'playlist', 'custom'].includes(themeFromUrl);

  if (isSpecialMode || resolveBackendId(themeFromUrl)) {
    themeInitRef.current = true;

    console.log('[THEME INIT] initializing theme from URL', {
      themeFromUrl,
      autoplayFromUrl,
      trackFromUrl
    });

    if (!isSpecialMode) {
      setSelectedStyle(themeFromUrl);
    }

    setViewMode(trackFromUrl || autoplayFromUrl ? 'player' : 'settings');
  }
}
  };

  syncThemeFromUrl();
}, []); // eslint-disable-line react-hooks/exhaustive-deps

  // V1.2 FIX: Post-mount safety sync — after ALL effects run, double-check selectedSound and index
  // This catches any race where another effect overwrites the initial restoration
  useEffect(() => {
    if (!_audioActiveOnMount) return;
    // Use a microtask delay to run AFTER all synchronous effects complete
    const timer = setTimeout(() => {
      const serviceTrack = soundscapeService.currentTrack;
      if (serviceTrack && serviceTrack.name !== selectedSound?.name) {
        console.log('[Safety Sync] Correcting selectedSound', { was: selectedSound?.name, now: serviceTrack.name });
        setSelectedSound(serviceTrack);
        currentPlayingSoundRef.current = serviceTrack;
      }
      // V1.2 FIX: Also sync currentPlayingIndexRef by finding the track in the library
      const actualTrack = serviceTrack || selectedSound;
      if (actualTrack) {
        const tracks = getTracksForTheme(selectedStyle);
        console.log('[DEBUG THEME]', {
          selectedStyle,
          themeFromUrl,
          tracksCount: tracks.length,
        });
        const matchIdx = tracks.findIndex(t => 
          t.name === actualTrack.name || t.file === actualTrack.file || t.path === actualTrack.path
        );
        if (matchIdx >= 0) {
          console.log('[Safety Sync] Setting currentPlayingIndexRef to', matchIdx);
          currentPlayingIndexRef.current = matchIdx;
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // V1.1: Auto-play ref (used later after favorites are loaded)
  const autoPlayTriggeredRef = useRef(false);
  const themeSwitchInProgressRef = useRef(false);
  
  // ═══════════════════════════════════════════════════════════════════════
  // SWIPE-BACK GESTURE (iOS-like left-edge swipe)
  // Scoped to /therapy catalog views only. Never touches audio.
  // ═══════════════════════════════════════════════════════════════════════
  const swipeBackHandler = useCallback(() => {
    if (viewMode === 'settings') {
      if (themeFromUrl === 'favorites' || themeFromUrl === 'playlist' || playlistIdFromUrl) {
        navigate('/favorites', { replace: true });
      } else if (themeFromUrl) {
        navigate('/', { replace: true });
      } else {
        setViewMode('selection');
      }
    } else if (viewMode === 'selection' && !isPlaying) {
      navigate('/');
    }
  }, [viewMode, isPlaying, navigate, setViewMode, themeFromUrl]);

  useSwipeBack({
    enabled: viewMode === 'settings' || (viewMode === 'selection' && !isPlaying),
    onBack: swipeBackHandler,
  });

  // Music volume is non-persistent — no localStorage save
  
  // FIX 1: Ensure fullscreen mode is synced with viewMode === 'player'
  // This hides the bottom navigation when the player is visible
  useEffect(() => {
    if (viewMode === 'player' && selectedSound) {
      setIsFullscreen(true);
    }
  }, [viewMode, selectedSound, setIsFullscreen]);
  
  // Playlist state
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const currentPlaylistRef = useRef(null); // Sync ref for timer/callback closures
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
  const currentPlaylistIndexRef = useRef(0); // Store actual index in ref for callbacks
  const [playlistStartTime, setPlaylistStartTime] = useState(null);
  
  // Timer state (for general session duration with fade out)
  const [sessionDuration, setSessionDuration] = useState(recommendedDuration || null); // in minutes - use Coach recommendation
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [elapsedTimeBeforePause, setElapsedTimeBeforePause] = useState(0); // Track accumulated time in minutes
  const [timerInterval, setTimerInterval] = useState(null);
  const [timerTick, setTimerTick] = useState(0); // Force re-render for timer display
  
  // NEW: Current session timer (resets to 0 on each Play)
  const [currentSessionElapsed, setCurrentSessionElapsed] = useState(0); // in seconds
  const currentSessionTimerRef = useRef(null);
  
  // Session tracking for history
  const [currentSessionStart, setCurrentSessionStart] = useState(null);

  // Night Mode: single intro voice message for "sleep" theme
  const nightVoicePlayedRef = useRef(false);
  const nightVoiceAudioRef = useRef(null);

  const playNightVoiceMessage = async () => {
    if (nightVoicePlayedRef.current) return; // Play only once per session
    nightVoicePlayedRef.current = true;

    try {
      const res = await fetch(`${API_BASE_URL}/api/voice-messages/night?lang=${currentLang}`);
      if (!res.ok || res.status === 204) return;
      const msg = await res.json();
      if (!msg?.id) return;

      console.log('[NightMode] Playing intro voice:', msg.id);
      const audioUrl = `${API_BASE_URL}/api/voice-messages-audio/${msg.id}?stream=true`;
      const audio = new Audio(audioUrl);
      audio.volume = 0.8;
      nightVoiceAudioRef.current = audio;

      // Duck music volume while voice plays
      soundscapeService.setVolume(0.15);
      audio.onended = () => {
        console.log('[NightMode] Voice ended, restoring music volume');
        soundscapeService.setVolume(volume / 100);
        nightVoiceAudioRef.current = null;
      };
      audio.onerror = () => {
        console.warn('[NightMode] Voice playback error');
        soundscapeService.setVolume(volume / 100);
        nightVoiceAudioRef.current = null;
      };

      // Small delay to let music settle
      setTimeout(() => audio.play().catch(() => {}), 1500);
    } catch (e) {
      console.warn('[NightMode] Error fetching night voice:', e);
    }
  };

  // ============================================
  // CURRENT SESSION TIMER FUNCTIONS
  // ============================================
  
  const startSessionTimer = () => {
    setCurrentSessionElapsed(0);
    
    // Clear any existing timer
    if (currentSessionTimerRef.current) {
      clearInterval(currentSessionTimerRef.current);
    }
    
    // Start new timer that increments every second
    currentSessionTimerRef.current = setInterval(() => {
      setCurrentSessionElapsed(prev => prev + 1);
    }, 1000);
  };
  
  const pauseSessionTimer = () => {
    if (currentSessionTimerRef.current) {
      clearInterval(currentSessionTimerRef.current);
      currentSessionTimerRef.current = null;
    }
  };
  
  const resumeSessionTimer = () => {
    
    // Clear any existing timer
    if (currentSessionTimerRef.current) {
      clearInterval(currentSessionTimerRef.current);
    }
    
    // Resume timer from current value
    currentSessionTimerRef.current = setInterval(() => {
      setCurrentSessionElapsed(prev => prev + 1);
    }, 1000);
  };
  
  const stopSessionTimer = () => {
    if (currentSessionTimerRef.current) {
      clearInterval(currentSessionTimerRef.current);
      currentSessionTimerRef.current = null;
    }
    setCurrentSessionElapsed(0); // Reset to 0
  };

  // Equalizer settings state
  const [equalizerSettings, setEqualizerSettings] = useState(null);
  
  // Toggle states - will be set to true if tests exist
  const [equalizerEnabled, setEqualizerEnabled] = useState(true);
  const [notchEnabled, setNotchEnabled] = useState(true);
  const [labProgram, setLabProgram] = useState(null); // Active Lab Mode program
  
  // Save audio toggle preferences to backend
  const saveAudioToggles = async (eqEnabled, nEnabled) => {
    try {
      const userId = userManager.getUserId();
      await fetch(`${API_BASE_URL}/api/audio-toggles/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ equalizer_enabled: eqEnabled, notch_enabled: nEnabled })
      });
    } catch (e) {
      console.warn('[TherapyPlayer] Could not save audio toggles');
    }
  };
  
  // FIX 1: Audio Profile Modal state (same as Radio and Playlists)
  const compressGain = (gain) => {
  if (!gain || gain <= 0) return 0;

  const maxInput = 60;
  const maxOutput = 10;

  const normalized = Math.log10(1 + gain) / Math.log10(1 + maxInput);

  return Number((normalized * maxOutput).toFixed(2));
};

const smoothAudiometricBands = (bands) => {
  if (!Array.isArray(bands) || bands.length <= 2) return bands;

  return bands.map((band, index) => {
    if (index === 0 || index === bands.length - 1) {
      return band;
    }

    const previous = bands[index - 1];
    const next = bands[index + 1];

    const smoothedGain =
      band.gain * 0.5 +
      previous.gain * 0.25 +
      next.gain * 0.25;

    return {
      ...band,
      gain: Number(smoothedGain.toFixed(2)),
    };
  });
};



  useEffect(() => {
  if (!equalizerSettings) return;

  const bands = [];

  if (equalizerEnabled && equalizerSettings?.audiometric_gains) {

  const audiometricBands = equalizerSettings.audiometric_gains.map((band) => ({
    frequency: Number(band.frequency),
    gain: compressGain(Number(band.gain)),
    bandwidth: 1.0,
  }));

  const smoothedBands = smoothAudiometricBands(audiometricBands);

  smoothedBands.forEach((band) => {
    bands.push(band);
  });
}

  if (notchEnabled) {
    const freq =
      labProgram?.f_t ||
      equalizerSettings?.inverse_notch?.center_frequency;

    const gain =
      labProgram?.gain_db ??
      equalizerSettings?.inverse_notch?.gain ??
      3;

    if (freq) {
      bands.push({
        frequency: Number(freq),
        gain: Number(gain),
        bandwidth: 0.5,
      });
    }
  }

  console.log('🎛️ [HEAREN P1] APPLY NATIVE EQ', {
    equalizerEnabled,
    notchEnabled,
    bands,
  });

  nativeAudioPlayer.setMultiBandEQ(bands)
    .then(() => {
      console.log('✅ [HEAREN P1] Native EQ applied');
    })
    .catch((err) => {
      console.error('❌ [HEAREN P1] Native EQ error', err);
    });

}, [equalizerEnabled, notchEnabled, equalizerSettings, labProgram]);
  // State for library sounds loaded from API
  // Keys match CANONICAL R2 THEMES: classical, chill, breathing, nature, sleep, pulse_flow
  // ⭐ V1: 'ambient' is deprecated - use 'breathing' for relaxation/meditation themes
  const [librarySounds, setLibrarySounds] = useState({
    classical: [],
    chill: [],  // V1: renamed from chill_lofi to match R2 category
    breathing: [],  // V1: replaces both ambient and breathing_meditation
    nature: [],
    sleep: [],
    pulse_flow: [],
    deep_calm: [],
  });
  
  // V2: Metadata version tracking for cache invalidation
  const [cachedMetadataVersion, setCachedMetadataVersion] = useState(null);

  // BUILD 58: Favorites - Single source of truth = favoritePaths (canonical keys)
  const [favoritePaths, setFavoritePaths] = useState([]); // Array of canonical paths (e.g., "nature/Forest_001.m4a")
  const [favorites, setFavorites] = useState([]); // Full favorite objects (for UI rendering)
  const [playlists, setPlaylists] = useState([]);

  // V1.1: Auto-play specific track when arriving from Favorites page
  useEffect(() => {
    if (autoPlayTriggeredRef.current || !trackFromUrl || !favorites.length) return;
    if (selectedStyle !== 'favorites') return;
    
    const decodedTrack = decodeURIComponent(trackFromUrl);
    const matchIdx = favorites.findIndex(fav => {
      const favPath = decodeURIComponent(fav.sound?.path || '');
      return favPath === decodedTrack || favPath.endsWith(decodedTrack.split('/').pop());
    });
    
    if (matchIdx >= 0) {
      autoPlayTriggeredRef.current = true;
      const fav = favorites[matchIdx];
      const path = decodeURIComponent(fav.sound?.path || '');
      let streamUrl;
      if (path.startsWith('/api/r2/stream/')) {
        const parts = path.split('/');
        streamUrl = buildAudioUrl(parts[4]?.toLowerCase(), decodeURIComponent(parts[5] || ''), false);
      } else if (path.includes('/') && !path.startsWith('/')) {
        const parts = path.split('/');
        streamUrl = buildAudioUrl(parts[0]?.toLowerCase(), extToM4a(parts.slice(1).join('/')), false);
      } else {
        streamUrl = buildAudioUrl((fav.sound?.category || 'classical').toLowerCase(), extToM4a(path), false);
      }
      
      const sound = {
        id: path,
        name: fav.sound?.name || path.split('/').pop(),
        file: streamUrl,
        path: path,
        type: 'library',
        category: fav.sound?.category?.toLowerCase(),
      };
      
      console.log('[V1.1] Auto-play favorite track:', sound.name);
      currentPlayingSoundRef.current = sound;

      currentPlayingIndexRef.current = matchIdx;
      setSelectedSound(sound);
      // Directly start playback with the sound object (don't rely on stale state)
      setTimeout(() => {
        setIsPlaying(true);
        startPlayback(sound);
      }, 300);
    }
  }, [favorites, selectedStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  
  // V1.1: Soft limit message for Free users
  const [limitMessage, setLimitMessage] = useState(null);

  // Fullscreen timer modal state
  const [showFullscreenTimer, setShowFullscreenTimer] = useState(false);

  // V1.4: Listening Comfort hook for intensity limits
  const { 
    applyVolume: applySafeVolume, 
    status: safeListeningStatus, 
    showBadge: showSafeListeningBadge,
    badgeText: safeListeningBadgeText,
    badgeColor: safeListeningBadgeColor,
    badgeIcon: safeListeningBadgeIcon,
    isAboveSoftCap,
    isAtHardCap,
    hardCap: SAFE_LISTENING_HARD_CAP
  } = useSafeListening(volume, currentLang);

  
  // ============================================
  // TIMER GLOBAL (source of truth unique)
  // ============================================
  const { sessionElapsedSeconds, sessionFormatted, isActive: timerActive } = useTherapyTimer();
  
  // Pour l'affichage
  const elapsedSeconds = sessionElapsedSeconds;
  const sessionTargetSeconds = 12 * 60; // 12 minutes target

  // REMOVED: Redundant cleanup useEffect that was causing session duplication
  // Session saving is now handled exclusively by therapyTimer.stop()
  // This useEffect was being called on EVERY elapsedSeconds change (every second)
  // causing hundreds of duplicate API calls. See /app/ROOT_CAUSE_ANALYSIS.md

const doesTrackMatchTheme = (track, theme) => {
  if (!track || !theme) return false;

  const category = track.category;

  const normalizeThemeForPlayback = (themeId) => {
    const id = String(themeId || '').toLowerCase().trim();

    if (id === 'instrumental' || id === 'instrumentale' || id === 'classique') {
      return 'classical';
    }

    if (id === 'lofi' || id === 'chill' || id === 'chill_lofi') {
      return 'chill_lofi';
    }

    return id;
  };

  const themeToCategories = {
    nature: ['nature'],
    relaxation: ['respiration', 'breathing', 'relaxation'],
    sleep: ['sommeil', 'sleep'],
    pulse_flow: ['pulse_flow'],
    deep_calm: ['deep_calm'],

    classical: ['classique', 'classical', 'instrumental', 'instrumentale'],
    instrumental: ['classique', 'classical', 'instrumental', 'instrumentale'],

    chill: ['chill', 'chill_lofi', 'lofi'],
    lofi: ['chill', 'chill_lofi', 'lofi'],
    chill_lofi: ['chill', 'chill_lofi', 'lofi'],

    breathing: ['respiration', 'breathing'],
  };

  const normalizedTheme = normalizeThemeForPlayback(theme);

  const allowed =
    themeToCategories[normalizedTheme] ||
    themeToCategories[theme] ||
    [normalizedTheme];

  return allowed.includes(category);
};

  // ═══════════════════════════════════════════════════════════════════════════
  // BUILD 66: Subscribe to soundscapeService state changes
  // This replaces local state management for isPlaying/isLoadingPlayback
  // The service emits events, and the UI reacts to them
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    console.log('[TherapyPlayer] Subscribing to soundscapeService...');
    
    const unsubscribe = soundscapeService.onStateChange((payload) => {
      console.log('[TherapyPlayer] State change:', payload);
      
      switch (payload.state) {
        case 'loading':
          setIsLoadingPlayback(true);
          break;
        case 'playing':
          setIsLoadingPlayback(false);
          setIsPlaying(true);
          // Update selected sound from service only if it matches the current theme
          if (payload.track) {
            if (doesTrackMatchTheme(payload.track, selectedStyle)) {
              setSelectedSound(payload.track);
            } else {
              console.warn('[TherapyPlayer] Ignored stale playing track', {
                currentTheme: selectedStyle,
                track: payload.track.name,
                category: payload.track.category,
            });
          }
        }
        break;
        case 'paused':
          setIsLoadingPlayback(false);
          setIsPlaying(false);
          break;
        case 'stopped':
          setIsLoadingPlayback(false);
          setIsPlaying(false);
          break;
        case 'error':
          setIsLoadingPlayback(false);
          setIsPlaying(false);
          // Show localized error: distinguish offline vs generic failure
          const isOffline = !navigator.onLine;
          const errorMsg = isOffline
            ? (currentLang === 'fr' ? 'Connexion requise pour charger le prochain son.' : 'Connection required to load the next sound.')
            : (currentLang === 'fr' ? 'Impossible de charger le son.' : 'Unable to load the sound.');
          toast.error(errorMsg, { duration: 8000 });
          console.error('[TherapyPlayer] Playback error:', payload.error);
          break;
        default:
          break;
      }
    });
    
    return () => {
      console.log('[TherapyPlayer] Unsubscribing from soundscapeService...');
      unsubscribe();
    };
  }, [currentLang, selectedStyle]);

  // V1.2 FIX: Subscribe to track changes (crossfade updates currentTrack without emitting 'playing')
  useEffect(() => {
  const unsubTrack = soundscapeService.onTrackChange((track) => {
    if (!track) return;

    if (!doesTrackMatchTheme(track, selectedStyle)) {
      console.warn('[TherapyPlayer] Ignored stale track change', {
        currentTheme: selectedStyle,
        track: track.name,
        category: track.category,
      });
      return;
    }

    console.log('[TherapyPlayer] Track changed via service:', track.name);
    setSelectedSound(track);
    currentPlayingSoundRef.current = track;

    // Also sync the NowPlaying context for Mini Player resume
    setNowPlaying({
      title: track.display_name || track.name || 'Unknown',
      isPlaying: true,
      playerState: { selectedSound: track, selectedStyle },
    });
  });

  return () => unsubTrack();
}, [selectedStyle, setNowPlaying]);

  // Load user's equalizer settings from backend
  useEffect(() => {
    const loadEqualizerSettings = async () => {
      try {
        // Fetch Lab Mode program first
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        
        let labProgramData = null;
        try {
          const labResponse = await fetch(
            `${API_BASE_URL}/api/lab/programs`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          if (labResponse.ok) {
            const data = await labResponse.json();
            console.log('═══════════════════════════════════════════════');
            console.log('🎯 [Player] LOADED profile from Lab Mode API:');
            console.log('    active_program:', data.active_program);
            
            // Transform the API response to a simplified format for the player
            // The API returns { notch: {...}, inverse_notch: {...}, active_program: "notch" }
            // We need to extract the active program's data
            // IMPORTANT: Use active_program as the source of truth, not .enabled
            if (data.active_program && data[data.active_program]) {
              const activeProgram = data[data.active_program];
              labProgramData = {
                program_type: data.active_program,
                f_t: activeProgram.f_t,
                gain_db: activeProgram.gain_db,
                enabled: true  // If there's an active_program, it's enabled
              };
              setLabProgram(labProgramData);
              console.log('    f_t:', activeProgram.f_t, 'Hz');
              console.log('    gain_db:', activeProgram.gain_db, 'dB (from profile, no hardcode)');
              console.log('    source:', activeProgram.source || 'unknown');
              console.log('═══════════════════════════════════════════════');
            } else {
              console.log('    ⚠️ No active program found');
              console.log('═══════════════════════════════════════════════');
            }
          }
        } catch (labError) {
        }
        
        const userId = userManager.getUserId();
        
        // Fetch equalizer parameters from backend (uses latest tests automatically)
        const response = await fetch(
          `${API_BASE_URL}/api/equalizer/${userId}`
        );
        
        if (response.ok) {
          const equalizerData = await response.json();
          console.log('📊 Equalizer data received:', equalizerData);
          
          // Override with Lab Mode data if active
          // labProgramData structure: { program_type, f_t, gain_db, enabled }
          if (labProgramData && labProgramData.enabled) {
            
            // Create proper inverse_notch object structure with Lab Mode settings
            equalizerData.has_inverse_notch = true;
            equalizerData.inverse_notch = {
              center_frequency: labProgramData.f_t,
              bandwidth: 200,
              gain: labProgramData.gain_db
            };
            equalizerData.lab_mode_active = true;
            equalizerData.lab_program_type = labProgramData.program_type;
            
            const gainSign = labProgramData.gain_db > 0 ? '+' : '';
          }
          
          setEqualizerSettings(equalizerData);
          console.log('[Equalizer DATA full]', equalizerData);
          
          // Enable toggles if tests exist
          const hasAudiometric = equalizerData.has_audiometric_compensation;
          const hasNotch =
            equalizerData.has_inverse_notch === true ||
            !!equalizerData.inverse_notch ||
            Number(equalizerData.tinnitus_frequency) > 0;
          
          // V1.1: Audio correction toggles are session-only.
          // Default ON whenever the corresponding profile/test exists.
          const eqOn = hasAudiometric === true;
          const notchOn = hasNotch === true;

          console.log('[Audio Toggles] Defaults from tests:', {
            hasAudiometric,
            hasNotch,
            eqOn,
            notchOn
          });
          
          // CRITICAL: Always load equalizer to establish audio path
          audioService.loadEqualizer(equalizerData, eqOn, notchOn);
          
          // Set UI toggles to match saved state
          setEqualizerEnabled(eqOn);
          setNotchEnabled(notchOn);
          
          if (hasAudiometric || hasNotch) {
          } else {
          }
        } else {
          console.error('❌ Failed to fetch equalizer settings');
        }
      } catch (error) {
        console.error('❌ Error loading equalizer:', error);
      }
    };
    
    loadEqualizerSettings();
    
    // Cleanup: Stop audio when leaving the page
    // V1.2: Only stop audio if user explicitly pressed Stop.
    // If navigating away while playing → keep audio alive (mini player takes over).
    return () => {
      // If not an explicit stop, keep audio playing for mini player
      if (!explicitStopRef.current) {
        console.log('[TherapyPlayer] Unmount without explicit stop — keeping audio for mini player');

        if (soundscapeService.hasActiveSoundscapeSession?.() || soundscapeService.playbackState === 'PLAYING') {
          console.log('[TherapyPlayer] Unmount protected — audio/presence should stay alive');
        }

        setIsFullscreen(false);
       return;
      }

      // Explicit stop — full cleanup
      if (currentSessionStart) {
        therapyTimer.stop().catch(err =>
          console.error('Error saving session on unmount:', err)
        );
      }

      console.warn('[TherapyPlayer][CLEANUP ENTER]', {
        hasGlobalSession: soundscapeService.hasActiveSoundscapeSession?.(),
        playbackState: soundscapeService.getPlaybackState?.(),
        currentTrack:
         soundscapeService.getCurrentTrack?.()?.name ||
         soundscapeService.getCurrentTrack?.()?.display_name,
      });

      if (soundscapeService.hasActiveSoundscapeSession?.()) {
        console.log('[TherapyPlayer] Cleanup skipped audio stop — global session active');
      } else {
        soundscapeService.stop({ reason: 'cleanup' });
        audioService.stopTherapy({ reason: 'cleanup' });
        clearNowPlaying();
      }

      if (playlistWatcherRef.current) {
        clearInterval(playlistWatcherRef.current);
        playlistWatcherRef.current = null;
      }

      setIsPlaying(false);
      setIsFullscreen(false);
    };
      
  }, []); // Only run on mount/unmount, not when timerInterval changes
  
  // Handle Coach session info
  useEffect(() => {
    if (fromCoach && recommendedDuration) {
      // Coach session started
      
      // Duration is already set in state initialization
      // Show a notification to user
      const message = `Séance planifiée : ${recommendedDuration} min\nThérapie complète (Correction pertes + Notch inverse)`;
      console.log('💡', message);
    }
  }, [fromCoach, recommendedDuration, sessionDate, sessionProtocol]);
  
  // === SOS CALM MODE: Auto-start audio when coming from SOS button ===
  useEffect(() => {
    if (sosMode && sosSound) {
      console.log('[SOS] Starting calm mode with sound:', sosSound);
      
      // Get language for error messages
      const lang = localStorage.getItem('hearen_lang') || 'fr';
      
      // Set volume (default 35% for calm)
      const targetVolume = sosVolume || 0.35;
      setVolume(targetVolume * 100);
      audioService.setVolume(targetVolume);
      
      // Select the sound
      setSelectedSound(sosSound);
      
      // Go directly to player mode
      setViewMode('player');
      setIsFullscreen(true);
      
      // Start playback after a short delay
      const startPlayback = async () => {
        console.log('[START PLAYBACK][SOS CALLED]');
        try {
          // Build audio URL - use audioUrl if available, otherwise build from file_path
          let audioUrl;
          if (sosSound.audioUrl) {
            // V1.9: Re-encode URL if needed
            audioUrl = sosSound.audioUrl.startsWith('/api/r2/stream/') 
              ? `${API_BASE_URL}${reencodeAudioPath(sosSound.audioUrl)}`
              : sosSound.audioUrl;
          } else if (sosSound.file_path) {
            // V1.9: Parse file_path (format: "category/filename" or just "filename")
            const parts = sosSound.file_path.split('/');
            if (parts.length >= 2) {
              const category = parts[0];
              const filename = parts.slice(1).join('/');
              audioUrl = buildAudioUrl(category, filename);
            } else {
              audioUrl = buildAudioUrl('classical', sosSound.file_path);
            }
          } else {
            console.error('[SOS] No valid audio path found');
            toast.error(lang === 'fr' ? 'Audio temporairement indisponible' : 'Audio temporarily unavailable');
            return;
          }
          
          console.log('[SOS] Starting audio:', audioUrl);
          
          // Add error listener to audio element
          const audioElement = audioService.getAudioElement?.();
          if (audioElement) {
            const errorHandler = (e) => {
              console.error('[SOS] Audio error event:', e);
              toast.error(lang === 'fr' ? 'Audio temporairement indisponible' : 'Audio temporarily unavailable');
            };
            audioElement.addEventListener('error', errorHandler, { once: true });
          }
          
          await audioService.playTherapy(audioUrl);
          setIsPlaying(true);
          therapyTimer.start();
        } catch (error) {
          console.error('[SOS] Error starting audio:', error);
          toast.error(lang === 'fr' ? 'Audio temporairement indisponible' : 'Audio temporarily unavailable');
        }
      };
      
      // Small delay to allow component setup
      setTimeout(startPlayback, 150);
    }
  }, [sosMode, sosSound, sosAutoPlay, sosVolume]);
  
  // V1.1.1: Initialize Guided mode
  useEffect(() => {
    if (presenceModeFromNav && !isPresenceMode) {
      console.log('[Presence] Presence mode activated from navigation');
      setIsPresenceMode(true);
      
      // Set up presence service callbacks
      presenceService.setCallbacks({
        onVoiceStart: (message) => {
          console.log('[Presence] Voice started:', message?.text?.substring(0, 40));
          setIsVoiceActive(true);
          // DEBUG: Visual indicator to confirm ducking is triggered
          console.log('[Presence] DUCK triggered - presenceService active');
        },
        onVoiceEnd: () => {
          console.log('[Presence] Voice ended');
          setIsVoiceActive(false);
        },
        onStateChange: (state) => {
          console.log('[Presence] State changed:', state);
        }
      });
      
      // Voice messages will be loaded when start() is called with the correct theme
      // (after user selects a category and presses play)
      console.log('[Presence] Callbacks set, waiting for playback to start');
    }
    
    // Cleanup on unmount
      return () => {
        if (!isPresenceMode) return;

        if (soundscapeService.hasActiveSoundscapeSession?.()) {
          console.log('[TherapyPlayer] Cleanup skipped presence stop — global session active');
          return;
        }

        if (soundscapeService.playbackState === 'PLAYING') {
          console.log('[TherapyPlayer] Cleanup skipped presence stop — audio still playing');
          return;
        }

        presenceService.stop();
      };
  }, [presenceModeFromNav, isPresenceMode, currentLang]);
  
  // V1.1.1: Start/stop guided scheduler when playback state changes
useEffect(() => {
  console.warn('[HERA][EFFECT ENTRY]', {
    isPresenceMode,
    voiceEnabled,
    isPlaying,
    currentLang,
    selectedStyle,
    userId: user?.id,
    serviceActive: presenceService.isActive,
    servicePaused: presenceService.isPaused,
    welcomeVoicePlayed: welcomeVoicePlayedRef.current,
  });

  if (!isPresenceMode || !voiceEnabled) {
    presenceService.isPaused = true;
    presenceService.isVoicePlaying = false;

    console.warn('[HERA][EFFECT] voice disabled / presence off — soft pause', {
      isPresenceMode,
      voiceEnabled,
      isActive: presenceService.isActive,
      isPaused: presenceService.isPaused,
      isVoicePlaying: presenceService.isVoicePlaying,
    });

    return;
  }

  if (isPlaying) {
    if (USE_NATIVE_AUDIO_PLAYER && welcomeVoicePlayedRef.current) {
      console.log('[Presence] Native resume detected, skipping Hera re-start');
      return;
    }

    if (presenceService.isActive && !presenceService.isPaused) {
      console.log('[Presence] Already active, skipping re-start');
    } else {
      console.warn('[HERA][START CALL]', {
  lang: currentLang,
  theme: selectedStyle,
  userId: user?.id,
});

presenceService.start({
  lang: currentLang,
  theme: selectedStyle,
  userId: user?.id
}).then(() => {
  console.warn('[HERA][START RESOLVED]', presenceService.getState());
  const state = presenceService.getState();
  setPresenceVoiceCount(state.voiceCount);
}).catch((err) => {
  console.error('[HERA][START ERROR]', {
    name: err?.name,
    message: err?.message,
    stack: err?.stack,
  });
});
    }
  } else {
    presenceService.isPaused = true;
    presenceService.isVoicePlaying = false;

    console.warn('[HERA][EFFECT] soft pause only', {
      isActive: presenceService.isActive,
      isPaused: presenceService.isPaused,
      isVoicePlaying: presenceService.isVoicePlaying,
    });
  }
}, [isPresenceMode, voiceEnabled, isPlaying, currentLang, selectedStyle, user?.id]);
  
  // V1.1.1: Handle voice volume change
  const handleVoiceVolumeChange = useCallback((newVolume) => {
    // newVolume is 0-100, convert to 0.0-1.0
    const volumeFloat = newVolume / 100;
    presenceService.setVoiceVolume(volumeFloat);
    setVoiceVolume(newVolume);
  }, []);
  
  // Separate cleanup effect for timer
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // ⭐ V2 GATING: Determine current mode for free theme filtering
  const currentMode = presenceModeFromNav ? 'presence' : 'soundscapes';
  const freeThemeIds = currentMode === 'presence' ? FREE_PRESENCE_THEME_IDS : FREE_SOUNDSCAPE_THEME_IDS;

  // ⭐ V2 GATING: Block access to premium themes for Free users (URL/cache/state access)
  useEffect(() => {
    if (selectedStyle === 'favorites' || selectedStyle === 'playlist' || selectedStyle === 'custom') {
      return;
    }
    const normalizedThemeId = normalizeThemeId(selectedStyle);
    if (isFreeUser && !isThemeFree(normalizedThemeId, currentMode)) {
      console.log('🔒 [Gating] Blocked premium theme for Free user:', normalizedThemeId, 'mode:', currentMode);
      // Silently reset to first free theme (don't navigate to /premium)
      const fallback = currentMode === 'presence' ? 'sleep' : 'relaxation';
      setSelectedStyle(fallback);
    }
  }, [selectedStyle, isFreeUser, currentMode]);

  // Music volume: always 80% default for all categories (no auto-adjust)

  // Load library sounds from API
  // V1 FINAL: 7 categories using canonical IDs from themes.js
  // API supports both legacy and canonical IDs via category_mapping in server.py
  const loadLibrarySounds = async () => {
    // BUILD 56: Performance timing
    const startTime = performance.now();
    console.log('[Perf][UI] loadLibrarySounds START');
    
    setIsLoadingSounds(true);
    
    // Map from canonical theme IDs to API category names
    // V1: 'ambient' deprecated - use 'breathing' for relaxation
    const categoryApiMapping = {
      classical: 'classique',
      instrumental: 'classique',

      breathing: 'respiration',
      relaxation: 'respiration',

      chill: 'chill_lofi',
      lofi: 'chill_lofi',
      chill_lofi: 'chill_lofi',

      nature: 'nature',
      sleep: 'sommeil',
      pulse_flow: 'pulse_flow',
      deep_calm: 'deep_calm'
    };
    const categories = Object.keys(categoryApiMapping);
    const loadedSounds = {};
    
    // Get user language (from localStorage or default to 'fr')
    const userLang = localStorage.getItem('hearen_lang') || 'fr';
    
    // V2: Track latest metadata version from any category
    let latestMetadataVersion = null;
    
    // V1.1.1 FIX: Fetch a single category directly (no caching of errors)
    const fetchCategory = async (apiCategory, userLang) => {
      const response = await fetch(
        `${API_BASE_URL}/api/library-sounds/${apiCategory}?lang=${userLang}`
      );
      if (!response.ok) {
        // THROW instead of returning empty - prevents cachedFetch from caching errors
        throw new Error(`[Soundscapes] ${apiCategory} HTTP ${response.status}`);
      }
      return response.json();
    };

    // Load categories in parallel
    const loadPromises = categories.map(async (category) => {
      const apiCategory = categoryApiMapping[category];
      
      try {
        // Use cachedFetch for performance, but fetchFn THROWS on error (not cached)
        const { cachedFetch, CACHE_KEYS, CACHE_TTL } = await import('@/services/cacheService');
        const cacheKey = CACHE_KEYS.soundscapesTracks(apiCategory, userLang);
        
        const data = await cachedFetch(
          cacheKey,
          () => fetchCategory(apiCategory, userLang),
          { ttl: CACHE_TTL.TRACKS }
        );
        
        const sounds = data.sounds || [];
        console.log(`[Soundscapes][Load] ${category} (api=${apiCategory}): ${sounds.length} sounds`);
        
        if (data.metadata_version) {
          latestMetadataVersion = data.metadata_version;
        }
        
        return {
          category,
          sounds: sounds.map(sound => ({
            id: sound.filename.replace(/\.[^/.]+$/, ''),
            name: sound.display_name || sound.filename,
            file: sound.path,
            path: `${apiCategory}/${encodeURIComponent(sound.filename)}`,
            category: apiCategory,
            type: 'file'
          }))
        };
      } catch (error) {
        // cachedFetch failed (timeout or API error) - try direct fetch as fallback
        console.warn(`[Soundscapes][Retry] ${category} cache/fetch failed, trying direct...`, error.message);
        try {
          const data = await fetchCategory(apiCategory, userLang);
          const sounds = data.sounds || [];
          console.log(`[Soundscapes][Retry] ${category}: ${sounds.length} sounds (direct)`);
          return {
            category,
            sounds: sounds.map(sound => ({
              id: sound.filename.replace(/\.[^/.]+$/, ''),
              name: sound.display_name || sound.filename,
              file: sound.path,
              path: `${apiCategory}/${encodeURIComponent(sound.filename)}`,
              category: apiCategory,
              type: 'file'
            }))
          };
        } catch (retryError) {
          console.error(`[Soundscapes][Error] ${category} failed after retry:`, retryError.message);
          return { category, sounds: [] };
        }
      }
    });
    
    // V1.4.0: Progressive rendering — update state as each category resolves
    let firstResolved = false;
    loadPromises.forEach(promise => {
      promise.then(({ category, sounds }) => {
        if (sounds.length > 0) {
          setLibrarySounds(prev => ({ ...prev, [category]: sounds }));
          if (!firstResolved) {
            firstResolved = true;
            setIsLoadingSounds(false);
            console.log(`[Perf][UI] First category ready: ${category} (${sounds.length} tracks)`);
          }
        }
      }).catch(() => {}); // Errors handled in the promise itself
    });
    
    // Wait for all categories (needed for retry logic + metadata)
    const results = await Promise.all(loadPromises);
    
    // Build loadedSounds object from results
    let totalSounds = 0;
    results.forEach(({ category, sounds }) => {
      loadedSounds[category] = sounds;
      totalSounds += sounds.length;
    });
    
    console.log(`[Soundscapes][Load] Total: ${totalSounds} sounds across ${categories.length} categories`);
    
    // V1.1.2: If ALL categories returned 0 sounds, purge cache and retry once
    if (totalSounds === 0 && !loadLibrarySounds._retried) {
      loadLibrarySounds._retried = true;
      console.warn('[Soundscapes][Load] ALL categories empty — purging cache and retrying...');
      const { invalidateCachePattern } = await import('@/services/cacheService');
      invalidateCachePattern('soundscapes:');
      setIsLoadingSounds(false);
      return loadLibrarySounds();
    }
    loadLibrarySounds._retried = false;
    
    // Update cached metadata version
    if (latestMetadataVersion) {
      setCachedMetadataVersion(latestMetadataVersion);
    }
    
    setLibrarySounds(loadedSounds);
    setIsLoadingSounds(false);
    
    const duration = Math.round(performance.now() - startTime);
    console.log(`[Perf][UI] loadLibrarySounds COMPLETE durationMs=${duration}`);
  };
  
  // V2: Check if metadata has been updated (admin changed display names)
  const checkAndReloadIfStale = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/metadata/version`);
      const data = await response.json();
      
      if (cachedMetadataVersion && data.display_names_updated_at !== cachedMetadataVersion) {
        console.log('[Soundscapes] Metadata changed, purging cache + reloading...', {
          cached: cachedMetadataVersion,
          server: data.display_names_updated_at
        });
        // V1.1.1: Purge all soundscapes cache entries BEFORE reloading
        const { invalidateCachePattern } = await import('@/services/cacheService');
        invalidateCachePattern('soundscapes:');
        invalidateCachePattern('radio:');
        await loadLibrarySounds();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('[Soundscapes] Error checking metadata version:', error);
      return false;
    }
  };

  useEffect(() => {
    // BUILD 56: Preload catalog on mount for better performance
    const userLang = localStorage.getItem('hearen_lang') || 'fr';
    preloadSoundscapesCatalog(userLang);
    
    // BUILD 56: Restore last selected theme
    const lastTheme = getLastSoundscapesTheme();
    if (lastTheme && !playlistIdFromUrl && !themeFromUrl) {
      // Map legacy API names back to canonical IDs if needed
      const legacyToCanonical = {
        'classique': 'classical',
        'respiration': 'breathing',
        'chill_lofi': 'chill',
        'sommeil': 'sleep'
      };
      const canonicalTheme = legacyToCanonical[lastTheme] || lastTheme;
      console.log(`[Perf][LastTheme] Restoring: ${lastTheme} -> ${canonicalTheme}`);
      setSelectedStyle(canonicalTheme);
    }
    
    loadLibrarySounds();
    
    // Listen for language changes and reload sounds
    const handleLanguageChange = (event) => {
      loadLibrarySounds();
    };
    
    // Listen for library updates from AdminSounds page (cross-tab via storage)
    const handleLibraryUpdateStorage = (event) => {
      // Only reload if the storage key actually changed (prevents loops)
      if (event.key === 'hearen-library-updated') {
        loadLibrarySounds();
      }
    };
    
    // Listen for library updates from AdminSounds page (same window via custom event)
    const handleLibraryUpdateCustom = () => {
      loadLibrarySounds();
    };
    
    // RECHARGEMENT AUTOMATIQUE: Recharger quand on revient sur la page
    // (depuis Home ou après avoir été sur Admin)
    // V2: Use smart check - only reload if metadata version changed
    const handleFocus = () => {
      checkAndReloadIfStale();
    };
    
    // V2: Handle iOS app foreground (visibilitychange is more reliable than focus for iOS)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Soundscapes] App became visible, checking for updates...');
        checkAndReloadIfStale();
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLibraryUpdateStorage);
    window.addEventListener('libraryUpdated', handleLibraryUpdateCustom);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLibraryUpdateStorage);
      window.removeEventListener('libraryUpdated', handleLibraryUpdateCustom);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cachedMetadataVersion]); // V2: Re-run when cached version changes
  
  // REMOVED: Fetch today's minutes from backend - now handled by useTherapyTimer hook

  // BUILD 56: Save last selected theme when it changes
  useEffect(() => {
    if (selectedStyle && selectedStyle !== 'favorites' && selectedStyle !== 'playlist' && selectedStyle !== 'custom') {
      // Map canonical IDs to API names for persistence
      const canonicalToLegacy = {
        'classical': 'classique',
        'breathing': 'respiration',
        'chill': 'chill_lofi',
        'sleep': 'sommeil'
      };
      const apiTheme = canonicalToLegacy[selectedStyle] || selectedStyle;
      saveSoundscapesTheme(apiTheme);
    }
  }, [selectedStyle]);

  // BUILD 64: Handler for category selection with loading state
  // Ensures we show "Loading..." instead of "No sounds" during fetch
  const handleCategorySelect = async (categoryId) => {
    const startTime = performance.now();
    console.log(`[Soundscapes] load start theme=${categoryId}`);
    
    // Set loading state BEFORE changing category
    setIsLoadingCategory(true);
    setCategoryLoadError(null);
    
    // Change category
    setSelectedStyle(categoryId);
    setViewMode('settings');
    
    // V2: Resolve canonical key for librarySounds lookup
    // Theme IDs like 'chill_lofi' map to 'chill' in librarySounds
    const libraryKeyMap = {
      'chill_lofi': 'chill',
      'relaxation': 'breathing',
      'meditation': 'breathing',
    };
    const libraryKey = libraryKeyMap[categoryId] || categoryId;
    
    // Check if sounds are already loaded for this category
    const existingSounds = librarySounds[libraryKey];
    if (existingSounds && existingSounds.length > 0) {
      console.log(`[Soundscapes] load success count=${existingSounds.length} ms=${Math.round(performance.now() - startTime)} (cached)`);
      setIsLoadingCategory(false);
      return;
    }
    
    // If sounds not loaded, wait for them (with timeout)
    const TIMEOUT_MS = 10000;
    const checkInterval = 200;
    let elapsed = 0;
    
    while (elapsed < TIMEOUT_MS) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));
      elapsed += checkInterval;
      
      // Check if sounds are now available
      const sounds = librarySounds[libraryKey];
      if (sounds && sounds.length > 0) {
        console.log(`[Soundscapes] load success count=${sounds.length} ms=${Math.round(performance.now() - startTime)}`);
        setIsLoadingCategory(false);
        return;
      }
      
      // Also check if the global loading is done (might be empty category)
      if (!isLoadingSounds && elapsed > 2000) {
        // After 2s, if global loading is done, accept empty result
        console.log(`[Soundscapes] load complete (possibly empty) ms=${Math.round(performance.now() - startTime)}`);
        setIsLoadingCategory(false);
        return;
      }
    }
    
    // Timeout reached
    console.log(`[Soundscapes] load error timeout ms=${Math.round(performance.now() - startTime)}`);
    setCategoryLoadError('timeout');
    setIsLoadingCategory(false);
  };

  // Load custom sounds
  useEffect(() => {
    const loadCustomSounds = async () => {
      try {
        const userId = userManager.getUserId();
        const sounds = await api.getCustomSounds(userId);
        setCustomSounds(sounds.filter(s => s.in_playlist));
      } catch (error) {
        console.error('Error loading custom sounds:', error);
      }
    };
    loadCustomSounds();
  }, []);

  // Load favorites and playlists
  useEffect(() => {
    const loadFavoritesAndPlaylists = async () => {
      try {
        const [favs, plys] = await Promise.all([
          api.getFavorites(),
          api.getPlaylists()
        ]);
        
        // BUILD 58: Extract canonical paths as single source of truth
        // V1.1.1: Normalize favorite paths for consistent matching
        // Old favorites: "Nature/Nature_001.m4a" (no category prefix)
        // New favorites: "nature/Nature%2FNature_001.m4a" (encoded, with category)
        // Canonical: "nature/Nature/Nature_001.m4a" (decoded, with category)
        const paths = favs.map(fav => {
          const rawPath = decodeURIComponent(fav.sound?.path || '');
          if (!rawPath) return '';
          
          // Check if path already has a category prefix (e.g., "nature/Nature/...")
          const category = (fav.sound?.category || '').toLowerCase();
          if (category && !rawPath.toLowerCase().startsWith(category + '/')) {
            // Old format without category prefix — prepend it
            return `${category}/${rawPath}`;
          }
          return rawPath;
        }).filter(Boolean);
        setFavoritePaths(paths);
        setFavorites(favs);
        console.log('[Fav] Loaded favoritePaths:', paths.length, paths);
        
        setPlaylists(plys);
        
        // V5: Update currentPlaylist with new translated name if it's currently playing
        if (currentPlaylist && plys.length > 0) {
          const updatedPlaylist = plys.find(p => p.id === currentPlaylist.id);
          if (updatedPlaylist) {
            // Update playlist with translated name while preserving current playback state
            setCurrentPlaylist(prev => {
              const updated = {
                ...prev,
                name: updatedPlaylist.name,
                sounds: updatedPlaylist.sounds
              };
              currentPlaylistRef.current = updated;
              return updated;
            });
          }
        }
        
        // If playlist ID is in URL, load and play it
        if (playlistIdFromUrl && plys.length > 0) {
          const playlist = plys.find(p => p.id === playlistIdFromUrl);
          if (playlist) {
            if (playlist.sounds && playlist.sounds.length > 0) {
              console.log('   Playlist sounds:', playlist.sounds);
              console.log('   First sound path:', playlist.sounds[0]?.path);
              console.log('   First sound type:', playlist.sounds[0]?.type);
              setCurrentPlaylist(playlist);
              currentPlaylistRef.current = playlist;
              setCurrentPlaylistIndex(0);
              currentPlaylistIndexRef.current = 0; // Initialize ref
              setSelectedStyle('playlist');
              setViewMode('settings'); // Go directly to player view instead of category selection
              
              // V2.2.4: Clear any leftover crossfade timer from previous soundscape session
              cancelAllCrossfadeTimers();
              
              // Auto-select first sound with proper formatting
              const rawFirstSound = playlist.sounds[0];
              const firstPath = decodeURIComponent(rawFirstSound.path || rawFirstSound.file || '');

                let firstCategory = rawFirstSound.category?.toLowerCase();
                let firstFilename = firstPath;

              if (firstPath.startsWith('/api/r2/stream/')) {
                const parts = firstPath.split('/');
                firstCategory = parts[4]?.toLowerCase();
                firstFilename = decodeURIComponent(parts[5] || '');
              } else if (firstPath.includes('/') && !firstPath.startsWith('/')) {
                 const parts = firstPath.split('/');
                 firstCategory = parts[0]?.toLowerCase();
                 firstFilename = parts.slice(1).join('/');
              }

              firstFilename = extToM4a(firstFilename);
              const firstStreamUrl = buildAudioUrl(firstCategory || 'classical', firstFilename, false);

              const firstSound = {
                 ...rawFirstSound,
                 id: rawFirstSound.id || firstPath,
                 name: rawFirstSound.name || firstFilename,
                 file: firstStreamUrl,
                 path: firstPath,
                 type: 'library',
                category: firstCategory || rawFirstSound.category || 'classical'
             };

              console.log('🎵 [Playlist][FirstSound Normalize]', firstSound);
              console.log('   First sound formatted:', firstSound);
              setSelectedSound(firstSound);
              
              // Auto-play the playlist immediately
              // Start playback immediately without delay to avoid flash

              console.log('[Playlist][INSTALLING AUDIO WATCHER NOW]', {
                hasAudioContext: !!audioService.audioContext,
                hasIOSBuffer: !!audioService.iosSoundBuffer,
                hasIOSSource: !!audioService.iosSoundSource,
                currentPlaylist: currentPlaylistRef.current?.name,
                selectedSound: selectedSound?.name
              });

              console.log('[Playlist][START DIRECT]', {
                playlist: playlist.name,
                firstSound: firstSound.name,
                playlistRef: currentPlaylistRef.current?.name,
                indexRef: currentPlaylistIndexRef.current
              });

              startPlayback(firstSound).then(() => {
                console.log('[Playlist][START OK] installing fallback watcher');

                console.log('[Playlist][SERVICE SNAPSHOT after start]', {
                  hasAudioElement: !!soundscapeService.audioElement,
                  hasSoundBuffer: !!soundscapeService.soundBuffer,
                  hasSoundSource: !!soundscapeService.soundSource,
                  keys: Object.keys(soundscapeService || {})
                });

                if (playlistWatcherRef.current) {
                   clearInterval(playlistWatcherRef.current);
                }

              playlistWatcherRef.current = setInterval(() => {
                const duration =
                  soundscapeService.soundBuffer?.duration ||
                  soundscapeService.currentDuration ||
                  0;

                const position =
                  soundscapeService.getCurrentTrackPosition?.() ||
                  soundscapeService.currentTime ||
                  0;

                const sourceEnded =
                  !!soundscapeService.soundSource?._stopped;

                console.log('[Playlist][WATCH]', {
                  hasBuffer: !!soundscapeService.soundBuffer,
                  hasSource: !!soundscapeService.soundSource,
                  sourceEnded,
                  position,
                  duration
                });

                if (
                
                  sourceEnded ||
                 (duration > 0 && isFinite(duration) && position >= duration - 0.5)
                ) {

                  clearInterval(playlistWatcherRef.current);
                  playlistWatcherRef.current = null;

                  handleSoundEnded();
                }
              }, 500);

              setIsLoadingPlaylist(false);

            }).catch(() => {
              setIsLoadingPlaylist(false);
            });
            } else {
              // Still show the playlist interface so user can add sounds
              setCurrentPlaylist(playlist);
              currentPlaylistRef.current = playlist;
              setSelectedStyle('playlist');
              setViewMode('settings');
            }
          } else {
          }
        }
      } catch (error) {
        console.error('Error loading favorites/playlists:', error);
      }
    };
    loadFavoritesAndPlaylists();
    
    // Listen for language changes and reload playlists
    const handleLanguageChange = (event) => {
      loadFavoritesAndPlaylists();
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [playlistIdFromUrl]);

  // Generated sounds (noise and tones) to add to library sounds
  const generatedSounds = {
    sleep: [],
    noise: [],
    tones: [
      { id: 'sine_440', name: t?.pureTone440 || 'Ton pur 440 Hz', file: null, type: 'generated', generator: 'sine', frequency: 440 },
      { id: 'sine_1000', name: t?.pureTone1000 || 'Ton pur 1000 Hz', file: null, type: 'generated', generator: 'sine', frequency: 1000 },
    ],
  };

  // Combine library sounds from API with generated sounds
  // ⭐ V1: Keys use canonical R2 category IDs - 'ambient' is deprecated
  const soundLibrary = {
    classical: librarySounds.classical || [],
    chill: librarySounds.chill || [],  // V1: canonical chill category
    chill_lofi: librarySounds.chill || [],  // Alias for frontend theme ID
    breathing: librarySounds.breathing || [],  // V1: canonical breathing category
    breathing_meditation: librarySounds.breathing || [],  // Alias for frontend
    relaxation: librarySounds.breathing || [],  // V1: relaxation uses breathing audio
    meditation: librarySounds.breathing || [],  // V1: meditation uses breathing audio
    nature: librarySounds.nature || [],
    sleep: [...(librarySounds.sleep || []), ...(generatedSounds.sleep || [])],
    pulse_flow: librarySounds.pulse_flow || [],
    deep_calm: librarySounds.deep_calm || [],
    // Legacy aliases for backward compatibility
    classique: librarySounds.classical || [],
    piano_ambiant: librarySounds.breathing || [],  // V1: was ambient, now breathing
    ambient: librarySounds.breathing || [],  // V1: deprecated -> breathing
    respiration: librarySounds.breathing || [],
    sommeil: librarySounds.sleep || [],
    // Other categories
    noise: [...(generatedSounds.noise || [])],
    tones: [...(generatedSounds.tones || [])],
  };

  // ⭐ V2 GATING: Limit tracks to FREE_TRACKS_PER_THEME for free users
  const getTracksForTheme = (themeId) => {
  const raw = soundLibrary?.[themeId];

  let tracks = [];

  if (Array.isArray(raw)) {
    tracks = raw;
  } else if (Array.isArray(raw?.tracks)) {
    tracks = raw.tracks;
  } else if (Array.isArray(raw?.sounds)) {
    tracks = raw.sounds;
  } else if (Array.isArray(raw?.items)) {
    tracks = raw.items;
  }

  if ((!tracks || tracks.length === 0) && Array.isArray(librarySounds)) {
    tracks = librarySounds.filter(s =>
      s.category === themeId ||
      s.theme === themeId ||
      s.style === themeId
    );
  }

  console.log('[GET TRACKS RESULT]', {
    themeId,
    rawExists: !!raw,
    rawIsArray: Array.isArray(raw),
    rawKeys: raw && typeof raw === 'object' ? Object.keys(raw) : null,
    librarySoundsCount: Array.isArray(librarySounds) ? librarySounds.length : null,
    count: tracks.length,
    firstTrack: tracks[0] || null,
  });

  if (isFreeUser && freeThemeIds.includes(themeId)) {
    return tracks.slice(0, FREE_TRACKS_PER_THEME);
  }

  if (isFreeUser && !freeThemeIds.includes(themeId)) {
    return [];
  }

  return tracks;
};

  // AUTOPLAY: Instant theme entry
  const autoplayTriggeredRef = useRef(false);
  const allowNextAutoplayRef = useRef(false);
  const welcomeVoicePlayedRef = useRef(false);
  const themeInitRef = useRef(false);

  useEffect(() => {
    if (!autoplayFromUrl) return;
    if (autoplayTriggeredRef.current) return;
    // Block autoplay only when restoring an already-active session,
    // not when entering a theme explicitly from Home
    if (_audioActiveOnMount && !themeFromUrl && !allowNextAutoplayRef.current) return;

    // Wait until loading is complete for this theme
    if (isLoadingSounds) return;

    const tracks = getTracksForTheme(selectedStyle);
    console.log('[DEBUG THEME]', {
      selectedStyle,
      themeFromUrl,
      tracksCount: tracks.length,
    });

    if (USE_NATIVE_AUDIO_PLAYER && tracks.length > 0) {
      nativeSessionTracksRef.current = tracks;

      console.log('[NATIVE SESSION TRACKS][FROZEN FROM AUTOPLAY]', {
        selectedStyle,
        count: tracks.length,
        first: tracks[0]?.name || tracks[0]?.display_name,
      });
    }

    console.log('[TRACKS DEBUG BEFORE AUTOPLAY]', {
      selectedStyle,
      tracksCount: tracks?.length || 0,
      tracks: tracks?.map(t => ({
        id: t.id,
        name: t.name,
        title: t.title,
        url: t.url || t.audioUrl || t.fileUrl
      }))
    });

    if (tracks.length === 0) {
      console.log('[Autoplay] Waiting for tracks...');
      return;
    }
    autoplayTriggeredRef.current = true;
    themeSwitchInProgressRef.current = false;
    allowNextAutoplayRef.current = false;

    // Keep player UI visible during autoplay attempt
    setViewMode('player');

    // Shuffle: pick a random first track, avoiding the last played one if possible
    const lastPlayedId = localStorage.getItem('hearen_last_track_' + selectedStyle);
    const candidateTracks = tracks.filter(t => t.id !== lastPlayedId);
    const pool = candidateTracks.length > 0 ? candidateTracks : tracks;
    const firstTrack = pool[Math.floor(Math.random() * pool.length)];

    // Keep player UI visible, but do not show a provisional track title
    setSelectedSound(null);

    console.log('[AUTOPLAY PICK]', {
      theme: selectedStyle,
      firstTrackId: firstTrack?.id,
      firstTrackName: firstTrack?.name,
      firstTrackFile: firstTrack?.file,
      poolSize: pool.length,
    });


    localStorage.setItem('hearen_last_track_' + selectedStyle, firstTrack.id);

    // Reset transition/session state for fresh direct-autoplay theme entry
    cancelAllCrossfadeTimers();
    preloadedNextRef.current = null;
    isTransitioningRef.current = false;
    playedTracksRef.current = [];
    const isFreshSession =
      !currentPlayingSoundRef.current &&
      !soundscapeService.isPlaying &&
      !soundscapeService.isPaused;

    if (isFreshSession && !autoplayTriggeredRef.current) {
      console.warn('[HERA][WELCOME RESET][TRUE FIRST SESSION]');
      welcomeVoicePlayedRef.current = false;
    } else {
      console.warn('[HERA][WELCOME RESET SKIPPED]', {
        isFreshSession,
        autoplayAlreadyTriggered: autoplayTriggeredRef.current,
        hasCurrentTrack: !!currentPlayingSoundRef.current,
        serviceIsPlaying: soundscapeService.isPlaying,
        serviceIsPaused: soundscapeService.isPaused,
      });
    }
    currentPlayingIndexRef.current = tracks.findIndex(t => t.id === firstTrack.id);
    if (currentPlayingIndexRef.current < 0) currentPlayingIndexRef.current = 0;
    currentPlayingSoundRef.current = firstTrack;

    // startPlayback already calls setSelectedSound + setViewMode('player') internally
    sessionThemeRef.current = selectedStyle;

    // 🔁 Safe autoplay with retry if first start fails (race condition after theme-switch)
    const runAutoplay = async () => {
      console.log('[AUTOPLAY] before startPlayback', {
      firstTrackId: firstTrack?.id,
      firstTrackName: firstTrack?.name,
      firstTrackFile: firstTrack?.file,
      firstTrackUrl: firstTrack?.url || firstTrack?.audioUrl || firstTrack?.fileUrl
    });

    const result = await startPlayback(firstTrack);

    console.log('[AUTOPLAY] after startPlayback');
  const ok = result === true || result?.success === true;

  console.log('[AUTOPLAY START RESULT]', {
    theme: selectedStyle,
    firstTrackId: firstTrack?.id,
    firstTrackName: firstTrack?.name,
    result,
    ok,
  });

  if (!ok) {
    console.warn('[Autoplay] First startPlayback failed, cleaning state and retrying once...');

    autoplayTriggeredRef.current = false;

    // Hard reset transient playback state after failed start
    cancelAllCrossfadeTimers();
    preloadedNextRef.current = null;
    isTransitioningRef.current = false;
    explicitStopRef.current = false;
    currentPlayingSoundRef.current = null;

    setTimeout(async () => {
      if (autoplayTriggeredRef.current) return;

      autoplayTriggeredRef.current = true;

      const retryResult = await startPlayback(firstTrack);
      const retryOk = retryResult === true || retryResult?.success === true;

      console.log('[AUTOPLAY RETRY RESULT]', {
        theme: selectedStyle,
        firstTrackId: firstTrack?.id,
        firstTrackName: firstTrack?.name,
        retryResult,
        retryOk,
      });

      if (!retryOk) {
        console.warn('[Autoplay] Retry failed, returning to safe state');

        autoplayTriggeredRef.current = false;
        currentPlayingSoundRef.current = null;

      // Stay on player UI to avoid flashing the track list
      setViewMode('player');
      setSelectedSound(null);
    }
    }, 180);
  }
};

    void runAutoplay();
    }, [autoplayFromUrl, librarySounds, isLoadingSounds, selectedStyle, _audioActiveOnMount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // SOUNDSCAPE CONTINUOUS PLAYBACK (V1)
  // ============================================
  // Track history to avoid immediate repeats in continuous mode
  const playedTracksRef = useRef([]);
  // Ref to hold startPlayback function for use in handleSoundscapeEnded
  const startPlaybackRef = useRef(null);
  useEffect(() => {
  if (!USE_NATIVE_AUDIO_PLAYER) {
    console.log('[NativeAudio] listener skipped: native player disabled');
    return;
  }

  if (!NativeAudio?.addListener) {
    console.warn('[NativeAudio] addListener unavailable', NativeAudio);
    return;
  }

  let sub = null;
    console.warn('[NativeAudio][LISTENER INSTALL SOURCE]', {
    path: window.location.pathname,
    time: Date.now(),
  });

 console.log('[NativeAudio] installing trackEnded listener');

NativeAudio.addListener('trackEnded', async () => {
  console.warn('[HERA][NATIVE TRACK ENDED][ENTRY]', {
    isPresenceMode: isPresenceModeRef.current,
    manualNextInProgress: manualNextInProgressRef.current,
    isTransitioning: isTransitioningRef.current,
    presenceActive: presenceService.isActive,
    presencePaused: presenceService.isPaused,
    voicePlaying: presenceService.isVoicePlaying,
  });


  if (manualNextInProgressRef.current) {
      console.warn('[HERA][NATIVE TRACK ENDED][SKIP VOICE ONLY] manual next in progress');
    } else if (!presenceService.isActive) {
      console.warn('[HERA][NATIVE TRACK ENDED][SKIP VOICE ONLY] presence inactive');
    } else if (
      presenceService.isActive &&
      !presenceService.isPaused &&
      !presenceService.isVoicePlaying
    ) {
      console.warn('[HERA][NATIVE TRACK ENDED][SKIP] tryTransitionMessage disabled - using awaited playNextVoice below');
    }

    if (currentPlaylistRef.current) {
      console.warn('[Playlist][NATIVE TRACK ENDED] advancing playlist', {
        playlist: currentPlaylistRef.current?.name,
        index: currentPlaylistIndexRef.current,
      });

    handleSoundEnded();
    return;
  }

  /*
  if (window.__HEAREN_NATIVE_TRANSITION_IN_FLIGHT) {
    console.log('[NativeAudio][trackEnded] ignored: GLOBAL transition already in flight');
    return;
  }
  */

  //window.__HEAREN_NATIVE_TRANSITION_IN_FLIGHT = true;

  //clearTimeout(window.__HEAREN_NATIVE_TRANSITION_LOCK_TIMER);

  //window.__HEAREN_NATIVE_TRANSITION_LOCK_TIMER = setTimeout(() => {
  //  window.__HEAREN_NATIVE_TRANSITION_IN_FLIGHT = false;
  //  isTransitioningRef.current = false;
  //  console.log('[NativeAudio][trackEnded] transition lock auto-released');
  //}, 2500);

  console.log('🔥 [NATIVE EVENT] trackEnded received in JS', {
    globalLock: window.__HEAREN_NATIVE_TRANSITION_IN_FLIGHT,
    isTransitioning: isTransitioningRef.current,
    selectedStyle,
    currentTrack: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.file,
  });

  const now = Date.now();

  window.__HEAREN_LAST_NATIVE_PLAY_STARTED_AT =
    window.__HEAREN_LAST_NATIVE_PLAY_STARTED_AT || 0;

  if (now - window.__HEAREN_LAST_NATIVE_PLAY_STARTED_AT < 3000) {
    console.log('[NativeAudio][trackEnded] ignored: ended too soon after play', {
      deltaMs: now - window.__HEAREN_LAST_NATIVE_PLAY_STARTED_AT,
    });
    return;
  }

  window.__HEAREN_LAST_NATIVE_TRACK_ENDED_AT =
    window.__HEAREN_LAST_NATIVE_TRACK_ENDED_AT || 0;

  if (now - window.__HEAREN_LAST_NATIVE_TRACK_ENDED_AT < 5000) {
    console.log('[NativeAudio][trackEnded] ignored: GLOBAL duplicate debounce', {
      deltaMs: now - window.__HEAREN_LAST_NATIVE_TRACK_ENDED_AT,
    });
    return;
  }

  window.__HEAREN_LAST_NATIVE_TRACK_ENDED_AT = now;

  console.warn('[NATIVE LISTENER VERSION TEST 2026-05-08]');
  console.log('[NativeAudio] trackEnded received → V1 native next');

  lastNativeTrackEndedAtRef.current = now;

  if (explicitStopRef.current) {
    console.log('[NativeAudio][trackEnded] ignored: explicit stop');
    return;
  }

  if (isTransitioningRef.current) {
    console.log('[NativeAudio][trackEnded] ignored: already transitioning');
    return;
  }

  isTransitioningRef.current = true;

  try {
    let nextTrack = preloadedNextRef.current?.track;

    console.log('[NativeAudio][trackEnded] nextTrack candidate', {
      current: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.display_name,
      next: nextTrack?.name || nextTrack?.display_name,
      preloadedIndex: preloadedNextRef.current?.index,
    });

    if (!nextTrack) {
      console.warn('[NativeAudio][trackEnded] no preloaded next track → using sequence fallback');

      try {
        nextTrack = getNextTrackInSequence?.();
      } catch (seqErr) {
        console.error('[NativeAudio][trackEnded] getNextTrackInSequence failed', seqErr);
      }

      if (!nextTrack) {
        console.error('[NativeAudio][trackEnded] no next track available → stop transition');
        return;
      }
    }

 
      const nextResolved =
        nextTrack.file || nextTrack.url || nextTrack.audioUrl || nextTrack.path;

      if (!nextResolved) {
        console.error('[NativeAudio][trackEnded] ABORT: nextTrack has no audio source', {
          nextTrack,
          file: nextTrack?.file,
          url: nextTrack?.url,
          audioUrl: nextTrack?.audioUrl,
          path: nextTrack?.path,
        });
        return;
      }

const nextNativeUrl = toNativeAudioUrl(nextResolved);

    console.log('[NativeAudio][trackEnded] playing next native track', {
      name: nextTrack?.name || nextTrack?.display_name,
      nextNativeUrl,
    });

    console.warn('[NativeAudio][trackEnded] BEFORE load', {
      next: nextTrack?.name,
      nextNativeUrl,
    });

    await nativeAudioPlayer.load(nextNativeUrl);

    console.warn('[NativeAudio][trackEnded] AFTER load / BEFORE play', {
      next: nextTrack?.name,
    });

    // HEAREN — Hera restored on native trackEnded transitions
    console.warn('[HERA][DEBUG][BEFORE AWAITED PLAYNEXTVOICE BLOCK]', {
      isActive: presenceService?.isActive,
      isPaused: presenceService?.isPaused,
      isVoicePlaying: presenceService?.isVoicePlaying,
      nextTrack: nextTrack?.name || nextTrack?.display_name,
    });

    let didEarlyPlayBeforeHera = false;

    try {
      if (
        presenceService &&
        presenceService.isActive &&
        !presenceService.isPaused &&
        !presenceService.isVoicePlaying
      ) {
        console.log('[NativeAudio][trackEnded] lockscreen-safe order: play next track BEFORE Hera');

        await nativeAudioPlayer.play();
        didEarlyPlayBeforeHera = true;
        window.__HEAREN_LAST_NATIVE_PLAY_STARTED_AT = Date.now();

        console.warn('[NativeAudio][trackEnded] AFTER early play / BEFORE Hera', {
          next: nextTrack?.name,
        });

        isTransitioningRef.current = false;
        console.warn('[NativeAudio][trackEnded] transition lock released before Hera voice');

        await presenceService.tryTransitionMessage();

        console.warn('[HERA][DEBUG][AFTER TRY TRANSITION MESSAGE STATE]', {
          isActive: presenceService?.isActive,
          isPaused: presenceService?.isPaused,
          isVoicePlaying: presenceService?.isVoicePlaying,
          trackCount: presenceService?._trackCount,
          tracksSinceTransition: presenceService?._tracksSinceTransition,
        });

        console.warn('[HERA][DEBUG][AFTER AWAITED TRY TRANSITION MESSAGE]');
      } else {
        console.log('[NativeAudio][trackEnded] Hera skipped', {
          isActive: presenceService?.isActive,
          isPaused: presenceService?.isPaused,
          isVoicePlaying: presenceService?.isVoicePlaying,
        });
      }
    } catch (heraErr) {
      console.warn('[NativeAudio][trackEnded] Hera trigger failed', heraErr);
    }

    if (!didEarlyPlayBeforeHera) {
      await nativeAudioPlayer.play();
      window.__HEAREN_LAST_NATIVE_PLAY_STARTED_AT = Date.now();

      console.warn('[NativeAudio][trackEnded] AFTER fallback play', {
        next: nextTrack?.name,
      });
    } else {
      console.warn('[NativeAudio][trackEnded] play already done before Hera', {
        next: nextTrack?.name,
      });
    }
const playedIndex =
  typeof preloadedNextRef.current?.index === 'number'
    ? preloadedNextRef.current.index
    : (() => {
        const themeForSequence = sessionThemeRef.current || selectedStyle;
        const rawSequenceLibrary = soundLibrary?.[themeForSequence];

        const sequenceTracks =
          Array.isArray(rawSequenceLibrary) ? rawSequenceLibrary :
          Array.isArray(rawSequenceLibrary?.tracks) ? rawSequenceLibrary.tracks :
          Array.isArray(rawSequenceLibrary?.sounds) ? rawSequenceLibrary.sounds :
          Array.isArray(rawSequenceLibrary?.items) ? rawSequenceLibrary.items :
          [];

        return sequenceTracks.findIndex(t => {
          const a = t?.id || t?.file || t?.path || t?.audioUrl || t?.name || t?.display_name;
          const b = nextTrack?.id || nextTrack?.file || nextTrack?.path || nextTrack?.audioUrl || nextTrack?.name || nextTrack?.display_name;
          return a && b && a === b;
        });
      })();

if (playedIndex >= 0) {
  currentPlayingIndexRef.current = playedIndex;
}

console.log('[NativeAudio][trackEnded] current index updated', {
  playedIndex,
  currentPlayingIndex: currentPlayingIndexRef.current,
});

setSelectedSound(nextTrack);
currentPlayingSoundRef.current = nextTrack;
nativeCurrentUrlRef.current = nextNativeUrl;

    setIsPlaying(true);

    setNowPlaying({
      title: nextTrack.display_name || nextTrack.name || 'Hearen',
      theme: themeFromUrl || selectedStyle,
      themeLabel: t?.themes?.[selectedStyle] || selectedStyle,
      isPlaying: true,
      trackPath: nextResolved,
      accent: undefined,
      playerState: {
        selectedSound: nextTrack,
        selectedStyle,
      },
    });

    preloadedNextRef.current = null;

console.log('[NativeAudio][trackEnded] BEFORE native refreeze next', {
  currentTrack: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.display_name,
  currentIndex: currentPlayingIndexRef.current,
  selectedStyle,
  sessionTheme: sessionThemeRef.current,
});

let following = getNextTrackInSequence();

if (!following?.nextTrack) {
  const themeForSequence = sessionThemeRef.current || selectedStyle;
  const raw = soundLibrary?.[themeForSequence];

  let tracks =
    Array.isArray(raw) ? raw :
    Array.isArray(raw?.tracks) ? raw.tracks :
    Array.isArray(raw?.sounds) ? raw.sounds :
    Array.isArray(raw?.items) ? raw.items :
    [];

  if (!tracks.length && nativeSessionTracksRef.current?.length) {
    tracks = nativeSessionTracksRef.current;

    console.warn('[NativeAudio][trackEnded] REFROZEN using nativeSessionTracksRef fallback', {
      count: tracks.length,
      currentIndex: currentPlayingIndexRef.current,
      currentTrack: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.display_name,
    });
  }

  const fallbackNextIndex = currentPlayingIndexRef.current + 1;
  const fallbackNextTrack = tracks[fallbackNextIndex] || tracks[0] || null;

  following = {
    nextTrack: fallbackNextTrack,
    nextIndex: fallbackNextTrack ? (tracks[fallbackNextIndex] ? fallbackNextIndex : 0) : -1,
  };

  console.warn('[NativeAudio][trackEnded] REFROZEN fallback direct index', {
    themeForSequence,
    tracksCount: tracks.length,
    currentIndex: currentPlayingIndexRef.current,
    nextIndex: following.nextIndex,
    nextTrack: following.nextTrack?.name || following.nextTrack?.display_name,
  });
}

if (following?.nextTrack) {
  preloadedNextRef.current = {
    track: following.nextTrack,
    index: following.nextIndex,
    nextTrack: following.nextTrack,
    nextIndex: following.nextIndex,
    native: true,
  };

  console.log('[NativeAudio][trackEnded] REFROZEN next native track', {
    current: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.display_name,
    frozenNext: following.nextTrack?.name || following.nextTrack?.display_name,
    frozenNextIndex: following.nextIndex,
  });
} else {
  console.warn('[NativeAudio][trackEnded] REFROZEN failed — no following track', {
    following,
    currentTrack: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.display_name,
    currentIndex: currentPlayingIndexRef.current,
  });
}

  } catch (err) {
    console.error('[NativeAudio][trackEnded] native next failed', err);
  } finally {
    isTransitioningRef.current = false;
    window.__HEAREN_NATIVE_TRANSITION_IN_FLIGHT = false;

    console.log('[NativeAudio][trackEnded] transition finished — lock released', {
      isTransitioning: isTransitioningRef.current,
    });
  }
  }).then((listener) => {
    sub = listener;
    console.log('[NativeAudio] trackEnded listener installed');
  }).catch((err) => {
    console.error('[NativeAudio] trackEnded listener install failed', err);
  });

  return () => {
    sub?.remove?.();
    window.__HEAREN_NATIVE_TRACK_ENDED_LISTENER_INSTALLED = false;
  };
}, []);
  // V1.3 FIX: Ref to track current playing sound (state can be stale in callbacks)
  const currentPlayingSoundRef = useRef(null);
  const sessionThemeRef = useRef(null);
  // V1.3 FIX: Ref to track current index in playlist (handles duplicate tracks)
  const currentPlayingIndexRef = useRef(0);
  const nativeSessionTracksRef = useRef([]);
  // Session token: invalidates stale transitions from previous theme/session
  const playbackSessionRef = useRef(0);

  const isTransitioningRef = useRef(false);
  const manualNextInProgressRef = useRef(false);
  const nativeStartInProgressRef = useRef(false);
  const nativeCurrentUrlRef = useRef(null);

  // V1: Prevent duplicate native trackEnded events
  const lastNativeTrackEndedAtRef = useRef(0);

  const preloadedNextRef = useRef(null); // { track, index }

  // V2: Crossfade scheduling timers — ALL tracked to prevent orphaned timers
  const crossfadeTimerRef = useRef(null);
  const crossfadeDelayTimerRef = useRef(null); // The 500ms delay timer
  const voiceLeadTimerRef = useRef(null); // The voice lead timer
  


  // ═══════════════════════════════════════════════════════════════════════
  // V2: CROSSFADE SCHEDULING — schedule smooth transition before track ends
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Cancel ALL crossfade-related timers (crossfade, delay, voice lead).
   * Must be called before scheduling new timers or when tracks change.
   */
  const cancelAllCrossfadeTimers = () => {
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current);
      crossfadeTimerRef.current = null;
    }
    if (crossfadeDelayTimerRef.current) {
      clearTimeout(crossfadeDelayTimerRef.current);
      crossfadeDelayTimerRef.current = null;
    }
    if (voiceLeadTimerRef.current) {
      clearTimeout(voiceLeadTimerRef.current);
      voiceLeadTimerRef.current = null;
    }
  };

  /**
   * Schedule a crossfade to the next track before the current track ends.
   * The crossfade starts `crossfadeDuration` (3.5s) before the track ends,
   * ensuring the preloaded buffer is used for an instant, gapless transition.
   */
  const scheduleCrossfade = () => {
  console.log('[CROSSFADE][ENTER scheduleCrossfade]', {
    selectedStyle,
    sessionTheme: sessionThemeRef.current,
    selectedSound: selectedSound?.name,
    currentPlaying: currentPlayingSoundRef.current?.name,
  });

  console.log('[HERA][SCHEDULECROSSFADE][FUNCTION REACHED]');

  console.log('[CROSSFADE][SCHEDULE DETAILS]', {
    currentTrack: currentPlayingSoundRef.current?.name,
    preloadedNext: preloadedNextRef.current?.track?.name,
    preloadedIndex: preloadedNextRef.current?.index,
    duration: currentPlayingSoundRef.current?.duration,
  });

  if (!preloadedNextRef.current?.track) {
    console.warn('[CROSSFADE][NO PRELOAD — skip schedule]');
    return;
  }
    // Cancel ALL previous timers to prevent orphaned callbacks
    cancelAllCrossfadeTimers();

    // Wait for track duration to be available
    crossfadeDelayTimerRef.current = setTimeout(() => {
      crossfadeDelayTimerRef.current = null;
      const actualDuration = soundscapeService.getCurrentTrackDuration();
      console.log('[CROSSFADE][DURATION CHECK]', {
        actualDuration,
        position: soundscapeService.getCurrentTrackPosition(),
      });
      if (!actualDuration || actualDuration < 8) {
        console.warn('[CROSSFADE][ABORT invalid duration]', { actualDuration });
        return;
      }

      const crossfadeDuration = 6.0;
      let delayMs;

      // Deep Calm: fixed 9-minute timer (track loops via BufferSourceNode.loop=true)
      const isDeepCalm = selectedStyle === 'deep_calm';
      if (isDeepCalm) {
        const DEEP_CALM_PLAY_SECONDS = 360; // 6 minutes per track (2 loops)
        delayMs = Math.max(0, (DEEP_CALM_PLAY_SECONDS - crossfadeDuration) * 1000);
        console.log(`[Crossfade] Deep Calm: scheduled in ${(delayMs / 1000).toFixed(0)}s (${DEEP_CALM_PLAY_SECONDS}s per track, actual buffer=${actualDuration.toFixed(0)}s)`);
      } else {
        const position = soundscapeService.getCurrentTrackPosition();
        const remaining = actualDuration - position;

        if (remaining < crossfadeDuration + 1) return; // too late

        delayMs = Math.max(0, (remaining - crossfadeDuration) * 1000);
        console.log(`[Crossfade] Scheduled in ${(delayMs / 1000).toFixed(1)}s (track ${actualDuration.toFixed(0)}s, remaining ${remaining.toFixed(1)}s)`);
      }
      console.log('[HERA][PRE-TRANSITION][BEFORE SCHEDULE BLOCK]', {
        delayMs,
        presenceActive: presenceService.isActive,
        isPresenceMode: isPresenceModeRef.current,
      });
      // Schedule Hera slightly before crossfade, to mask transition latency
      const heraLeadMs = 10000; // test: Hera starts earlier
      const heraDelayMs = Math.max(0, delayMs - heraLeadMs);

      console.log('[HERA][PRE-TRANSITION][CHECK]', {
        isPresenceMode: isPresenceModeRef.current,
        presenceActive: presenceService.isActive,
        voicePlaying: presenceService.isVoicePlaying,
        delayMs,
        heraDelayMs,
      });

      if (presenceService.isActive) {
        console.log('[HERA][PRE-TRANSITION] scheduled', {
          heraDelayMs,
          delayMs,
          heraLeadMs,
        });

        if (voiceLeadTimerRef.current) {
          clearTimeout(voiceLeadTimerRef.current);
        }

        voiceLeadTimerRef.current = setTimeout(() => {
          voiceLeadTimerRef.current = null;

          if (!isPresenceModeRef.current) return;
          if (!presenceService.isActive) return;
          if (presenceService.isVoicePlaying) return;

          console.log('[HERA][PRE-TRANSITION] fire before crossfade');

          presenceService.tryTransitionMessage();
        }, heraDelayMs);
      }

      // Schedule crossfade
      crossfadeTimerRef.current = setTimeout(() => {
        crossfadeTimerRef.current = null;

        console.warn('[TIMING][CROSSFADE TIMER FIRED]', {
          t: performance.now(),
          delayMs
        });

        performCrossfade();
      }, delayMs);
    }, 500);
  };

   /**
   * Perform the crossfade to the next track.
   * Voice transition is triggered HERE (at the actual transition moment), not before.
   */
  const performCrossfade = async () => {
  console.warn('[TIMING][PERFORM CROSSFADE ENTER]', {
    t: performance.now(),
    currentTrack:
      currentPlayingSoundRef.current?.display_name ||
      currentPlayingSoundRef.current?.name ||
      currentPlayingSoundRef.current?.file,
    preloadedNext:
      preloadedNextRef.current?.track?.display_name ||
      preloadedNextRef.current?.track?.name ||
      preloadedNextRef.current?.track?.file,
    isTransitioning: isTransitioningRef.current,
  });

  const sessionAtStart = playbackSessionRef.current;

  if (isTransitioningRef.current) {
    console.log('[Crossfade] Already transitioning, skipping');
    return;
  }

  // V1.2 FIX: Bail out if user explicitly stopped
  if (explicitStopRef.current) {
    console.log('[Crossfade] Stopped by user, aborting crossfade');
    return;
  }

  isTransitioningRef.current = true;

  // Cancel remaining timers
  cancelAllCrossfadeTimers();

  let nextTrack, nextIndex;
  if (preloadedNextRef.current) {
    ({ track: nextTrack, index: nextIndex } = preloadedNextRef.current);
    preloadedNextRef.current = null;
  } else {
    ({ nextTrack, nextIndex } = getNextTrackInSequence());
  }

  if (!nextTrack) {
    console.warn('[Crossfade] No next track, skipping');
    isTransitioningRef.current = false;
    return;
  }

  // Abort if a newer playback session started meanwhile
  if (sessionAtStart !== playbackSessionRef.current) {
    console.log('[Crossfade] Aborted — stale session');
    isTransitioningRef.current = false;
    return;
  }

  // Fire transition voice NOW (at the actual crossfade moment)
  // if (isPresenceModeRef.current && presenceService.isActive) {
    // console.log(
      // `[Crossfade] Firing transition voice at crossfade point ` +
      // `(track=${presenceService._trackCount}, sinceTransition=${presenceService._tracksSinceTransition})`
    // );
    // presenceService.tryTransitionMessage().catch((err) => {
      // console.error('[Crossfade] tryTransitionMessage error:', err);
    // });
  // }

  // Music volume: use current user volume (not category-dependent)
  const targetVolume = volume;

  console.log('[SEQ][CrossfadeChoose]', {
    selectedStyle,
    sessionTheme: sessionThemeRef.current,
    sessionAtStart,
    currentSession: playbackSessionRef.current,
    nextIndex,
    nextTrack: nextTrack?.name,
    nextTrackCategory: nextTrack?.category,
    usedPreload: !!nextTrack,
  });
   
  console.log('🔥 [TRANSITION PATH] using soundscapeService.crossfadeTo', {
    nextTrack: nextTrack?.name || nextTrack?.display_name || nextTrack?.file,
    useNative: USE_NATIVE_AUDIO_PLAYER,
  });

  const result = await soundscapeService.crossfadeTo(nextTrack, {
    volume: targetVolume / 100,
    loop: selectedStyle === 'deep_calm',
    onEnded: handleSoundscapeEnded
  });

  // Abort if a newer playback session started during async crossfade
  if (sessionAtStart !== playbackSessionRef.current) {
    console.log('[Crossfade] Aborted after await — stale session');
    isTransitioningRef.current = false;
    return;
  }

  // V1.2 FIX: After async crossfade, re-check if user stopped during the await
  if (explicitStopRef.current) {
    console.log('[Crossfade] User stopped during crossfade — stopping new track');
    soundscapeService.stop({ reason: 'post-crossfade-cleanup' });
    isTransitioningRef.current = false;
    return;
  }

  if (result.success) {
  console.log(`[Crossfade] Transition to "${nextTrack.name}" complete`);

  currentPlayingIndexRef.current = nextIndex;
  currentPlayingSoundRef.current = nextTrack;
  setSelectedSound(nextTrack);

  setNowPlaying({
    title: nextTrack.display_name || nextTrack.name || 'Unknown',
    theme: themeFromUrl || selectedStyle,
    themeLabel: t?.themes?.[selectedStyle] || selectedStyle,
    isPlaying: true,
    trackPath: nextTrack.file || nextTrack.path,
    accent: undefined,
    playerState: { selectedSound: nextTrack, selectedStyle },
  });

  triggerPreloadNext();
  scheduleCrossfade();
  }

  setTimeout(() => {
    isTransitioningRef.current = false;
  }, 6000);
};

    /**
   * Get next track in sequence for favorites/soundscapes
   * Uses index-based navigation to handle duplicate tracks correctly
   */
   const getNextTrackInSequence = () => {
     console.log('[SEQ][ENTER getNextTrackInSequence]');

     const themeForSequence = sessionThemeRef.current || selectedStyle;
const rawSequenceLibrary = soundLibrary?.[themeForSequence];

const sequenceLibraryCount =
  Array.isArray(rawSequenceLibrary) ? rawSequenceLibrary.length :
  Array.isArray(rawSequenceLibrary?.tracks) ? rawSequenceLibrary.tracks.length :
  Array.isArray(rawSequenceLibrary?.sounds) ? rawSequenceLibrary.sounds.length :
  Array.isArray(rawSequenceLibrary?.items) ? rawSequenceLibrary.items.length :
  0;

console.log('[SEQ][SOURCE DEBUG]', {
  selectedStyle,
  sessionTheme: sessionThemeRef.current,
  themeForSequence,
  soundLibraryKeys: Object.keys(soundLibrary || {}),
  rawExists: !!rawSequenceLibrary,
  rawIsArray: Array.isArray(rawSequenceLibrary),
  rawKeys: rawSequenceLibrary && typeof rawSequenceLibrary === 'object'
    ? Object.keys(rawSequenceLibrary)
    : null,
  soundLibraryCount: sequenceLibraryCount,
  librarySoundsCount: Array.isArray(librarySounds) ? librarySounds.length : null,
  firstLibrarySound: librarySounds?.[0] || null,
});

    // Helper function to get translated name from soundLibrary
    const getTranslatedName = (soundPath, originalName, category) => {
      let filename = soundPath;
      let searchCategory = category;
      
      // Handle proxy URL format
      if (soundPath.startsWith('/api/r2/stream/')) {
        const parts = soundPath.split('/');
        searchCategory = parts[4];
        filename = parts[5];
      } else if (soundPath.includes('/')) {
        filename = soundPath.split('/').pop();
      }
      
      // Search in the specific category first
      if (searchCategory && soundLibrary[searchCategory]) {
        const found = soundLibrary[searchCategory].find(s => {
          const soundFilename = s.file?.split('/').pop() || s.id;
          return soundFilename === filename;
        });
        if (found) return found.name;
      }
      
      // Search in all categories
      for (const cat of Object.keys(soundLibrary)) {
        const found = soundLibrary[cat].find(s => {
          const soundFilename = s.file?.split('/').pop() || s.id;
          return soundFilename === filename;
        });
        if (found) return found.name;
      }
      
      return originalName;
    };
    
    // 🔥 NORMALISATION SOURCE TRACKS (ULTRA ROBUSTE)
let sequenceSounds = [];

// 1. PRIORITÉ FAVORITES
if (themeForSequence === 'favorites') {
  sequenceSounds = favorites.map(fav => {
    const sound = fav.sound || {};
    const path = sound.path || '';

    let category, filename, streamUrl;

    if (path.startsWith('/api/r2/stream/')) {
      const parts = path.split('/');
      category = parts[4] || 'unknown';
      filename = parts[5] || path;
      streamUrl = reencodeAudioPath(path);
    } else if (path.includes('/') && !path.startsWith('/')) {
      [category, filename] = path.split('/');
      filename = extToM4a(filename);
      streamUrl = buildAudioUrl(category, filename, false);
    } else {
      filename = extToM4a(path.startsWith('/') ? path.slice(1) : path);
      category = sound.category || 'classical';
      streamUrl = buildAudioUrl(category, filename, false);
    }

    const displayName =
      sound.name ||
      getTranslatedName(path, filename, category) ||
      filename;

    return {
      id: path,
      name: displayName,
      file: streamUrl,
      path,
      type: sound.type || 'file',
      category: (category || 'unknown').toLowerCase(),
    };
  });

  console.log('[SEQ][FAVORITES BUILT]', {
    count: sequenceSounds.length,
    first: sequenceSounds?.[0],
  });

} else {
  // 2. SOURCE NORMALE
  if (Array.isArray(rawSequenceLibrary)) {
    sequenceSounds = rawSequenceLibrary;
  } else if (Array.isArray(rawSequenceLibrary?.tracks)) {
    sequenceSounds = rawSequenceLibrary.tracks;
  } else if (Array.isArray(rawSequenceLibrary?.sounds)) {
    sequenceSounds = rawSequenceLibrary.sounds;
  } else if (Array.isArray(rawSequenceLibrary?.items)) {
    sequenceSounds = rawSequenceLibrary.items;
  }

  // 3. FALLBACK getTracksForTheme
  if (!sequenceSounds.length) {
    sequenceSounds = getTracksForTheme(themeForSequence);
  }

  // 4. CLEAN
  sequenceSounds = (sequenceSounds || []).filter(Boolean);

  // 5. FALLBACK FINAL
  if (!sequenceSounds.length && Array.isArray(librarySounds)) {
    console.warn('[SEQ][FALLBACK LIBRARY SOUNDS]');

    sequenceSounds = librarySounds.filter(s =>
      s?.category === themeForSequence ||
      s?.theme === themeForSequence ||
      s?.style === themeForSequence ||
      s?.themeId === themeForSequence
    );
  }

  console.log('[SEQ][FINAL SOURCE]', {
    themeForSequence,
    count: sequenceSounds.length,
    first: sequenceSounds?.[0],
  });
}

// SÉCURITÉ
if (!sequenceSounds.length) {
  console.error('[SEQ][NO TRACKS FOUND]');
  return {
    nextTrack: null,
    nextIndex: -1,
  };
}

// NEXT TRACK LOGIC
const currentIndex = currentPlayingIndexRef.current ?? 0;
const nextIndex = (currentIndex + 1) % sequenceSounds.length;
const nextTrack = sequenceSounds[nextIndex];

console.log('[SEQ][NEXT TRACK]', {
  currentIndex,
  nextIndex,
  total: sequenceSounds.length,
  nextTrack,
});

// IMPORTANT
// Do NOT mutate currentPlayingIndexRef here.
// This function is also used for preload.
// The current index must only be updated when the track actually starts playing.
return {
  nextTrack,
  nextIndex,
};
};
  // ═══════════════════════════════════════════════════════════════════════
  // V1.1.1: PRELOAD ORCHESTRATION — gapless iOS transitions
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Trigger preload of the NEXT track. Call after current track starts playing.
   * Freezes { track, index } in preloadedNextRef so the transition uses the
   * same track even if the playlist changes while the current one is playing.
   */
  const triggerPreloadNext = async () => {
  console.log('[PRELOAD][ENTER triggerPreloadNext]', {
    selectedStyle,
    sessionTheme: sessionThemeRef.current,
    selectedSound: selectedSound?.name,
    currentPlaying: currentPlayingSoundRef.current?.name,
  });

  const result = getNextTrackInSequence();

console.log('[PRELOAD][RAW RESULT]', result);

const nextTrack = result?.nextTrack;
const nextIndex = result?.nextIndex;

console.log('[SEQ][PreloadNext]', {
  selectedStyle,
  sessionTheme: sessionThemeRef.current,
  nextIndex,
  nextTrack: nextTrack?.name,
  nextTrackId: nextTrack?.id,
  nextTrackFile: nextTrack?.file,
  nextTrackPath: nextTrack?.path,
  nextTrackCategory: nextTrack?.category,
});

if (!nextTrack) {
  console.warn('[PRELOAD][ABORT] No nextTrack resolved — keeping existing preload', {
    result,
    selectedStyle,
    sessionTheme: sessionThemeRef.current,
    existingPreloaded: preloadedNextRef.current?.track?.name || preloadedNextRef.current?.track?.display_name,
    existingIndex: preloadedNextRef.current?.index,
  });

  // Important: do NOT clear preloadedNextRef here.
  // A duplicate/stale preload call must not erase a valid frozen next track.
  return;
}

  // MODE NATIF iOS
  if (USE_NATIVE_AUDIO_PLAYER) {
    const nextResolved =
      nextTrack.file || nextTrack.url || nextTrack.audioUrl || nextTrack.path;

    const nextNativeUrl = nextResolved?.startsWith('http')
      ? nextResolved
      : `${API_BASE_URL}${nextResolved}`;

    console.log('[NATIVE PRELOAD] before preloadNext', {
      nextTrack: nextTrack?.name,
      nextNativeUrl,
    });

    // 🔥 IMPORTANT : on fige tout de suite le prochain track côté JS
    // Même si le preload natif échoue, la transition saura quoi jouer.
    preloadedNextRef.current = {
      track: nextTrack,
      index: nextIndex,
      nextTrack,
      nextIndex,
      native: true,
      nativeUrl: nextNativeUrl,
    };

    console.log('[PRELOAD][FROZEN JS BEFORE NATIVE]', {
      nextTrack: nextTrack?.name,
      nextIndex,
      nativeUrl: nextNativeUrl,
    });

    console.warn('🔥🔥🔥 [NATIVE PRELOAD] SKIPPED — JS FROZEN FALLBACK ONLY 🔥🔥🔥', {
      frozenTrack: preloadedNextRef.current?.track?.name,
      frozenIndex: preloadedNextRef.current?.index,
      nativeUrl: nextNativeUrl,
    });

    // IMPORTANT TEST:
    // Do not call nativeAudioPlayer.preloadNext here.
    // It appears to block the JS flow before returning.

    console.log('[NATIVE PRELOAD][DISABLED — using load/play fallback]');

    return;

    return;
  }

  // FALLBACK WEB AUDIO
  preloadedNextRef.current = {
    track: nextTrack,
    index: nextIndex,
  };

  console.log(`[Preload] Freezing next track: "${nextTrack.name}" (idx=${nextIndex})`);

  soundscapeService.preloadTrack(nextTrack);
};

  /**
   * Handler for soundscape track ended - auto-play next track in sequence
   * V1.1.1: Uses the PRELOADED buffer for gapless playback.
   * This is ONLY for soundscapes/favorites (not playlists which have their own handler)
   */
  const handleSoundscapeEnded = async () => {

  console.warn('[TIMING][HANDLE SOUND END ENTER]', {
    t: performance.now()
  });

  console.log('[MINI][ONENDED TEST] handleSoundscapeEnded fired', {
    path: window.location.pathname,
    currentTrack: currentPlayingSoundRef.current?.name,
    isTransitioning: isTransitioningRef.current,
    hasPreloaded: !!preloadedNextRef.current,
  });
  
    console.log('[Soundscape][ENTER handleSoundscapeEnded]', {
      explicitStop: explicitStopRef.current,
      isTransitioning: isTransitioningRef.current,
      currentTrack: currentPlayingSoundRef.current?.name,
      currentIndex: currentPlayingIndexRef.current,
      selectedStyle,
      sessionTheme: sessionThemeRef.current,
      hasPreloaded: !!preloadedNextRef.current,
    });
    // V1.2 FIX: Bail out if user explicitly stopped
    if (explicitStopRef.current) {
      console.log('[Soundscape] Stopped by user, ignoring onEnded');
      return;
    }
    // V1.3 FIX: Prevent rapid calls that could cause issues
    if (isTransitioningRef.current) {
      console.log('[Soundscape] Already transitioning, ignoring...');
      return;
    }
    isTransitioningRef.current = true;

    // Cancel all scheduled crossfade timers (track ended naturally = fallback path)
    cancelAllCrossfadeTimers();

    console.warn('[TIMING][A END REAL]', {
      track:
        currentPlayingSoundRef.current?.display_name ||
        currentPlayingSoundRef.current?.name ||
        currentPlayingSoundRef.current?.file,
      hasPreloaded: !!preloadedNextRef.current,
      t: performance.now()
    });

    const transitionT0 = performance.now();
    console.log('[Soundscape] Track ended naturally — fallback transition...');

    // Hera first, then transition shortly after to mask latency
    console.warn('[TIMING][HERA CONDITION CHECK]', {
      isActive: presenceService.isActive,
      isVoicePlaying: presenceService.isVoicePlaying,
      t: performance.now()
    });

    if (presenceService.isActive && !presenceService.isVoicePlaying) {
      console.log('[HERA][FALLBACK PRE-TRANSITION] start before fallback transition');

      console.warn('[TIMING][HERA START REQUEST]', {
        t: performance.now()
      });

      presenceService.tryTransitionMessage().catch((err) => {
        console.error('[HERA][FALLBACK PRE-TRANSITION] error:', err);
      });

      console.warn('[TIMING][HERA ARTIFICIAL DELAY START]', {
        delayMs: 2000,
        t: performance.now()
      });

      await new Promise((resolve) => setTimeout(resolve, 2000));

      console.warn('[TIMING][HERA ARTIFICIAL DELAY END]', {
        t: performance.now()
      });

      console.log('[HERA][FALLBACK PRE-TRANSITION] delayed transition start');
    }

    console.log('[HERA][TRANSITION STATE BEFORE REQUEST]', {
      isPresenceMode: isPresenceModeRef.current,
      isActive: presenceService.isActive,
      isPaused: presenceService.isPaused,
      isVoicePlaying: presenceService.isVoicePlaying,
      audioContextState: presenceService.audioContext?.state,
      hasGraph: !!presenceService.graph,
      trackCount: presenceService._trackCount,
      tracksSinceTransition: presenceService._tracksSinceTransition,
    });
    
    
    // TEST MODE: Hera at every fallback/native transition.
    // Do not rely on the pre-transition timer for now.
    if (
      isPresenceModeRef.current &&
      presenceService.isActive &&
      !presenceService.isVoicePlaying
    ) {
      console.log('[HERA][TRANSITION][DIRECT FIRE]', {
        track: presenceService._trackCount,
        sinceTransition: presenceService._tracksSinceTransition,
      });

      presenceService.tryTransitionMessage().catch((err) => {
        console.error('[HERA][TRANSITION][DIRECT FIRE] error:', err);
      });
    } else {
      console.log('[HERA][TRANSITION][DIRECT SKIPPED]', {
        isPresenceMode: isPresenceModeRef.current,
        isActive: presenceService.isActive,
        isVoicePlaying: presenceService.isVoicePlaying,
      });
    }
    // Load and start next track
        let nextTrack, nextIndex;
        if (preloadedNextRef.current) {
      nextTrack =
        preloadedNextRef.current.nextTrack ||
        preloadedNextRef.current.track;

      nextIndex =
        preloadedNextRef.current.nextIndex ??
        preloadedNextRef.current.index;

      console.log('[Soundscape] Using PRELOADED next', {
        nextTrack: nextTrack?.name,
        nextIndex,
        preloadedRaw: preloadedNextRef.current,
      });

      preloadedNextRef.current = null;
    } else {
      ({ nextTrack, nextIndex } = getNextTrackInSequence());
      console.log('[Soundscape][COMPUTED NEXT DEBUG]', {
        nextTrack,
        nextIndex,
        hasNextTrack: !!nextTrack,
        selectedStyle,
        sessionTheme: sessionThemeRef.current,
        currentIndex: currentPlayingIndexRef.current,
        currentTrack: currentPlayingSoundRef.current?.name,
      });
      console.log(`[Soundscape] Computed next: "${nextTrack?.name}" (idx=${nextIndex})`);
      console.log('[Soundscape][NEXT DEBUG]', {
        nextTrackId: nextTrack?.id,
        nextTrackName: nextTrack?.name,
        nextIndex,
        hasStartPlaybackRef: !!startPlaybackRef.current,
      });
    }

    if (nextTrack) {
      currentPlayingIndexRef.current = nextIndex;
      currentPlayingSoundRef.current = nextTrack;
      setSelectedSound(nextTrack);

      if (startPlaybackRef.current) {
        try {
          if (explicitStopRef.current) {
            console.log('[Soundscape] User stopped before next track, aborting');
            isTransitioningRef.current = false;
            return;
          }

          nativeStartInProgressRef.current = false;

          await startPlaybackRef.current(nextTrack);

          currentPlayingIndexRef.current = nextIndex;
          currentPlayingSoundRef.current = nextTrack;

          console.log('[SEQ][INDEX UPDATED AFTER TRANSITION]', {
            nextIndex,
            nextTrack: nextTrack?.name,
          });

          // 🔥 NATIVE FALLBACK: after starting the next track,
          // immediately freeze the following one for the next ended event.
          if (USE_NATIVE_AUDIO_PLAYER) {
            const following = getNextTrackInSequence();

            if (following?.nextTrack) {
              preloadedNextRef.current = {
                track: following.nextTrack,
                index: following.nextIndex,
                nextTrack: following.nextTrack,
                nextIndex: following.nextIndex,
                native: true,
              };

              console.log('[NATIVE FALLBACK][REFROZEN NEXT AFTER TRANSITION]', {
                currentTrack: nextTrack?.name,
                frozenNextTrack: following.nextTrack?.name,
                frozenNextIndex: following.nextIndex,
              });
            } else {
              console.warn('[NATIVE FALLBACK][NO FOLLOWING TRACK TO FREEZE]');
            }
          }

          if (explicitStopRef.current) {
            console.log('[Soundscape] User stopped during playback start, cleaning up');
            soundscapeService.stop({ reason: 'post-transition-cleanup' });
            isTransitioningRef.current = false;
            return;
          }

          const gapMs = Math.round(performance.now() - transitionT0);
          console.log(`[Soundscape] Fallback transition complete in ${gapMs}ms`);

          isTransitioningRef.current = false;
        } catch (error) {
          console.error('[Soundscape] startPlayback error:', error);
          isTransitioningRef.current = false;
        }
      }
    } else {
      console.warn('[Soundscape] No next track available, stopping');
      setIsPlaying(false);
      isTransitioningRef.current = false;
    }

    // Allow next transition
    setTimeout(() => { isTransitioningRef.current = false; }, 100);
  };

  /**
   * Handle MANUAL next in soundscape mode (user pressed Next button).
   * Same as handleSoundscapeEnded but WITHOUT triggering voice messages.
   * Voice messages must ONLY play during NATURAL transitions.
   */
  const handleManualNextSoundscape = async () => {
    console.warn('[NEXT][MANUAL HANDLER ENTERED]');
    const themeForSequence = sessionThemeRef.current || selectedStyle;

    // V1.2 FIX: Manual user action always takes priority — cancel any ongoing transition
    cancelAllCrossfadeTimers();
    isTransitioningRef.current = true;

    console.log('[Soundscape] Manual next — NO voice trigger');
        console.warn('[NEXT][MANUAL GUARD SET]', {
      manualNextInProgress: manualNextInProgressRef.current,
      isTransitioning: isTransitioningRef.current,
    });
    manualNextInProgressRef.current = true;

    // Increment track count (so the rhythm rule still counts this as a played track)
    if (isPresenceModeRef.current && presenceService.isActive) {
      presenceService._trackCount++;
      presenceService._tracksSinceTransition++;
    }

    // V1.2 FIX: Before computing next, sync currentPlayingIndexRef from actual playing track
    // This prevents stale index after Mini Player resume (ref resets to 0 on remount)
    const serviceTrack = soundscapeService.currentTrack;
    if (serviceTrack) {
      // Build the correct track list (favorites uses a different source than getTracksForTheme)
      let tracks;
      if (themeForSequence === 'favorites') {
        tracks = favorites.map(fav => {
          const sound = fav.sound || {};
          return { name: sound.name, file: sound.path, path: sound.path };
        });
      } else {
        tracks = getTracksForTheme(themeForSequence);
      }
      const actualIdx = tracks.findIndex(t =>
        t.name === serviceTrack.name || t.file === serviceTrack.file || t.path === serviceTrack.path
      );
      if (actualIdx >= 0 && actualIdx !== currentPlayingIndexRef.current) {
        console.log('[Soundscape] Correcting index from', currentPlayingIndexRef.current, 'to', actualIdx);
        currentPlayingIndexRef.current = actualIdx;
        // Also clear stale preload since index changed
        preloadedNextRef.current = null;
      }
    }

    let nextTrack, nextIndex;
    if (preloadedNextRef.current) {
      ({ track: nextTrack, index: nextIndex } = preloadedNextRef.current);
      preloadedNextRef.current = null;
    } else {
      preloadedNextRef.current = null;
      ({ nextTrack, nextIndex } = getNextTrackInSequence());
    }

   if (nextTrack) {
  currentPlayingIndexRef.current = nextIndex;
  currentPlayingSoundRef.current = nextTrack;
  setSelectedSound(nextTrack);

  if (startPlaybackRef.current) {
    try {
      await startPlaybackRef.current(nextTrack);

      currentPlayingIndexRef.current = nextIndex;
      currentPlayingSoundRef.current = nextTrack;

      console.log('[SEQ][INDEX UPDATED AFTER TRANSITION]', {
        nextIndex,
        nextTrack: nextTrack?.name,
      });

    } catch (err) {
      console.error('[Soundscape] Manual next error', err);

    } finally {
      isTransitioningRef.current = false;

      setTimeout(() => {
        manualNextInProgressRef.current = false;
        console.log('[Soundscape] Manual next guard released');
      }, 1500);
    }
  }
}

setTimeout(() => {
  isTransitioningRef.current = false;
}, 100);

setTimeout(() => {
  manualNextInProgressRef.current = false;
  isTransitioningRef.current = false;
}, 1500);
};

  // Define categories from canonical themes (SINGLE SOURCE OF TRUTH)
  // Use i18n for translated labels (with fallback if t is not available)
  // t is an object, not a function - access nested keys via dot notation
  const getLabel = (labelKey, fallback) => {
    if (t && typeof t === 'object') {
      // labelKey format: "themes.sleep" -> access t.themes.sleep
      const keys = labelKey.split('.');
      let value = t;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }
      if (value && typeof value === 'string') return value;
    }
    return fallback;
  };
  
  // Show ALL themes but mark premium ones as locked
  const categories = [
    ...CANONICAL_THEMES.map(theme => ({
      id: theme.id,
      name: getLabel(theme.labelKey, theme.name),
      subtitle: '',
      icon: theme.icon,
      bgImage: theme.bgImage,
      locked: isFreeUser && !freeThemeIds.includes(theme.id),
    })),
    // NOTE: "My Sounds" / custom uploads removed for V1
    // BUILD 58: Favorites count uses favoritePaths.length (single source of truth)
    { 
      id: 'favorites', 
      name: `${t?.categoryFavorites || (currentLang === 'en' ? 'Favorites' : 'Favoris')}${favoritePaths.length > 0 ? ` (${favoritePaths.length})` : ''}`, 
      subtitle: currentLang === 'en' ? 'Your saved sounds' : 'Vos sons enregistrés', 
      icon: '⭐',
      bgImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80' // Warm amber/gold tones
    },
  ];
  
  // Add playlist category if viewing a playlist
  if (currentPlaylist) {
    categories.unshift({ 
      id: 'playlist', 
      name: currentPlaylist.name, 
      icon: '🎼',
      bgImage: null
    });
  }
  
  // Get the selected category for background image
  const selectedCategory = categories.find(cat => cat.id === selectedStyle);

  // Helper functions for favorites
  // V3 FIX: Check against canonical path format
  /**
   * BUILD 52.6: Get canonical favorite path for consistent deduplication
   * This is the SINGLE SOURCE OF TRUTH for favorite path resolution
   * 
   * Priority:
   * 1. sound.path (if already canonical format)
   * 2. sound.id (if stable identifier)
   * 3. Extract from sound.file URL
   * 
   * Returns: "category/filename.m4a" or "soundscapes/theme/filename.m4a"
   * BUILD 56.1 iOS FIX: NEVER returns null - always builds a fallback key
   */
  const getCanonicalFavoritePath = (sound) => {
    if (!sound) {
      console.error('[Fav][Soundscapes] ERROR: sound is null/undefined');
      return null;
    }

    // Log input for debugging iOS issues
    console.log('[Fav][Soundscapes] input:', { 
      id: sound.id, 
      path: sound.path, 
      file: sound.file?.substring?.(0, 50), 
      category: sound.category,
      name: sound.name 
    });

    // 1. PRIORITY: Use sound.path if it's a stable path (not a URL)
    if (sound.path && typeof sound.path === 'string' && !sound.path.startsWith('/api/') && !sound.path.startsWith('http')) {
      // V1.1.1: Always decode URIs for consistent canonical paths (fixes %2F → /)
      const decodedPath = decodeURIComponent(sound.path);
      console.log('[Fav][Soundscapes] canonicalPath=' + decodedPath + ' source=path');
      return decodedPath;
    }

    // 2. ID with slash is already canonical
    if (sound.id && typeof sound.id === 'string' && sound.id.includes('/') && !sound.id.startsWith('http')) {
      console.log('[Fav][Soundscapes] canonicalPath=' + sound.id + ' source=id');
      return sound.id;
    }

    // 3. Build from category + id/filename
    if (sound.category) {
      const filename = sound.id || sound.filename || sound.name;
      if (filename) {
        const cleanFilename = String(filename).split('/').pop();
        const filenameWithExt = cleanFilename.includes('.') ? cleanFilename : `${cleanFilename}.m4a`;
        const canonicalPath = `${sound.category.toLowerCase()}/${filenameWithExt}`;
        console.log('[Fav][Soundscapes] canonicalPath=' + canonicalPath + ' source=category+filename');
        return canonicalPath;
      }
    }

    // 4. Extract from streaming URL - with robust iOS fallback using split()
    const file = sound.file || sound.path || '';
    if (typeof file === 'string' && file.includes('/api/r2/stream/')) {
      // Try regex first
      const match = file.match(/\/api\/r2\/stream\/([^/]+)\/([^/]+)$/);
      if (match) {
        const [, category, filename] = match;
        const canonicalPath = `${category}/${decodeURIComponent(filename)}`;
        console.log('[Fav][Soundscapes] canonicalPath=' + canonicalPath + ' source=stream-url-regex');
        return canonicalPath;
      }
      
      // iOS FALLBACK: If regex fails, use split() approach
      const parts = file.split('/api/r2/stream/')[1];
      if (parts) {
        const segments = parts.split('/').filter(Boolean);
        if (segments.length >= 2) {
          // Handle soundscapes/theme/filename or category/filename
          if (segments[0] === 'soundscapes' && segments.length >= 3) {
            const canonicalPath = `soundscapes/${segments[1]}/${decodeURIComponent(segments[2])}`;
            console.log('[Fav][Soundscapes] canonicalPath=' + canonicalPath + ' source=stream-url-split-soundscapes');
            return canonicalPath;
          } else {
            const canonicalPath = `${segments[0]}/${decodeURIComponent(segments[segments.length - 1])}`;
            console.log('[Fav][Soundscapes] canonicalPath=' + canonicalPath + ' source=stream-url-split');
            return canonicalPath;
          }
        }
      }
    }

    // 5. Use selectedStyle as category (component context)
    if (selectedStyle && selectedStyle !== 'favorites' && selectedStyle !== 'playlist' && selectedStyle !== 'custom') {
      const filename = sound.id || sound.name || sound.filename;
      if (filename) {
        const cleanFilename = String(filename).split('/').pop();
        const filenameWithExt = cleanFilename.includes('.') ? cleanFilename : `${cleanFilename}.m4a`;
        // Map canonical style to API category
        const styleToCategory = {
          'classical': 'classique',
          'breathing': 'respiration',
          'chill': 'chill_lofi',
          'sleep': 'sommeil',
          'nature': 'nature',
          'pulse_flow': 'pulse_flow'
        };
        const category = styleToCategory[selectedStyle] || selectedStyle.toLowerCase();
        const canonicalPath = `${category}/${filenameWithExt}`;
        console.log('[Fav][Soundscapes] canonicalPath=' + canonicalPath + ' source=selectedStyle');
        return canonicalPath;
      }
    }

    // 6. LAST RESORT: Build from any available identifier
    const anyFilename = sound.id || sound.name || sound.filename || sound.display_name;
    if (anyFilename) {
      const cleanFilename = String(anyFilename).split('/').pop();
      const filenameWithExt = cleanFilename.includes('.') ? cleanFilename : `${cleanFilename}.m4a`;
      // Use 'unknown' category - better than returning null
      const canonicalPath = `unknown/${filenameWithExt}`;
      console.warn('[Fav][Soundscapes] canonicalPath=' + canonicalPath + ' source=lastResort (no category)');
      return canonicalPath;
    }

    console.error('[Fav][Soundscapes] ERROR: Could not determine canonicalPath for:', JSON.stringify(sound));
    return null;
  };

  // BUILD 58: isFavorite uses favoritePaths as single source of truth
  const isFavorite = (soundOrPath) => {
    if (!soundOrPath) return false;
    
    // Get canonical path - handle both sound objects and path strings
    const canonicalPath = typeof soundOrPath === 'string'
      ? getCanonicalFavoritePath({ path: soundOrPath, id: soundOrPath })
      : getCanonicalFavoritePath(soundOrPath);
    
    if (!canonicalPath) return false;
    
    // BUILD 58: Check against favoritePaths (single source of truth)
    return favoritePaths.includes(canonicalPath);
  };

  // BUILD 58: toggleFavorite updates favoritePaths FIRST (single source of truth)
  const toggleFavorite = async (sound) => {
    const canonicalPath = getCanonicalFavoritePath(sound);
    
    if (!canonicalPath) {
      console.error('[Fav] ERROR: canonicalPath is null for sound:', JSON.stringify(sound));
      return;
    }
    
    // BUILD 58: Check against favoritePaths (single source of truth)
    const isCurrentlyFavorite = favoritePaths.includes(canonicalPath);
    console.log('[Fav] action=' + (isCurrentlyFavorite ? 'remove' : 'add') + ' path=' + canonicalPath);
    
    try {
      // Extract category from canonical path
      const pathParts = canonicalPath.split('/');
      const category = pathParts.length > 1 ? pathParts[0] : (sound.category || 'nature');
      
      const soundRef = {
        path: canonicalPath,
        name: sound.name || sound.display_name || sound.id,
        type: sound.type || 'library',
        category: category.toLowerCase()
      };

      if (isCurrentlyFavorite) {
        // Remove from favorites
        await api.removeFavorite(canonicalPath);
        
        // BUILD 58: Update favoritePaths FIRST (new array reference)
        setFavoritePaths(prev => {
          const next = prev.filter(p => p !== canonicalPath);
          console.log('[Fav] REMOVED path=' + canonicalPath + ' newCount=' + next.length);
          return next;
        });
        
        // Then update favorites objects for UI
        setFavorites(prev => prev.filter(fav => fav.sound?.path !== canonicalPath));
        
      } else {
        // V2 GATING: Limit favorites for free users
        if (isFreeUser && favoritePaths.length >= FREE_FAVORITES_LIMIT) {
          toast.error(
            currentLang === 'en' 
              ? `Free limit: ${FREE_FAVORITES_LIMIT} favorites max. Upgrade to Premium for unlimited.`
              : `Limite gratuite : ${FREE_FAVORITES_LIMIT} favoris max. Passez Premium pour un accès illimité.`,
            { duration: 4000 }
          );
          return;
        }
        // Add to favorites
        const result = await api.addFavorite(soundRef);
        
        // BUILD 58: Update favoritePaths FIRST (new array reference, dedupe)
        setFavoritePaths(prev => {
          if (prev.includes(canonicalPath)) {
            console.log('[Fav] SKIPPED (already exists) path=' + canonicalPath);
            return prev;
          }
          const next = [...prev, canonicalPath];
          console.log('[Fav] ADDED path=' + canonicalPath + ' newCount=' + next.length);
          return next;
        });
        
        // Then update favorites objects for UI
        const newFav = {
          id: result.id || `fav_${Date.now()}`,
          sound: soundRef,
          created_at: new Date().toISOString()
        };
        setFavorites(prev => {
          if (prev.some(f => f.sound?.path === canonicalPath)) return prev;
          return [...prev, newFav];
        });
      }
    } catch (error) {
      console.error('[Fav] ERROR:', error.message);
      if (error.type === 'LIMIT_REACHED') {
        setLimitMessage(error.message);
        setTimeout(() => setLimitMessage(null), 5000);
      }
    }
  };
  
  // Timer management functions
  const startTimer = () => {
    if (!sessionDuration) {
      return;
    }
    
    if (timerInterval) {
      return;
    }
    
    const startTime = Date.now();
    setSessionStartTime(startTime);
    
    // Check timer every second
    const interval = setInterval(() => {
      const elapsedSinceStart = (Date.now() - startTime) / 60000; // minutes since this play
      const totalElapsed = elapsedTimeBeforePause + elapsedSinceStart; // total elapsed including pauses
      const remaining = sessionDuration - totalElapsed;
      
      
      // Update tick to force re-render of timer display
      setTimerTick(prev => prev + 1);
      
      if (remaining <= 0) {
        stopWithFadeOut();
        clearInterval(interval);
        setTimerInterval(null);
      } else if (remaining <= 1) {
        // Start fade out in the last minute
        const fadeProgress = 1 - remaining; // 0 to 1 over the last minute
        const newVolume = volume * (1 - fadeProgress);
        console.log(`🔉 Fade out: progress=${fadeProgress.toFixed(2)}, volume=${newVolume.toFixed(0)}%`);
        audioService.setVolume(newVolume / 100);
      }
    }, 1000);
    
    setTimerInterval(interval);
  };
  
  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setSessionStartTime(null);
    setElapsedTimeBeforePause(0); // Reset accumulated time when stopping completely
  };
  
  const stopWithFadeOut = async () => {
    
    // Prepare additional parameters for session save
    let therapyType = 'nature'; // default
    if (['classique', 'piano_ambiant', 'chill_lofi', 'respiration', 'sommeil', 'nature', 'pulse_flow'].includes(selectedStyle)) {
      therapyType = selectedStyle;
    } else if (selectedStyle === 'custom') {
      therapyType = 'custom_sound';
    }
    
    const additionalParams = {
      sound_name: selectedSound?.name || selectedSound?.display_name || 'Unknown',
      volume: volume,
      equalizer_enabled: equalizerEnabled,
      notch_enabled: notchEnabled,
      category: selectedStyle
    };
    
    if (currentPlaylist) {
      additionalParams.playlist_name = currentPlaylist.name;
      additionalParams.playlist_id = currentPlaylist.id;
    }
    
    // Add Coach session info if this session was started from Coach
    if (fromCoach && sessionDate) {
      additionalParams.from_coach = true;
      additionalParams.coach_session_date = sessionDate;
      additionalParams.coach_protocol = sessionProtocol || 'complete_therapy';
      additionalParams.recommended_duration = recommendedDuration;
    }
    
    const fadeSteps = 20;
    const fadeInterval = 50; // ms
    const currentVolume = volume;
    
    // BUILD 66: Fade out using both services
    for (let i = fadeSteps; i >= 0; i--) {
      const fadeVolume = (currentVolume * i) / fadeSteps;
      soundscapeService.setVolume(fadeVolume / 100);
      audioService.setVolume(fadeVolume / 100);
      await new Promise(resolve => setTimeout(resolve, fadeInterval));
    }
    
    // Stop playback (user-initiated) - use both services
    soundscapeService.stop({ reason: 'user' });
    audioService.stopTherapy({ reason: 'user' });
    // Stop presence voice messages immediately
    if (isPresenceModeRef.current) {
      presenceService.stop();
    }
    // V2.2.5: Clear playlist watcher
    if (playlistWatcherRef.current) {
      clearInterval(playlistWatcherRef.current);
      playlistWatcherRef.current = null;
    }
    setIsPlaying(false);
    setCurrentAudioSource(null);
    setPlaylistStartTime(null);
    setCurrentSessionStart(null); // Reset session start
    stopTimer();
    
    // Exit fullscreen mode
    setIsFullscreen(false);
    
    // Stop timer with additional parameters (automatically saves session to backend)
    await therapyTimer.stop({
      additionalParams: {
        ...additionalParams,
        therapy_type: therapyType
      }
    });
    
    // Clear coach session from sessionStorage after completion
    sessionStorage.removeItem('activeCoachSession');
    
    // Restore volume
    soundscapeService.setVolume(volume / 100);
    audioService.setVolume(volume / 100);
  };
  
  const startPlayback = async (sound) => {
  console.log('[START PLAYBACK] entered', {
    soundId: sound?.id,
    soundName: sound?.name,
    soundFile: sound?.file,
    soundPath: sound?.path,
    soundUrl: sound?.url || sound?.audioUrl || sound?.fileUrl,
    selectedStyle,
    viewMode,
    USE_NATIVE_AUDIO_PLAYER
  });

  console.log('[START PLAYBACK][ENTER]', {
    sound: sound?.name || sound?.display_name || sound?.file,
    selectedStyle,
    themeFromUrl,
    currentPlaylist: !!currentPlaylistRef.current,
  });

  console.log('[START PLAYBACK][GLOBAL CALLED]', {
    sound: sound?.name,
    selectedStyle,
    viewMode,
    category: sound?.category,
    file: sound?.file,
    path: sound?.path,
  });

    // ========================================================================
    // BUILD 66: Refactored to use soundscapeService for iOS Safari compatibility
    // The service uses an event-driven state machine that preserves the iOS
    // gesture context required for audio playback.
    // ========================================================================
    console.warn('[START PLAYBACK][BEFORE GLOBAL SESSION]');
    console.log('🚀 [Soundscapes][startPlayback] Starting via soundscapeService...');
    console.warn('[START PLAYBACK][ENTRY]');
    console.warn('[START PLAYBACK][MARKER A — before global session block]');
    console.log('🎯 [Soundscapes][startPlayback] sound=', {
      id: sound?.id,
      name: sound?.name,
      type: sound?.type,
      file: sound?.file,
      path: sound?.path,
      category: sound?.category
    });
    
    // V1.3 FIX: Update ref immediately so handleSoundscapeEnded has correct current track
    currentPlayingSoundRef.current = sound;
    
    // Resolve audio file from sound object
    const resolved = sound?.file || sound?.path || sound?.filename;
    console.log('🔗 [Soundscapes][startPlayback] resolved=', resolved);
    
    if (!resolved && sound?.type !== 'generated') {
      console.error('❌ [Soundscapes][startPlayback] No valid file/path for sound');
      toast.error(t?.errorPlayingSound || 'No audio file path for sound', { duration: 5000 });
      return false;
    }
    
    // Music volume: always 80% default for all categories
    const targetVolume = volume; // Use current volume state (default 80%, user can change)
    
    console.log(`🔊 [Soundscapes][Volume] Category: ${selectedStyle}, Target: ${targetVolume}%`);
    
    // Handle generated sounds (noise) separately - they use audioService directly
    if (sound.type === 'generated') {
      try {
        const source = audioService.generateNoise(sound.generator, sound.frequency);
        setCurrentAudioSource(source);
        setIsPlaying(true);
        setSelectedSound(sound);
        startSessionTimer();
        setViewMode('player');
        setIsFullscreen(true);
        therapyTimer.startActivity({
          type: 'therapeutic',
          label: sound.name || 'Generated Sound',
          category: 'generated'
        });
        setVolume(targetVolume);
        audioService.setVolume(targetVolume / 100);
        return true;
      } catch (error) {
        console.error('❌ Error generating noise:', error);
        toast.error(`${t?.errorPlayingSound || 'Erreur'}: ${error.message}`, { duration: 5000 });
        return false;
      }
    }
    
    // ========================================================================
    // USE soundscapeService for file-based playback (iOS Safari compatible)
    // ========================================================================
    
    // Build track object for the service
    const trackForService = {
      ...sound,
      file: resolved,
      category: sound.category || selectedStyle
    };
    
    // Determine the onEnded callback
    // Use ref (not state) to avoid stale closure — state may not be updated yet when called synchronously
    const onEndedCallback = currentPlaylistRef.current ? handleSoundEnded : handleSoundscapeEnded;
   
    console.warn('[HEAREN V1][AUDIO PATH CHECK]', {
      selectedStyle,
      themeFromUrl,
      soundCategory: sound?.category,
      soundType: sound?.type,
      soundName: sound?.name || sound?.display_name,
      resolved,
      useNative: USE_NATIVE_AUDIO_PLAYER,
      isGenerated: sound?.type === 'generated',
      isDeepCalm: selectedStyle === 'deep_calm',
      isPlaylist: !!currentPlaylistRef.current,
    });

    // Native iOS audio engine — guarded path
console.log('[TherapyPlayer][NativeAudio] switch =', USE_NATIVE_AUDIO_PLAYER);

if (USE_NATIVE_AUDIO_PLAYER) {
  console.log('[START PLAYBACK][NATIVE] branch entered');

  if (nativeStartInProgressRef.current) {
    console.warn('[START PLAYBACK][NATIVE] already in progress');
    return true;
  }

  nativeStartInProgressRef.current = true;

  try {
    const nativeUrl = toNativeAudioUrl(resolved);

    console.log('[START PLAYBACK][NATIVE] before load', nativeUrl);
    await nativeAudioPlayer.load(nativeUrl);

    console.log('[START PLAYBACK][NATIVE] after load / before play');
    await nativeAudioPlayer.play();

    console.log('[START PLAYBACK][NATIVE] after play');

    setSelectedSound(sound);
    currentPlayingSoundRef.current = sound;
    nativeCurrentUrlRef.current = nativeUrl;

    setIsPlaying(true);
    setViewMode('player');
    setIsFullscreen(true);

    setNowPlaying({
      title: sound.display_name || sound.name || 'Unknown',
      theme: themeFromUrl || selectedStyle,
      themeLabel: t?.themes?.[selectedStyle] || selectedStyle,
      isPlaying: true,
      trackPath: resolved,
      accent: undefined,
      playerState: { selectedSound: sound, selectedStyle },
    });

    explicitStopRef.current = false;
    startSessionTimer();

    therapyTimer.startActivity({
      type: 'therapeutic',
      label: sound.display_name || sound.name || 'Soundscape',
      category: selectedStyle || sound.category || 'soundscape'
    });

    setVolume(targetVolume);

    await triggerPreloadNext();

    if (!currentPlaylistRef.current && preloadedNextRef.current?.track) {
      console.log('[NATIVE] preload ready after startPlayback', {
        currentTrack: currentPlayingSoundRef.current?.name,
        preloadedNext: preloadedNextRef.current?.track?.name,
      });
    }

    return true;

  } catch (error) {
    console.error('[START PLAYBACK][NATIVE] FAILED', error);
    toast.error(`Native audio failed: ${error.message || error}`);
    nativeCurrentUrlRef.current = null;
    return false;

  } finally {
    nativeStartInProgressRef.current = false;
  }
}

// Call the service - it handles iOS gesture context internally
// The service emits state changes that are captured by our useEffect subscription
console.log('[START PLAYBACK][BEFORE SERVICE PLAY]', {
  sound: sound?.name,
  selectedStyle,
  file: trackForService?.file,
  path: trackForService?.path,
});

console.log('[START PLAYBACK][SERVICE CAPABILITIES]', {
  hasPlay: typeof soundscapeService.play,
  hasPlaySoundscapeSession: typeof soundscapeService.playSoundscapeSession,
  hasSessionGetter: typeof soundscapeService.getSoundscapeSession,
});

const useGlobalSoundscapeSession =
  !currentPlaylistRef.current &&
  selectedStyle !== 'deep_calm' &&
  typeof soundscapeService.playSoundscapeSession === 'function';

const sessionThemeId = sessionThemeRef.current || themeFromUrl || selectedStyle;

const rawSessionLibrary = soundLibrary?.[sessionThemeId];

const sessionTracks =
  Array.isArray(rawSessionLibrary) ? rawSessionLibrary :
  Array.isArray(rawSessionLibrary?.tracks) ? rawSessionLibrary.tracks :
  Array.isArray(rawSessionLibrary?.sounds) ? rawSessionLibrary.sounds :
  Array.isArray(rawSessionLibrary?.items) ? rawSessionLibrary.items :
  [];

console.warn('[START PLAYBACK][MARKER BEFORE GLOBAL CHECK]');

let sessionStartIndex = sessionTracks.findIndex(t => {
  const a = t?.id || t?.file || t?.path || t?.audioUrl || t?.name || t?.display_name;
  const b = trackForService?.id || trackForService?.file || trackForService?.path || trackForService?.audioUrl || trackForService?.name || trackForService?.display_name;
  return a && b && a === b;
});

if (sessionStartIndex < 0) sessionStartIndex = 0;

console.log('[START PLAYBACK][GLOBAL SESSION CHECK]', {
  useGlobalSoundscapeSession,
  sessionThemeId,
  sessionTracksCount: sessionTracks.length,
  sessionStartIndex,
  track: trackForService?.name || trackForService?.display_name || trackForService?.file,
});

console.warn('[START PLAYBACK][MARKER AFTER GLOBAL CHECK]');

const result = useGlobalSoundscapeSession
  ? await soundscapeService.playSoundscapeSession({
      themeId: sessionThemeId,
      tracks: sessionTracks.length ? sessionTracks : [trackForService],
      startIndex: sessionStartIndex,
      volume: targetVolume / 100,
    })
   : await soundscapeService.play(trackForService, {
      volume: targetVolume / 100,
      loop: selectedStyle === 'deep_calm',
      onEnded: onEndedCallback,
    });
    console.log('[START PLAYBACK][AFTER SERVICE PLAY]', {
      result,
      success: result?.success,
      selectedStyle,
      currentPlaylist: !!currentPlaylistRef.current,
    });

    console.log('[Playlist][AFTER PLAY RESULT RAW]', {
      result,
      selectedStyle,
      hasPlaylist: !!currentPlaylistRef.current,
      playlistName: currentPlaylistRef.current?.name
    });

    if (result?.success !== false) {
      console.log('✅ [Soundscapes][startPlayback] soundscapeService.play() success');
      console.log('[Playlist][ENTER SUCCESS BLOCK]');

      // HEAREN V1 — Playlist auto-advance watcher installed after real service play
      if (currentPlaylistRef.current) {
        console.log('[Playlist][SERVICE PLAY OK] installing watcher after real play', {
        playlist: currentPlaylistRef.current?.name,
        index: currentPlaylistIndexRef.current,
        sound: sound?.name,
        hasBuffer: !!soundscapeService.soundBuffer,
        hasSource: !!soundscapeService.soundSource,
      });

      if (playlistWatcherRef.current) {
        clearInterval(playlistWatcherRef.current);
      }

      setTimeout(() => {
        console.log('[Playlist][AUDIO SERVICE WATCHER] installing delayed', {
          hasAudioContext: !!audioService.audioContext,
          hasIOSBuffer: !!audioService.iosSoundBuffer,
          hasIOSSource: !!audioService.iosSoundSource,
        });

        playlistWatcherRef.current = setInterval(() => {
      const duration = audioService.iosSoundBuffer?.duration || 0;

  const position =
    audioService.audioContext && audioService.iosStartTime
      ? audioService.audioContext.currentTime - audioService.iosStartTime
      : 0;

  const sourceEnded = !!audioService.iosSoundSource?._stopped;

  console.log('[Playlist][AUDIO SERVICE WATCH]', {
    hasBuffer: !!audioService.iosSoundBuffer,
    hasSource: !!audioService.iosSoundSource,
    sourceEnded,
    position,
    duration,
  });

  if (
    sourceEnded ||
    (duration > 0 && isFinite(duration) && position >= duration - 0.5)
  ) {
    console.log('[Playlist][AUDIO SERVICE WATCH] ended detected', {
      position,
      duration,
    });

    clearInterval(playlistWatcherRef.current);
    playlistWatcherRef.current = null;

    handleSoundEnded();
  }
    }, 500);
  }, 1000);
      
      // V1.2: Notify mini player of current track + store player state for resume
      setNowPlaying({
        title: sound.display_name || sound.name || 'Unknown',
        theme: themeFromUrl || selectedStyle,
        themeLabel: t?.themes?.[selectedStyle] || selectedStyle,
        isPlaying: true,
        trackPath: resolved,
        accent: undefined,
        playerState: { selectedSound: sound, selectedStyle },
      });
      explicitStopRef.current = false;
      
      // Update UI state (some of this is also done by the onStateChange listener)
      setSelectedSound(sound);
      startSessionTimer();
      setViewMode('player');
      setIsFullscreen(true);
      
      // Start therapy timer
      therapyTimer.startActivity({
        type: playlistIdFromUrl ? 'playlist' : 'therapeutic',
        label: sound.name || 'Therapeutic Sound',
        category: sound.category || 'general'
      });
      
      const handleStopWithFade = async (reason = 'user') => {
        try {
          explicitStopRef.current = true;
          isTransitioningRef.current = false;
          preloadedNextRef.current = null;
          autoplayTriggeredRef.current = false;

          await soundscapeService.stopWithFade(220, { reason });

          currentPlayingIndexRef.current = null;
          currentPlayingSoundRef.current = null;

          if (presenceService?.stop) {
      const shouldKeepPresence =
        soundscapeService.hasActiveSoundscapeSession?.() ||
        soundscapeService.playbackState === 'PLAYING';

      if (shouldKeepPresence && reason !== 'user' && reason !== 'media_session') {
        console.log('[TherapyPlayer] handleStopWithFade skipped presence stop — audio/global session active', {
          reason,
          playbackState: soundscapeService.playbackState,
        });
      } else {
        presenceService.stop();
      }
    }

          setIsPlaying(false);
          setSelectedSound(null);
        } catch (err) {
          console.error('[TherapyPlayer] handleStopWithFade failed', err);
          soundscapeService.stop?.({ reason: 'fallback' });
          setIsPlaying(false);
          setSelectedSound(null);
        }
      };


      // Set volume
      setVolume(targetVolume);
      soundscapeService.setVolume(targetVolume / 100);

      // Register media session handlers for hardware controls (AirPods, etc.)
      soundscapeService.setMediaSessionHandlers({
        onNext: handleNext,
        onStop: () => handleStopWithFade('media_session')
      });
      
      // V2.2.4: Preload next track for BOTH playlists and soundscapes (reduces latency)
      // But schedule crossfade ONLY for soundscapes (playlists use sequential playback)
      if (!useGlobalSoundscapeSession) {
        triggerPreloadNext();

        if (!currentPlaylistRef.current) {
          scheduleCrossfade();
        }
      } else {
        console.log('[START PLAYBACK][GLOBAL SESSION ACTIVE] preload/crossfade handled by soundscapeService');
      }
      

      if (currentPlaylistRef.current) {
        if (playlistWatcherRef.current) clearInterval(playlistWatcherRef.current);

        console.log('[Playlist][INSTALLING AUDIO WATCHER NOW]', {
          hasAudioContext: !!audioService.audioContext,
          hasIOSBuffer: !!audioService.iosSoundBuffer,
          hasIOSSource: !!audioService.iosSoundSource,
          currentPlaylist: currentPlaylistRef.current?.name,
          selectedSound: sound?.name
        });

        console.log('[Playlist][BEFORE DELAYED WATCHER TIMEOUT]');

        setTimeout(() => {
          console.log('[Playlist][AUDIO SERVICE WATCHER] installing delayed', {
            hasAudioContext: !!audioService.audioContext,
            hasIOSBuffer: !!audioService.iosSoundBuffer,
            hasIOSSource: !!audioService.iosSoundSource,
          });
        }, 1000);
      }

        playlistWatcherRef.current = setInterval(() => {
          console.log('[Playlist][WATCHER INTERVAL CREATED]', {
            interval: !!playlistWatcherRef.current
          });
          const el = soundscapeService.audioElement;
          const src = soundscapeService.soundSource;
          // Check HTMLAudioElement (non-iOS) or BufferSourceNode (iOS)
          const isEnded = el ? el.ended : (src && src._stopped);
          const duration = el ? el.duration : (soundscapeService.soundBuffer ? soundscapeService.soundBuffer.duration : 0);
          const position = el ? el.currentTime : soundscapeService.getCurrentTrackPosition();
          
          if (isEnded || (duration > 0 && isFinite(duration) && position >= duration - 0.3)) {
            clearInterval(playlistWatcherRef.current);
            playlistWatcherRef.current = null;
            handleSoundEnded();
          }
        }, 500);
      }
      
      return true;
    } else {
      console.error('❌ [Soundscapes][startPlayback] soundscapeService.play() failed:', result.reason);
      // Error toast is shown by the onStateChange listener
      return false;
    }
  };
  
  // Store startPlayback in ref for use by handleSoundscapeEnded
  startPlaybackRef.current = startPlayback;
  
  // saveTherapySession function removed - session saving now handled by therapyTimer service

  // ============================================
  // UNIFIED AUDIO CONTROLS HANDLERS
  // ============================================
  
/**
 * CANONICAL teardown - shared session shutdown without navigation
 */
const performSessionTeardown = useCallback(async (reason = 'unknown') => {
  try {
    console.log('[Teardown] Starting canonical session teardown:', reason);

    // Mark as explicitly stopped FIRST — prevents async callbacks from restarting
    explicitStopRef.current = true;

    // Cancel crossfade timers BEFORE stopping audio
    cancelAllCrossfadeTimers();

    // Clear playlist watcher
    if (playlistWatcherRef.current) {
      clearInterval(playlistWatcherRef.current);
      playlistWatcherRef.current = null;
    }

    // Stop timer and save session to backend
    await therapyTimer.stop();

    // Stop audio using both services (one will be no-op)
    clearNowPlaying();
    await soundscapeService.stopWithFade(220, { reason });
    audioService.stopTherapy({ reason });

    // Core playback/session state reset
    setIsPlaying(false);
    setCurrentAudioSource(null);
    setSessionStartTime(null);
    setCurrentSessionStart(null);

    // Stop timers
    stopTimer();
    stopSessionTimer();

    // Night Mode: cleanup voice audio if playing
    if (nightVoiceAudioRef.current) {
      nightVoiceAudioRef.current.pause();
      nightVoiceAudioRef.current = null;
    }

    // Stop presence service if active
    if (isPresenceMode) {
      if (soundscapeService.hasActiveSoundscapeSession?.()) {
        console.log('[TherapyPlayer] Presence stop skipped — global session active');
      } else {
        presenceService.stop();
      }
    }

    // Reset critical refs for stability
    preloadedNextRef.current = null;
    isTransitioningRef.current = false;
    currentPlayingSoundRef.current = null;
    currentPlayingIndexRef.current = 0;

    console.log('[Teardown] Completed');
  } catch (error) {
    console.error('❌ [Teardown] Error:', error);
  }
}, [clearNowPlaying, isPresenceMode]);

useEffect(() => {
  registerSessionStopHandler(async (reason = 'global-stop') => {
    const isMiniPlayerCloseTotal = reason === 'mini-player-close-total';

    if (keepAudioAliveOnBackRef.current && !isMiniPlayerCloseTotal) {
      console.log('[GlobalStop] ignored because back keeps audio alive');
      return;
    }

    console.log('[GlobalStop]', {
      reason,
      isMiniPlayerCloseTotal,
    });

    if (isMiniPlayerCloseTotal) {
      explicitStopRef.current = true;
      autoplayTriggeredRef.current = false;
      allowNextAutoplayRef.current = false;
      keepAudioAliveOnBackRef.current = false;
    }

    await performSessionTeardown(reason);

    setIsPlaying(false);
    setIsFullscreen(false);
    setSelectedSound(null);
    setCurrentPlaylist(null);
    currentPlaylistRef.current = null;

    if (isMiniPlayerCloseTotal) {
      setCurrentAudioSource(null);
      setViewMode('selection');
      clearNowPlaying();

      console.log('[GlobalStop] mini-player close total completed');
    }
  });

  return () => {
    // Intentionally keep the last registered handler available
    // while the app session is alive; do not clear here.
  };
}, [performSessionTeardown, clearNowPlaying]);
  /**
   * STOP handler - Ends session, saves to backend, closes player
   */
  const handleStop = async () => {
  try {
    console.log('[TherapyPlayer][STOP] user stop requested');

    // Prevent any residual autoplay / resume
    explicitStopRef.current = true;
    autoplayTriggeredRef.current = false;
    allowNextAutoplayRef.current = false;

    console.warn('[HERA][STOP] stopping presence service');

    presenceService.stop?.();
    
    // Native stop first
    if (USE_NATIVE_AUDIO_PLAYER) {
      console.log('[TherapyPlayer][NativeAudio] stop');
      await nativeAudioPlayer.stop();
      nativeStartInProgressRef.current = false;
      nativeCurrentUrlRef.current = null;
    }

    // Canonical teardown
    await performSessionTeardown('user');

    // Clean UI / mini player BEFORE navigation
    setIsPlaying(false);
    setSelectedSound(null);
    setCurrentPlaylist(null);
    currentPlaylistRef.current = null;
    clearNowPlaying?.();

    // Exit fullscreen mode
    setIsFullscreen(false);

    // Return to Home or Favorites page
    if (themeFromUrl === 'favorites' || themeFromUrl === 'playlist' || playlistIdFromUrl) {
      navigate('/favorites', { replace: true });
    } else if (themeFromUrl) {
      navigate('/', { replace: true });
    } else {
      setViewMode('selection');
    }

  } catch (error) {
    console.error('❌ [TherapyPlayer] Error in handleStop:', error);
  }
};
  
  /**
   * PLAY/PAUSE handler - Standard media control, does NOT close player
   * BUILD 66: Refactored to use soundscapeService for iOS Safari compatibility
   */
  const handlePlayPause = async () => {
    // ═══════════════════════════════════════════════════════════════════════
    // V1.1.1: iOS lock/unlock gesture resume
    // If the AudioContext needs a user gesture to resume (post-lock), do it now.
    // ═══════════════════════════════════════════════════════════════════════
    if (soundscapeService._needsGestureResume) {
      console.log('[TherapyPlayer] Gesture resume after lock/unlock');
      await soundscapeService.gestureResume();
      return; // Don't toggle play/pause — just resume
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // BUILD 66: iOS SAFARI FIX
    // The key insight: we don't need to resume audioContext here manually.
    // soundscapeService.play() does this internally in the correct gesture context.
    // For resume from pause, soundscapeService.resume() handles it.
    // ═══════════════════════════════════════════════════════════════════════
    
    if (!selectedSound) {
      alert(t?.selectSoundAlert || 'Veuillez sélectionner un son');
      return;
    }

    if (USE_NATIVE_AUDIO_PLAYER) {
      console.log('[TherapyPlayer][NativeAudio] handlePlayPause', {
        isPlaying,
        currentTrack: currentPlayingSoundRef.current?.name || currentPlayingSoundRef.current?.file,
        welcomeVoicePlayed: welcomeVoicePlayedRef.current,
      });

      if (isPlaying) {
        console.log('[TherapyPlayer][NativeAudio] PAUSE only');

        await nativeAudioPlayer.pause();

        setIsPlaying(false);
        setNowPlaying({ isPlaying: false });
        therapyTimer.pause();
        pauseSessionTimer();

        return;
      }



  // IMPORTANT:
  // Pause -> Play must be a pure native resume.
  // It must NOT restart the session and must NOT replay Hera welcome.
  if (currentPlayingSoundRef.current) {
    console.log('[TherapyPlayer][NativeAudio] RESUME only — block Hera welcome');

    welcomeVoicePlayedRef.current = true;

    await nativeAudioPlayer.play();

    setIsPlaying(true);
    setNowPlaying({ isPlaying: true });
    therapyTimer.resume?.();
    resumeSessionTimer?.();

    return;
  }

// Only if there is no current track: true fresh start
console.log('[TherapyPlayer][NativeAudio] FRESH START');

await startPlayback(selectedSound);
return;
}

    console.log(`🎵 [TherapyPlayer] handlePlayPause: isPlaying=${isPlaying}`);
    
    if (isPlaying) {
      // ═══════════════════════════════════════════════════════════════════════
      // PAUSE - Use soundscapeService for file sounds, audioService for generated
      // ═══════════════════════════════════════════════════════════════════════
      console.log('⏸️ [TherapyPlayer] Pausing...');
      
      if (selectedSound.type === 'generated') {
        // Generated sounds use audioService
        audioService.pauseTherapy();
      } else {
        // File-based sounds use soundscapeService
        soundscapeService.pause();
      }
      
      therapyTimer.pause();
      pauseSessionTimer();
      // Note: setIsPlaying(false) will be called by onStateChange listener
      // V1.2: Sync mini player
      setNowPlaying({ isPlaying: false });
      
      if (timerInterval && sessionStartTime) {
        const elapsedSinceStart = (Date.now() - sessionStartTime) / 60000;
        setElapsedTimeBeforePause(elapsedTimeBeforePause + elapsedSinceStart);
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      
    } else {
      // ═══════════════════════════════════════════════════════════════════════
      // PLAY - Check if resuming from pause or starting fresh
      // ═══════════════════════════════════════════════════════════════════════
      console.log('▶️ [TherapyPlayer] Playing...');
      
      setIsFullscreen(true);
      therapyTimer.resume();
      
      if (!currentSessionStart) {
        setCurrentSessionStart(Date.now());
        if (currentPlaylist) setPlaylistStartTime(Date.now());
      }
      
      if (sessionDuration) startTimer();
      
      // Check if resuming from pause
      const isPausedSoundscape = soundscapeService.getIsPaused();
      const isPausedAudio = audioService.isPausedState();
      
      if (selectedSound.type === 'generated' && isPausedAudio) {
        console.log('🔄 [TherapyPlayer] Resuming generated sound from pause...');
        const resumed = audioService.resumeTherapy();
        if (resumed) {
          setIsPlaying(true);
          setNowPlaying({ isPlaying: true });
          resumeSessionTimer();
        } else {
          await startPlayback(selectedSound);
        }
      } else if (isPausedSoundscape) {
        console.log('🔄 [TherapyPlayer] Resuming soundscape from pause...');
        const resumed = soundscapeService.resume();
        if (resumed) {
          // setIsPlaying(true) will be called by onStateChange listener
          setNowPlaying({ isPlaying: true });
          resumeSessionTimer();
        } else {
          // Fallback: start fresh
          await startPlayback(selectedSound);
        }
      } else {
        // Start fresh playback
        console.log('🎬 [TherapyPlayer] Starting fresh...');
        await startPlayback(selectedSound);
      }
    }
  };
  
  /**
   * NEXT handler - Go to next sound in playlist, continue session
   */
  const handleNext = () => {
    if (!currentPlaylist || !currentPlaylist.sounds || currentPlaylist.sounds.length === 0) {
      return;
    }

    // Cancel all crossfade timers when user manually advances
    cancelAllCrossfadeTimers();
    
    const nextIndex = currentPlaylistIndex + 1;
    
    if (nextIndex < currentPlaylist.sounds.length) {
      
      // Update index
      setCurrentPlaylistIndex(nextIndex);
      currentPlaylistIndexRef.current = nextIndex;
      
      const nextSound = currentPlaylist.sounds[nextIndex];
      setSelectedSound(nextSound);
      
      // If playing, start next sound immediately
      if (isPlaying) {
        startPlayback(nextSound);
      }
    } else {
      // Loop back to start
      
      setCurrentPlaylistIndex(0);
      currentPlaylistIndexRef.current = 0;
      
      const firstSound = currentPlaylist.sounds[0];
      setSelectedSound(firstSound);
      
      if (isPlaying) {
        startPlayback(firstSound);
      }
    }
  };

  const handleSoundEnded = () => {
    // V1.2 FIX: Bail out if user explicitly stopped
    if (explicitStopRef.current) {
      console.log('[Playlist] Stopped by user, ignoring onEnded');
      return;
    }
    // V2.2.5: Clear the polling watcher (might be triggered by watcher or native event)
    if (playlistWatcherRef.current) {
      clearInterval(playlistWatcherRef.current);
      playlistWatcherRef.current = null;
    }
    
    const playlist = currentPlaylistRef.current;
    console.log('   Current playlist:', playlist?.name);
    console.log('   Playlist sounds count:', playlist?.sounds?.length);
    
    // Note: Duration limit is now handled by the general sessionDuration timer
    // No need to check here as it will be handled by stopWithFadeOut
    
    // Play next sound in playlist
    if (playlist && playlist.sounds) {
      const nextIndex = currentPlaylistIndexRef.current + 1;
      console.log('   Calculated nextIndex:', nextIndex);
      console.log('   nextIndex < sounds.length?', nextIndex < playlist.sounds.length);
      
      if (nextIndex < playlist.sounds.length) {
        console.log('   Next sound will be:', playlist.sounds[nextIndex].name);
        
        // Update both state and ref
        setCurrentPlaylistIndex(nextIndex);
        currentPlaylistIndexRef.current = nextIndex;
        setSelectedSound(playlist.sounds[nextIndex]);
        
        // Auto-play next sound after a short delay
        setTimeout(() => {
          // V1.2 FIX: Re-check stop flag inside timeout
          if (explicitStopRef.current) return;
          if (startPlaybackRef.current) {
            console.log('   setTimeout executing, starting sound:', playlist.sounds[nextIndex].name);
            startPlaybackRef.current(playlist.sounds[nextIndex]);
          }
        }, 300);
      } else {
        // Loop back to start if playlist ends
        console.log('   Will play sound 0:', playlist.sounds[0].name);
        
        // Update both state and ref
        setCurrentPlaylistIndex(0);
        currentPlaylistIndexRef.current = 0;
        setSelectedSound(playlist.sounds[0]);
        
        setTimeout(() => {
          // V1.2 FIX: Re-check stop flag inside timeout
          if (explicitStopRef.current) return;
          if (startPlaybackRef.current) {
            console.log('   setTimeout executing, restarting with sound 0:', playlist.sounds[0].name);
            startPlaybackRef.current(playlist.sounds[0]);
          }
        }, 300);
      }
    }
  };

  const handleVolumeChange = (value) => {
    // Handle both array format [value] and direct value
    const rawVolume = Array.isArray(value) ? value[0] : value;
    
    // V1.4: Apply listening comfort limits
    const safeVolume = applySafeVolume(rawVolume);
    
    setVolume(safeVolume);
    // BUILD 66: Apply volume to both services
    if (isPlaying) {
      soundscapeService.setVolume(safeVolume / 100);
      audioService.setVolume(safeVolume / 100);
    }
  };

  const handleSoundSelect = async (sound, index = -1) => {
    // ========================================================================
    // V1.2: AUTO-PLAY ON SELECT — one tap = immediate playback
    // No more double manipulation (select + play)
    // ========================================================================
    
    console.log('🎯 [Soundscapes][Select] sound=', {
      id: sound?.id,
      name: sound?.name,
      file: sound?.file,
      path: sound?.path,
      category: sound?.category,
      type: sound?.type,
      isFavorite: selectedStyle === 'favorites',
      isCurrentlyPlaying: isPlaying
    });
    
    // Resolve the audio file path from sound object
    const resolved = sound?.file || sound?.path || sound?.filename || null;
    console.log('🔗 [Soundscapes][Resolve] resolved=', resolved);
    
    // GUARD: Don't select ghost tracks (no valid audio file)
    if (!resolved && sound?.type !== 'generated') {
      console.error('❌ [Soundscapes][Select] BLOCKED: No valid file/path/filename for sound:', sound?.name);
      toast.error(t?.audioUnavailable || 'Audio temporairement indisponible', { duration: 8000 });
      return;
    }
    
    // V1.3 FIX: Update index ref for proper Next navigation with duplicate tracks
    if (index >= 0) {
      currentPlayingIndexRef.current = index;
      console.log('🎵 [Soundscapes][Select] Set current index to:', index);
    }
    
    // Update refs
    currentPlayingSoundRef.current = sound;
    
    // Set selected sound state
    setSelectedSound(sound);
    
    // V1.2 FIX: Reset explicit stop flag when starting new playback
    explicitStopRef.current = false;
    
    // If already playing, stop current track first
    if (isPlaying) {
      console.log('🔄 [Soundscapes][Select] Already playing - switching track...');
      if (sound.type === 'generated') {
        audioService.stopTherapy({ reason: 'track_change' });
      } else {
        soundscapeService.stop({ reason: 'track_change' });
      }
      therapyTimer.pause();
    }
    
    // V1.2: Always start playback immediately on selection
    console.log('▶️ [Soundscapes][Select] Starting playback...');
    await startPlayback(sound);
  };

  // Helper function to get translated name for a sound based on its ID/path
  const getTranslatedSoundName = (soundPath, originalName, category) => {
    // Map of generated sound IDs to translation keys
    const generatedSoundMap = {
      'sine_440': t?.pureTone440 || 'Pure Tone 440 Hz',
      'sine_1000': t?.pureTone1000 || 'Pure Tone 1000 Hz',
    };

    // Check if it's a generated sound by ID
    if (generatedSoundMap[soundPath]) {
      return generatedSoundMap[soundPath];
    }

    // Extract filename from path - handle multiple formats
    let filename = soundPath;
    let searchCategory = category;
    
    // Handle proxy URL format: /api/r2/stream/nature/Nature_002.m4a
    if (soundPath.startsWith('/api/r2/stream/')) {
      const parts = soundPath.split('/');
      searchCategory = parts[4]; // category from proxy URL
      filename = parts[5]; // filename.m4a
    } else if (soundPath.includes('/')) {
      const parts = soundPath.split('/');
      filename = parts[parts.length - 1]; // Get last part (filename)
    }

    // For library sounds, try to find the sound in the loaded library with current language
    if (searchCategory && soundLibrary[searchCategory]) {
      const librarySound = soundLibrary[searchCategory].find(s => {
        // Match by filename from path
        const soundFilename = s.file?.split('/').pop() || s.id;
        return soundFilename === filename || s.file === soundPath || s.id === soundPath;
      });
      
      if (librarySound) {
        return librarySound.name;
      }
    }

    // If category not provided or not found, search in all categories
    for (const cat of Object.keys(soundLibrary)) {
      const librarySound = soundLibrary[cat].find(s => {
        const soundFilename = s.file?.split('/').pop() || s.id;
        return soundFilename === filename || s.file === soundPath || s.id === soundPath;
      });
      
      if (librarySound) {
        return librarySound.name;
      }
    }

    // If not found, return original name
    return originalName;
  };
  // Get sounds list based on selected style/category
  let currentSounds;
  if (selectedStyle === 'playlist' && currentPlaylist) {
  // ========================================================================
  // V3.1 FIX: Normalize playlist sounds to canonical streaming format
  // Prevents playlist auto-advance failures caused by inconsistent paths
  // ========================================================================
  currentSounds = currentPlaylist.sounds.map(sound => {
    const path = decodeURIComponent(sound.path || sound.file || '');

    let category = sound.category?.toLowerCase();
    let filename = path;

    // Case 1: Already proxy URL
    if (path.startsWith('/api/r2/stream/')) {
      const parts = path.split('/');
      category = parts[4]?.toLowerCase();
      filename = decodeURIComponent(parts[5] || '');
    }

    // Case 2: Canonical category/file format
    else if (path.includes('/') && !path.startsWith('/')) {
      const parts = path.split('/');
      category = parts[0]?.toLowerCase();
      filename = parts.slice(1).join('/');
    }

    // Normalize extension
    filename = extToM4a(filename);

    // Build stable streaming URL
    const streamUrl = buildAudioUrl(category || 'classical', filename, false);

    console.log('🎵 [Playlist][Normalize]', {
      original: sound.path,
      category,
      filename,
      streamUrl
    });

    return {
      ...sound,
      id: sound.id || path,
      name: sound.name || filename,
      file: streamUrl,
      path,
      type: 'library',
      category: category || sound.category || 'classical'
    };
  });
  } else if (selectedStyle === 'favorites') {
  // ========================================================================
  // V3 FIX: Convert favorites to playable sound format
  // Uses canonical path: category/filename.m4a
  // ========================================================================
  currentSounds = favorites.map(fav => {
    // V1.1.1: Always decode path for consistent processing
    const path = decodeURIComponent(fav.sound?.path || '');
    let category, filename, streamUrl;

    console.log('⭐ [Favorites][Load] Processing:', { path, originalCategory: fav.sound?.category });
      // Case 1: Already a proxy URL (/api/r2/stream/category/filename.m4a)
      if (path.startsWith('/api/r2/stream/')) {
        const parts = path.split('/');
        category = parts[4]?.toLowerCase();
        filename = decodeURIComponent(parts[5] || '');
        streamUrl = buildAudioUrl(category, filename, false);
        console.log('⭐ [Favorites][Load] Proxy URL:', { category, filename, streamUrl });
      }
      // Case 2: Canonical format "category/filename.m4a" (e.g., "nature/Nature_001.m4a")
      else if (path.includes('/') && !path.startsWith('/')) {
        const parts = path.split('/');
        category = parts[0]?.toLowerCase();
        filename = parts.slice(1).join('/'); // Handle filenames with slashes
        filename = extToM4a(filename);
        streamUrl = buildAudioUrl(category, filename, false);
        console.log('⭐ [Favorites][Load] Canonical:', { category, filename, streamUrl });
      }
      // Case 3: Legacy format just "filename.m4a"
      else {
        filename = extToM4a(path.startsWith('/') ? path.slice(1) : path);
        category = (fav.sound?.category || 'classical').toLowerCase();
        streamUrl = buildAudioUrl(category, filename, false);
        console.log('⭐ [Favorites][Load] Legacy:', { category, filename, streamUrl });
      }
      
      return {
        id: path,
        name: getTranslatedSoundName(path, fav.sound?.name || filename, category),
        file: streamUrl, // Properly encoded streaming URL
        path: path,      // Original canonical path for reference
        type: 'library', // V3: Always use 'library' type for consistent playback
        category: category
      };
    });
    
    console.log('⭐ [Favorites][Load] Total favorites loaded:', currentSounds.length);
  } else {
    currentSounds = getTracksForTheme(selectedStyle);
  }

  // ========================================================================
  // P0 FIX: Filter out "ghost tracks" - sounds without valid audio files
  // Don't display items that would cause playback failures
  // ========================================================================
  currentSounds = currentSounds.filter(sound => {
    // Generated sounds (white/pink/brown noise, tones) are always valid
    if (sound.type === 'generated') {
      return true;
    }
    
    // Check if sound has a valid audio file reference
    const hasValidFile = !!(sound.file || sound.path || sound.filename);
    
    if (!hasValidFile) {
      console.warn('🚫 [Soundscapes][Filter] Ghost track removed:', {
        id: sound.id,
        name: sound.name,
        file: sound.file,
        path: sound.path,
        filename: sound.filename
      });
    }
    
    return hasValidFile;
  });

  const toNativeAudioUrl = (path) => {
  if (!path) return null;

  if (path.startsWith('http')) {
    return path;
  }

  if (path.startsWith('/api/r2/stream/')) {
    return `${API_BASE_URL}${path}`;
  }

  if (path.includes('/') && !path.startsWith('/')) {
    return `${API_BASE_URL}/api/r2/stream/${path}`;
  }

  return `${API_BASE_URL}/${path.replace(/^\/+/, '')}`;
};

  // Fullscreen mode when in player view - V1 UNIFIED PLAYER
  // Show UnifiedPlayer when viewMode is 'player' AND a sound is selected (not just when playing)
  // V1.2: Detect playlist/favorites mode to hide voice controls
  const isPlaylistOrFavoritesMode = selectedStyle === 'favorites' || selectedStyle === 'playlist' || !!currentPlaylist;
  if (viewMode === 'player' && selectedSound) {
    // Use category background image or fallback to nature image
    const playerBgImage = selectedCategory?.bgImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80';
    
    // V1.2 FIX: Use soundscapeService.currentTrack as ground truth for display
    // This ensures the player ALWAYS shows the actually-playing track, even if selectedSound is stale
    const actualTrack = soundscapeService.currentTrack || selectedSound;
    
    // Prepare current track info for UnifiedPlayer
    const currentTrackInfo = actualTrack ? {
      display_name: actualTrack.display_name || actualTrack.name,
      display_name_fr: actualTrack.translations?.fr || actualTrack.display_name || actualTrack.name,
      display_name_en: actualTrack.translations?.en || actualTrack.display_name || actualTrack.name,
      filename: actualTrack.file || actualTrack.filename,
      url: actualTrack.url
    } : null;
    
    console.log('🎛️ [HEAREN P1] equalizerSettings snapshot', equalizerSettings);
    console.log('🎛️ [HEAREN P1] labProgram snapshot', labProgram);

    // Prepare tinnitus info from equalizerSettings or labProgram
    // V1.1 FIX: Check notchEnabled (current toggle state) not just has_inverse_notch (test exists)
    console.log('[Tinnitus DEBUG]', {
      notchEnabled,
      has_inverse_notch: equalizerSettings?.has_inverse_notch,
      labProgramEnabled: labProgram?.enabled,
    });
    const canonicalTinnitusProfile = getCanonicalTinnitusProfile({
      equalizerSettings,
      labProgram,
    });

    const hasTinnitusTest = canonicalTinnitusProfile.hasTest;

    const isTinnitusActive = notchEnabled && hasTinnitusTest;

    const tinnitusInfo = hasTinnitusTest ? {
      frequency: canonicalTinnitusProfile.frequency || 8000,
      gain: canonicalTinnitusProfile.gain !== null ? quantizeDb(canonicalTinnitusProfile.gain) : null,
      bandwidth: canonicalTinnitusProfile.bandwidth,
      type: labProgram?.program_type === 'notch' ? 'notch' : 'inverse',
      active: isTinnitusActive,
      source: canonicalTinnitusProfile.source,
    } : null;
    
    const matrixTinnitusProfile = {
      hasTinnitusTest: canonicalTinnitusProfile.hasTest,
      frequency: canonicalTinnitusProfile.frequency,
      gain: canonicalTinnitusProfile.gain,
      bandwidth: canonicalTinnitusProfile.bandwidth,
      source: canonicalTinnitusProfile.source,
      active: isTinnitusActive,
    };
    
// ======================================================
// HEAREN — Gain compression (psychoacoustic)
// ======================================================
    
    // Prepare audio profile info from equalizerSettings
    // Always show AUDIO_BANDS_COUNT frequencies (total tested) even if fewer have active compensation
    const hasAudioProfile = !!(equalizerSettings?.has_audiometric_compensation);
    const audioProfileInfo = hasAudioProfile ? {
      frequencies: equalizerSettings?.audiometric_gains || [],
      nbFrequences: equalizerSettings?.audiometric_gains?.length || AUDIO_BANDS_COUNT,
      applied: equalizerEnabled,
    } : null;
    
    // V1.1: currentLang now comes from useLanguage hook (reactive)
    
    return (
      <>
      <UnifiedPlayer
        // Context
        context={currentPlaylistRef.current ? 'playlist' : 'soundscape'}
        contextIcon={<ThemeIcon themeKey={selectedCategory?.id} size={24} />}
        contextName={
          selectedCategory?.name ||
          selectedCategory?.label ||
          (
            ['classical', 'classique', 'instrumental', 'instrumentale'].includes(String(selectedStyle || '').toLowerCase())
              ? 'Instrumental'
              : ['chill_lofi', 'chill', 'lofi'].includes(String(selectedStyle || '').toLowerCase())
                ? 'Lofi'
                : t?.yourSpace
          )
        }
        
        // Current track
        currentTrack={currentTrackInfo}
        
        // Playback state
        isPlaying={isPlaying}
        isLoading={isLoadingPlayback}
        currentTime={therapyTimer.getElapsedTime?.() || 0}
        duration={selectedSound?.duration || 0}
        
        // Callbacks
        // V1 FUNDAMENTALS:
        // Back navigation is disabled in the therapeutic player.
        // A session must now be ended only through Stop,
        // with a soft fade-out and clean return to Home.
        // Previous V2 behavior is kept below for reference.

        /*
        onBack={() => {
          keepAudioAliveOnBackRef.current = true;

          window.__HEAREN_KEEP_PRESENCE = true;

          console.log('[TherapyPlayer][Back] return Home, keep audio alive', {
            selectedSound,
            selectedStyle,
            isPlaying,
          });
        }}
        */

        // Callbacks
        // V1 FUNDAMENTALS:
        // Back navigation disabled.
        // Session must be ended only through Stop.
        onBack={undefined}

        onPlayPause={handlePlayPause}
        onStop={handleStop}
        onPrev={null}

        // V1: Next button triggers random track change in soundscapes (NO voice trigger)
        onNext={() => {
          if (!currentPlaylist) {
            console.log('[Soundscape] Manual next - selecting random track');
            handleManualNextSoundscape();
          }
        }}
        onSeek={null}
        onFavoriteToggle={() => {
          // BUILD 55: Enhanced debug logging for favorite toggle
          console.log('[Favorites][Player] onFavoriteToggle called');
          console.log('[Favorites][Player] selectedSound:', {
            id: selectedSound?.id,
            name: selectedSound?.name,
            path: selectedSound?.path,
            category: selectedSound?.category,
            file: selectedSound?.file,
            type: selectedSound?.type
          });
          
          if (selectedSound) {
            toggleFavorite(selectedSound);
          } else {
            console.warn('[Favorites][Player] No selectedSound available');
          }
        }}
        
        // Favorite state - BUILD 52.6: Use canonical isFavorite function
        isFavorite={selectedStyle === 'favorites' ? true : isFavorite(selectedSound)}
        
        // Navigation controls - enable Next for soundscapes and favorites (random next track)
        hasPrev={false}
        // V1.3 FIX: Enable Next for favorites too (check currentSounds length, not just soundLibrary)
        hasNext={!currentPlaylist && (selectedStyle === 'favorites' ? favorites.length > 1 : getTracksForTheme(selectedStyle)?.length > 1)}
        showPrevNext={!currentPlaylist && (selectedStyle === 'favorites' ? favorites.length > 1 : getTracksForTheme(selectedStyle)?.length > 1)}
        
        // Volume - sync UI state with actual audio volume
        volume={volume}
        onVolumeChange={(newVolume) => {
          // V1.4: Apply listening comfort limits
          const safeVolume = applySafeVolume(newVolume);
          setVolume(safeVolume);
          // Apply volume to audio service (convert from 0-100 UI scale to 0-1 audio scale)
          audioService.setVolume(safeVolume / 100);
        }}
        
        // Acouphènes settings
        tinnitusEnabled={notchEnabled}
        onTinnitusToggle={() => {
          if (isFreeUser) {
            setShowPremiumModal(true);
          } else {
            const newVal = !notchEnabled;
            setNotchEnabled(newVal);
            //saveAudioToggles(equalizerEnabled, newVal);
          }
        }}
        tinnitusInfo={tinnitusInfo}
        tinnitusLocked={isFreeUser}
        hasTinnitusData={hasTinnitusTest}
        
      audioProfileEnabled={equalizerEnabled}
      onAudioProfileToggle={() => {
        if (isFreeUser) {
          setShowPremiumModal(true);
        } else {
         const newVal = !equalizerEnabled;
          setEqualizerEnabled(newVal);
          //saveAudioToggles(newVal, notchEnabled);
        }
      }}
      audioProfileInfo={audioProfileInfo}
      audioProfileLocked={isFreeUser}
      hasAudioProfileData={hasAudioProfile}
      onAudioProfileClick={() => {
        console.log('🔥 CLICK REÇU DANS THERAPY PLAYER');
        setShowSpectrumPanel(true);
      }}

      onShowPremiumModal={() => setShowPremiumModal(true)}

      isPresenceMode={(isPlaylistOrFavoritesMode || selectedStyle === 'deep_calm') ? false : isPresenceMode}
      isVoiceActive={(isPlaylistOrFavoritesMode || selectedStyle === 'deep_calm') ? false : isVoiceActive}
      presenceLabel={currentLang === 'fr' ? 'Présence · Hera' : 'Presence · Hera'}
        
        // V1.1: Voice toggle — hidden for playlists/favorites and Deep Calm (non-verbal)
        voiceEnabled={selectedStyle === 'deep_calm' ? false : voiceEnabled}
        onVoiceToggle={
          (isPlaylistOrFavoritesMode || selectedStyle === 'deep_calm')
            ? undefined
            : () => {
              console.warn('[HERA][TOGGLE CLICKED RAW]');

              if (voiceToggleLockRef.current) {
                console.warn('[HERA][TOGGLE] ignored duplicate click');
                return;
              }

              voiceToggleLockRef.current = true;

              setTimeout(() => {
              voiceToggleLockRef.current = false;
              }, 500);

              const next = !voiceEnabledRef.current;

              console.warn('[HERA][TOGGLE]', {
                from: voiceEnabledRef.current,
                to: next,
              });

              setVoiceEnabled(next);
                voiceEnabledRef.current = next;

                localStorage.setItem(
                'hearen_voice_enabled',
                  String(next)
                );

                if (!next) {
                  console.warn('[HERA][TOGGLE] OFF');

                  presenceService.isPaused = true;
                  presenceService.isVoicePlaying = false;

                if (presenceService.stopVoicePlayback) {
                  presenceService.stopVoicePlayback();
                }


                } else {
                  console.warn('[HERA][TOGGLE] ON');

                  presenceService.isActive = true;
                  presenceService.isPaused = false;
                  presenceService.isVoicePlaying = false;

                  presenceService._trackCount = Math.max(presenceService._trackCount || 0, 1);
                  presenceService._tracksSinceTransition = Math.max(
                  presenceService._tracksSinceTransition || 0,
                  1
                  );

                   console.warn('[HERA][TOGGLE] ON STATE RESTORED', {
                      isActive: presenceService.isActive,
                      isPaused: presenceService.isPaused,
                      isVoicePlaying: presenceService.isVoicePlaying,
                      trackCount: presenceService._trackCount,
                      tracksSinceTransition: presenceService._tracksSinceTransition,
                   });
                }

                  // IMPORTANT:
                  // on NE reset PAS:
                  // _trackCount
                  // _tracksSinceTransition
                  // transition rhythm
                }
              }
        
        
        // V1.1.1: Voice volume — hidden for playlists/favorites and Deep Calm (non-verbal)
        voiceVolume={voiceVolume}
        onVoiceVolumeChange={(isPlaylistOrFavoritesMode || selectedStyle === 'deep_calm') ? undefined : handleVoiceVolumeChange}
        
        // Language
        lang={currentLang}
        
        // Background
        backgroundImage={playerBgImage}
        
        // V1.4: Safe Listening
        safeListeningStatus={safeListeningStatus}
        showSafeListeningBadge={showSafeListeningBadge}
        
        // Translations
        t={{
          noTrackSelected: t?.noTrackSelected || 'Aucun son sélectionné',
          back: t?.back || 'Retour',
          favorite: t?.favorite || 'Favori',
          play: t?.play || 'Lecture',
          pause: t?.pause || 'Pause',
          stop: t?.stop || 'Arrêter',
          previous: t?.previous || 'Précédent',
          next: t?.next || 'Suivant',
          tinnitusLabel: t?.tinnitusLabel || 'Acouphènes',
          audioProfileLabel: t?.audioProfileLabel || 'Profil audio',
          frequencies: t?.frequencies || 'fréquences',
          premiumFeature: t?.premiumFeature || 'Fonctionnalité Premium',
          endSession: t?.endSession || 'Fin de session'
        }}
      />
      
      {/* Comfort message — subtle ephemeral text after 3min of listening */}
      <ComfortMessage elapsedSeconds={elapsedSeconds} isPlaying={isPlaying} />

      <AudioProfileModal
        isOpen={showSpectrumPanel}
        onClose={() => setShowSpectrumPanel(false)}
        audioService={selectedSound?.type === 'generated' ? audioService : soundscapeService}
        isPlaying={isPlaying}
        equalizerSettings={equalizerSettings}
        equalizerEnabled={equalizerEnabled}
        setEqualizerEnabled={setEqualizerEnabled}
        notchEnabled={notchEnabled}
        setNotchEnabled={setNotchEnabled}
        labProgram={labProgram}
        onPlayPause={handlePlayPause}
        t={t}
        profileConfigured={hasAudioProfile}
        frequencyCount={audioProfileInfo?.nbFrequences || 0}
        audioProfileInfo={audioProfileInfo}
      />

      </>
    );
  }
  // Show loading screen when auto-loading playlist from URL
if (isLoadingPlaylist) {
  return (
    <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center relative">

      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#6F42EA] mb-4"></div>
        <p className="text-white/60">Chargement...</p>
      </div>
    </div>
  );
}

return (
  <div
    className="min-h-screen bg-[#0A0F1C] px-4 md:px-8 pb-72 relative overflow-hidden"
    style={{ paddingTop: '40px' }}
  >

    {/* Global keyframes for pulse animation */}
    <style>{`
      @keyframes pulsePlay {
        0%, 100% { transform: scale(1); box-shadow: 0 0 35px rgba(90,90,255,0.65); }
        50% { transform: scale(1.07); box-shadow: 0 0 50px rgba(111,66,234,0.85); }
      }
    `}</style>
      {/* V1.1: Soft limit message banner */}
      {limitMessage && (
        <div 
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in"
          style={{ animation: 'fadeSlideDown 300ms ease-out' }}
        >
          <div className="bg-gradient-to-r from-purple-600/90 to-violet-600/90 backdrop-blur-md px-6 py-3 rounded-xl border border-white/20 shadow-lg flex items-center gap-3 max-w-sm">
            <svg className="w-5 h-5 text-white/90 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white text-sm font-medium">{limitMessage}</span>
            <button 
              onClick={() => setLimitMessage(null)}
              className="text-white/70 hover:text-white ml-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Halo top-left violet premium */}
      <div className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] rounded-full pointer-events-none" />
      
      {/* Halo bottom-right bleu premium */}
      <div className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] rounded-full pointer-events-none" />
      
      {/* Grain fin premium */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay'
        }}
      />

      <div className="max-w-4xl mx-auto space-y-6 relative z-10">
        <Card className="bg-transparent rounded-2xl shadow-none border-0 mb-40">
          <CardHeader className="mb-4 px-0">
            <CardTitle 
              className="flex items-center gap-2 text-white font-semibold tracking-wide"
              style={{ 
                fontSize: '24px',
                filter: 'drop-shadow(0 6px 16px rgba(255,255,255,0.10))'
              }}
            >
              <Music className="text-white opacity-70" style={{ alignSelf: 'center' }} />
              {
                selectedCategory?.name ||
                selectedCategory?.label ||
                (
                  ['classical', 'classique', 'instrumental', 'instrumentale'].includes(String(selectedStyle || '').toLowerCase())
                    ? 'Instrumental'
                    : ['chill_lofi', 'chill', 'lofi'].includes(String(selectedStyle || '').toLowerCase())
                      ? 'Lofi'
                      : (t?.yourSpace || 'Ton espace')
                )
              }
            </CardTitle>
          </CardHeader>
          <CardContent style={{ paddingTop: '0', marginTop: '0' }}>
            {/* VIEW MODE: SELECTION - Audio V1.1 */}
            {viewMode === 'selection' && (
              <div style={{ marginTop: '8px' }}>
                <div 
                  className="overflow-y-auto max-h-[70vh] px-4 space-y-2"
                  style={{ 
                    paddingBottom: '96px',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {/* V1.1: MES PLAYLISTS - Entry Card (hidden in soundscapes-only mode) */}
                  {showPlaylistsEntry && (
                    <button
                      onClick={() => isFreeUser ? navigate('/premium') : navigate('/playlists')}
                      className={`w-full overflow-hidden group relative rounded-2xl bg-gradient-to-br from-[#6F42EA]/20 to-[#3B48EE]/15 backdrop-blur-[2px] border border-[#6F42EA]/30 transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] ${isFreeUser ? 'opacity-70' : ''}`}
                      style={{ 
                        minHeight: '88px',
                        boxShadow: '0 6px 25px rgba(111,66,234,0.35)',
                        animation: 'fadeSlideUp 200ms ease-out'
                      }}
                      data-testid="playlists-entry-btn"
                    >
                      {/* Premium lock badge */}
                      {isFreeUser && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm px-2 py-1 rounded-full z-10">
                          <Lock className="h-3 w-3 text-white" />
                          <span className="text-[10px] font-semibold text-white">Premium</span>
                        </div>
                      )}
                      {/* Content */}
                      <div className="relative flex items-center gap-4 text-left p-4">
                        <div className="bg-[#6F42EA]/30 backdrop-blur-md rounded-xl p-2 border border-[#6F42EA]/40 shadow-md flex-shrink-0">
                          <ListMusic className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div 
                            className="text-white font-semibold tracking-wide leading-tight text-base"
                            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.45))' }}
                          >
                            {t?.myPlaylists || 'Mes Playlists'}
                          </div>
                          <div className="text-white/60 text-sm mt-0.5">
                            {t?.playlistsSubtitle || 'Vos sons personnalisés'}
                          </div>
                        </div>
                        <div className="text-white/70 group-hover:text-white transition flex-shrink-0">
                          {isFreeUser ? (
                            <Lock className="w-5 h-5 text-amber-400/70" />
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  )}

                  {/* FAVORITES - Entry Point (V1.3) */}
                  {showPlaylistsEntry && (
                    <button
                      onClick={isFreeUser ? () => navigate('/premium') : async () => {
                        // BUILD 64: Reset loading states before switching
                        setIsLoadingCategory(false);
                        setCategoryLoadError(null);
                        
                        // Reload favorites before showing the list
                        try {
                          const freshFavorites = await api.getFavorites();
                          setFavorites(freshFavorites);
                          console.log('📌 Favorites reloaded:', freshFavorites.length);
                        } catch (error) {
                          console.error('Error reloading favorites:', error);
                        }
                        setSelectedStyle('favorites');
                        setViewMode('settings');
                      }}
                      className={`w-full overflow-hidden group relative rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-500/15 backdrop-blur-[2px] border border-amber-500/30 transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] ${isFreeUser ? 'opacity-70' : ''}`}
                      style={{ 
                        minHeight: '88px',
                        boxShadow: '0 6px 25px rgba(245,158,11,0.25)',
                        animation: 'fadeSlideUp 200ms ease-out 40ms both'
                      }}
                      data-testid="favorites-entry-btn"
                    >
                      {/* Premium lock badge */}
                      {isFreeUser && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm px-2 py-1 rounded-full z-10">
                          <Lock className="h-3 w-3 text-white" />
                          <span className="text-[10px] font-semibold text-white">Premium</span>
                        </div>
                      )}
                      {/* Content */}
                      <div className="relative flex items-center gap-4 text-left p-4">
                        <div className="bg-amber-500/30 backdrop-blur-md rounded-xl p-2 border border-amber-500/40 shadow-md flex-shrink-0">
                          <Star className="h-6 w-6 text-amber-300" />
                        </div>
                        <div className="flex-1">
                          <div 
                            className="text-white font-semibold tracking-wide leading-tight text-base"
                            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.45))' }}
                          >
                            {currentLang === 'en' ? 'Favorites' : 'Favoris'}
                          </div>
                          <div className="text-white/60 text-sm mt-0.5">
                            {currentLang === 'en' ? 'Your saved sounds' : 'Vos sons enregistrés'}
                            {favorites.length > 0 && (
                              <span className="ml-2 text-amber-400/80">({favorites.length})</span>
                            )}
                          </div>
                        </div>
                        <div className="text-white/70 group-hover:text-white transition flex-shrink-0">
                          {isFreeUser ? (
                            <Lock className="w-5 h-5 text-amber-400/70" />
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  )}

                  {/* SOUNDSCAPES - Primary Entry */}
                  {categories.filter(cat => cat.id !== 'favorites').map((cat, index) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        if (cat.locked) {
                          navigate('/premium');
                          return;
                        }
                        handleCategorySelect(cat.id);
                      }}
                      className={`w-full overflow-hidden group relative rounded-xl backdrop-blur-[2px] border transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] ${
                        cat.locked 
                          ? 'bg-white/3 border-white/5 opacity-70' 
                          : 'bg-white/5 border-white/10'
                      }`}
                      style={{ 
                        minHeight: '68px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.40)',
                        animation: `fadeSlideUp 200ms ease-out ${index * 40}ms both`
                      }}
                      data-testid={`theme-${cat.id}`}
                    >
                      {/* Background Image with overlays */}
                      {cat.bgImage ? (
                        <div className="absolute inset-0">
                          <img 
                            src={cat.bgImage} 
                            alt={cat.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            style={cat.locked ? { filter: 'grayscale(0.5) brightness(0.6)' } : undefined}
                          />
                          <div 
                            className="absolute inset-0" 
                            style={{ background: 'rgba(0, 0, 0, 0.45)' }}
                          />
                          <div 
                            className="absolute inset-0"
                            style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 50%, transparent 100%)' }}
                          />
                        </div>
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-600" />
                      )}
                      
                      {/* Content */}
                      <div className="relative flex items-center gap-4 text-left p-3">
                        <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5 border border-white/20 shadow-md flex-shrink-0">
                          <ThemeIcon themeKey={cat.id} size={20} />
                        </div>
                        <div className="flex-1">
                          <div 
                            className="text-white font-semibold tracking-wide leading-tight text-base"
                            style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.45))' }}
                          >
                            {cat.name}
                          </div>
                        </div>
                        {cat.locked ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-amber-300/80 font-medium">Premium</span>
                            <Lock className="w-4 h-4 text-amber-300/80" />
                          </div>
                        ) : (
                          <div className="text-white/70 group-hover:text-white transition flex-shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Animations CSS */}
                <style>{`
                  @keyframes fadeSlideUp {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
              </div>
            )}

            {/* VIEW MODE: SETTINGS - Settings page with category header */}
            {viewMode === 'settings' && (() => {
              const selectedCategory = categories.find(cat => cat.id === selectedStyle);
              
              return (
                <div className="space-y-6">
                  {/* Category Header Premium - Glass Card */}
                  <div 
                    className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[8px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-3 py-3 mx-2 mt-2"
                    style={{
                      animation: 'fadeSlideUp 250ms ease-out'
                    }}
                  >
                    {/* Overlay gradient léger */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#3B48EE]/25 via-transparent to-[#6F42EA]/25 pointer-events-none" />
                    
                    {/* Background image si disponible */}
                    {selectedCategory?.bgImage && (
                      <>
                        <img 
                          src={selectedCategory.bgImage} 
                          alt={selectedCategory.name}
                          className="absolute inset-0 w-full h-full object-cover opacity-20"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0F1C]/60 to-[#0A0F1C]/90" />
                      </>
                    )}
                    
                    <div className="relative z-10 flex items-center justify-between gap-2">
                      {/* Bouton Back premium */}
                      <button
                        onClick={async () => {
                          if (themeFromUrl === 'favorites' || themeFromUrl === 'playlist' || playlistIdFromUrl) {
                            navigate('/favorites', { replace: true });
                            return;
                          }
                          if (themeFromUrl) {
                            navigate('/', { replace: true });
                            return;
                          }
                          // Reload favorites count when returning to selection
                          try {
                            const freshFavorites = await api.getFavorites();
                            setFavorites(freshFavorites);
                          } catch (e) {
                            console.error('Error refreshing favorites:', e);
                          }
                          setViewMode('selection');
                        }}
                        className="inline-flex items-center gap-1.5 text-white/80 text-sm bg-white/10 rounded-2xl px-2.5 py-1.5 backdrop-blur-md border border-white/15 hover:bg-white/15 transition-all duration-200 shrink-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="font-medium">{t?.back || 'Retour'}</span>
                      </button>
                      
                      {/* Icône + titre au centre/droite */}
                      <div className="flex items-center gap-2 min-w-0 justify-end">
                        {/* Icône dans carré glass */}
                        <div className="bg-white/12 backdrop-blur-md rounded-2xl p-2.5 border border-white/15 shadow-md shrink-0">
                          <ThemeIcon themeKey={selectedCategory?.id} size={28} />
                        </div>
                        
                        <div className="text-right min-w-0">
                          <h2 className="text-white font-semibold tracking-wide text-base drop-shadow-[0_4px_14px_rgba(0,0,0,0.65)] truncate">
                            {selectedCategory?.name}
                          </h2>
                          {selectedCategory?.subtitle && (
                            <p className="text-white/70 text-xs mt-0.5 truncate">{selectedCategory?.subtitle}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* V1.2: Play button removed — tracks auto-play on tap */}

                  {/* Sound selector - Direct list */}
                  <div className="px-4">
                    {/* Informative text */}
                    {currentSounds.length > 0 && (
                      <p className="text-white/50 text-sm text-center mb-3">
                        {t?.locale === 'fr-FR' 
                          ? 'Appuyez sur un son pour l\'écouter' 
                          : 'Tap a sound to play'}
                      </p>
                    )}
                    
                    <div className="rounded-2xl bg-white/5 border border-white/10 max-h-60 overflow-y-auto backdrop-blur-sm">
                      {/* BUILD 64: Show skeleton while loading category */}
                      {(isLoadingSounds || isLoadingCategory) ? (
                        <div className="p-3">
                          <TrackListSkeleton count={5} />
                          <p className="text-white/50 text-xs text-center mt-2">
                            {currentLang === 'en' ? 'Loading sounds...' : 'Chargement des sons...'}
                          </p>
                        </div>
                      ) : categoryLoadError ? (
                        // BUILD 64: Error state with retry button
                        <div className="p-6 text-center space-y-4">
                          <p className="text-white/70 text-sm">
                            {currentLang === 'en' 
                              ? 'Unable to load sounds' 
                              : 'Impossible de charger les sons'}
                          </p>
                          <button
                            onClick={() => handleCategorySelect(selectedStyle)}
                            className="px-4 py-2 bg-[#6F42EA]/30 hover:bg-[#6F42EA]/40 text-white text-sm rounded-xl border border-[#6F42EA]/40 transition-all"
                          >
                            {currentLang === 'en' ? 'Retry' : 'Réessayer'}
                          </button>
                        </div>
                      ) : currentSounds.length === 0 ? (
                        <div className="p-6 text-center">
                          {selectedStyle === 'favorites' ? (
                            // Empty state for Favorites with CTA
                            <div className="space-y-4">
                              <Star className="w-12 h-12 mx-auto text-amber-500/50" />
                              <p className="text-white/70 text-sm">
                                {currentLang === 'en' 
                                  ? 'No favorites yet' 
                                  : 'Aucun favori pour le moment'}
                              </p>
                              <p className="text-white/50 text-xs">
                                {currentLang === 'en' 
                                  ? 'Add sounds to your favorites by tapping the star icon while listening.' 
                                  : 'Ajoutez des sons à vos favoris en appuyant sur l\'étoile pendant l\'écoute.'}
                              </p>
                              <button
                                onClick={() => handleCategorySelect('classical')}
                                className="mt-2 px-4 py-2 bg-[#6F42EA]/30 hover:bg-[#6F42EA]/40 text-white text-sm rounded-xl border border-[#6F42EA]/40 transition-all"
                              >
                                {currentLang === 'en' ? 'Browse themes' : 'Parcourir les sons'}
                              </button>
                            </div>
                          ) : selectedStyle === 'playlist' ? (
                            <p className="text-white/50 text-sm">
                              {currentLang === 'en' ? 'Empty playlist - Add sounds from Playlists page' : 'Playlist vide - Ajoutez des sons depuis la page Playlists'}
                            </p>
                          ) : (
                            <p className="text-white/50 text-sm">
                              {t?.noSoundsInCategory || 'Aucun son disponible dans cette catégorie'}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2 p-2">
                          {currentSounds.map((sound, idx) => {
                            const isSelected = selectedSound?.id === sound.id;
                            // BUILD 52.6: Use canonical isFavorite function (no manual path construction)
                            const isFav = selectedStyle === 'favorites' ? true : isFavorite(sound);
                            
                            return (
                              <div
                                key={`${sound.id}-${idx}`}
                                className={`relative overflow-hidden rounded-2xl backdrop-blur-[6px] border px-4 py-2.5 flex items-center justify-between gap-3 cursor-pointer transition-all duration-200 ${
                                  isSelected 
                                    ? 'bg-white/10 border-[#6F42EA]/60 ring-1 ring-[#6F42EA]/60 shadow-[0_0_20px_rgba(111,66,234,0.3)]' 
                                    : 'bg-white/5 border-white/10 hover:bg-white/8 hover:border-white/20'
                                }`}
                                onClick={() => handleSoundSelect(sound, idx)}
                                style={{
                                  animation: isSelected ? 'none' : `fadeSlideUp 200ms ease-out ${idx * 30}ms both`
                                }}
                              >
                                {/* Overlay gradient pour ligne active */}
                                {isSelected && (
                                  <div className="absolute inset-0 bg-gradient-to-r from-[#3B48EE]/25 to-[#6F42EA]/15 pointer-events-none" />
                                )}
                                
                                {/* V1.2: Play indicator icon (not a separate button — whole row is tappable) */}
                                <div className={`relative z-10 flex-shrink-0 rounded-full p-1.5 flex items-center justify-center border transition-all ${
                                  isSelected
                                    ? 'bg-[#6F42EA] border-[#6F42EA]'
                                    : 'bg-white/10 border-white/20'
                                }`}>
                                  {isSelected && isPlaying ? (
                                    <Pause className="h-3 w-3 text-white/90" fill="currentColor" />
                                  ) : (
                                    <Play className="h-3 w-3 text-white/80 ml-0.5" fill="currentColor" />
                                  )}
                                </div>
                                
                                {/* Nom du son */}
                                <span className={`relative z-10 flex-1 text-sm leading-snug ${
                                  isSelected ? 'font-semibold text-white' : 'text-white/90'
                                }`}>
                                  {sound.name}
                                </span>
                                
                                {/* V1.3: Bouton corbeille dans la vue favoris, sinon bouton étoile */}
                                {selectedStyle === 'favorites' ? (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      // Remove from favorites
                                      try {
                                        await api.removeFavorite(sound.path || sound.id);
                                        // Refresh favorites list
                                        const freshFavorites = await api.getFavorites();
                                        setFavorites(freshFavorites);
                                        console.log('🗑️ Removed from favorites:', sound.name);
                                        // If this was the selected sound, deselect it
                                        if (selectedSound?.id === sound.id) {
                                          setSelectedSound(null);
                                        }
                                      } catch (error) {
                                        console.error('Error removing favorite:', error);
                                      }
                                    }}
                                    className="relative z-10 flex-shrink-0 p-1.5 rounded-full bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 hover:scale-110 transition-all"
                                    title={t?.removeFromFavorites || 'Retirer des favoris'}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(sound);
                                    }}
                                    className="relative z-10 flex-shrink-0 text-xl hover:scale-110 transition-transform"
                                    title={isFav ? (t?.removeFromFavorites || 'Retirer des favoris') : (t?.addToFavorites || 'Ajouter aux favoris')}
                                  >
                                    <span className={isFav ? 'text-yellow-300/80' : 'text-white/40'}>
                                      {isFav ? '⭐' : '☆'}
                                    </span>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Session Timer Control Premium */}
                  <div className="px-4 pt-4 space-y-3 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-white">⏱️ {t?.sessionTimer || 'Timer de session'}</h3>
                        {fromCoach && (
                          <span className="text-xs font-medium bg-[#3B48EE]/30 text-[#6F42EA] px-2 py-0.5 rounded-xl border border-[#6F42EA]/30 backdrop-blur-md">
                            🎯 Recommandé Coach
                          </span>
                        )}
                      </div>
                      {currentSessionStart && sessionDuration && (() => {
                        const elapsedSinceStart = sessionStartTime ? (Date.now() - sessionStartTime) / 60000 : 0; // minutes since current play started
                        const totalElapsed = elapsedTimeBeforePause + elapsedSinceStart; // total elapsed including pauses
                        const remainingMin = Math.max(0, sessionDuration - totalElapsed);
                        const progressPercent = Math.min(100, (totalElapsed / sessionDuration) * 100);
                        const elapsedFormatted = Math.floor(totalElapsed);
                        const remainingFormatted = Math.ceil(remainingMin);
                        
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-[#6F42EA] font-medium">
                              ⏳ {remainingFormatted}min {t?.remaining || 'restantes'}
                            </span>
                            <span className="text-xs text-white/50">
                              {elapsedFormatted}min / {sessionDuration}min ({progressPercent.toFixed(0)}%)
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Progress Bar Premium when timer is active */}
                    {currentSessionStart && sessionDuration && (() => {
                      const elapsedSinceStart = sessionStartTime ? (Date.now() - sessionStartTime) / 60000 : 0; // minutes since current play started
                      const totalElapsed = elapsedTimeBeforePause + elapsedSinceStart; // total elapsed including pauses
                      const progressPercent = Math.min(100, (totalElapsed / sessionDuration) * 100);
                      
                      return (
                        <div className="relative w-full h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
                          <div
                            className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#3B48EE] to-[#6F42EA] transition-all duration-1000 ease-linear shadow-[0_0_10px_rgba(111,66,234,0.6)]"
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                      );
                    })()}
                    
                    <select
                      value={sessionDuration || ''}
                      onChange={(e) => setSessionDuration(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#6F42EA] focus:border-transparent text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isPlaying}
                    >
                      <option value="">{t?.unlimited || 'Sans limite'}</option>
                      <option value="5">5 minutes</option>
                      <option value="10">10 minutes</option>
                      <option value="15">15 minutes</option>
                      <option value="20">20 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                    </select>
                  </div>

                  {/* Audio Processing Toggles section removed - now only in fullscreen player */}
                </div>
              );
            })()}

            {/* VIEW MODE: PLAYER - Premium autoplay loading state */}
            {viewMode === 'player' && !selectedSound && autoplayFromUrl && (
              <div
                className="flex flex-col items-center justify-center pt-24 space-y-5"
                data-testid="autoplay-loading"
              >
                <div className="relative w-24 h-24">
                  <div className="absolute inset-0 rounded-full bg-indigo-500/10 animate-hearenPulse" />
                  <div className="absolute inset-2 rounded-[45%_55%_60%_40%/60%_40%_60%_40%] bg-indigo-400/40 animate-hearenMorph" />
                  <div className="absolute inset-6 rounded-full bg-indigo-300/60" />
                </div>

                <p className="text-white/70 text-sm text-center">
                  {t?.locale === 'en-US'
                    ? 'Preparing your sound environment…'
                    : 'Préparation de votre environnement sonore…'}
                </p>

                <p className="text-white/40 text-xs text-center">
                  {t?.locale === 'en-US'
                    ? 'Optimizing'
                    : 'Optimisation en cours'}
                </p>

                <div className="h-1 w-16 bg-indigo-400/40 rounded-full animate-pulse blur-sm" />
              </div>
            )}
            {/* VIEW MODE: PLAYER - Full therapy player */}
            {viewMode === 'player' && selectedSound && (
              <div className="space-y-6 pt-6">
    
                {/* Now Playing Title */}
                <div className="text-center">
                  <div className="text-white/80 text-sm font-medium mb-2">
                    🎵{selectedSound.name || selectedSound.display_name || (t?.locale === 'fr-FR' ? 'Lecture en cours...' : 'Now playing...')}
                  </div>
                  <ContentBadge type="soundscape" />
                </div>

                {/* 🔥 HEAREN FIELD (NEW CORE VISUAL) */}
                <div className="mt-4">
                  <HearenField
                    isPlaying={isPlaying}
                    eqEnabled={equalizerEnabled}
                    notchEnabled={notchEnabled}
                  />
                </div>

                {/* 🎯 AUDIO PROFILE INFO (clean + premium) */}
               <div className="text-center space-y-1">
                  <p className="text-white text-sm font-medium">
                    {t?.audioProfileLabel || 'Profil audio'}
                  </p>

                  <p className="text-white/50 text-xs">
                     {AUDIO_BANDS_COUNT || 7} bandes actives
                     {notchEnabled && ' · Acouphène actif'}
                  </p>
                </div>

                
                {/* Playlist Progress Indicator (if applicable) */}
                {currentPlaylist && (
                  <div className="bg-white/15 backdrop-blur-md rounded-2xl px-5 py-3 shadow-lg border border-white/10 mx-4">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <p className="text-white/90 font-semibold text-sm flex items-center gap-2">
                        🎼 {currentPlaylist.name}
                      </p>
                      <p className="text-white/70 text-xs">
                        {currentPlaylistIndex + 1} / {currentPlaylist.sounds.length} {t?.sounds || 'sons'}
                      </p>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-[#3B48EE] to-[#6F42EA] h-1.5 rounded-full transition-all duration-300 shadow-[0_0_8px_rgba(111,66,234,0.6)]"
                        style={{ width: `${((currentPlaylistIndex + 1) / currentPlaylist.sounds.length) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Circular Therapy Timer */}
                <div className="flex justify-center">
                  <CircularTherapyTimer
                    minutes={Math.floor(elapsedTimeBeforePause)}
                    totalMinutes={sessionDuration || 60}
                    isPlaying={isPlaying}
                  />
                </div>

                {/* Coach Banner */}
                {fromCoach && (
                  <div className="mx-4">
                    <CoachTherapyBanner
                      protocol={sessionProtocol}
                      duration={recommendedDuration}
                      date={sessionDate}
                      t={t}
                    />
                  </div>
                )}

                {/* Volume musique fixé à 80% — slider supprimé */}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fullscreen Timer Modal */}
      {showFullscreenTimer && (
        <FullscreenTimerModal
          isOpen={showFullscreenTimer}
          onClose={() => setShowFullscreenTimer(false)}
          currentSeconds={sessionElapsedSeconds}
          goalSeconds={sessionTargetSeconds}
          variant={selectedStyle}
        />
      )}

      {/* Premium Upgrade Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <div className="w-full max-w-2xl bg-gradient-to-br from-[#1a1f2e] to-[#252b3d] border-2 border-[#6F42EA]/30 rounded-3xl shadow-[0_30px_100px_rgba(111,66,234,0.7)] overflow-hidden animate-in zoom-in-95 duration-300">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] p-8 pb-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="w-10 h-10 text-white" />
                  <div>
                    <h2 className="text-3xl font-bold text-white">Hearen Premium</h2>
                    <p className="text-white/90 text-sm mt-1">
                      Unlock all features
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-all"
                >
                  <Plus className="w-6 h-6 text-white rotate-45" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      All Soundscapes
                    </h3>
                    <p className="text-white/70 text-sm mt-1">
                      Unlock access to all soundscapes: Chill, Focus, Nature, Relax, Sleep, Classical and more
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      Unlimited Playlists
                    </h3>
                    <p className="text-white/70 text-sm mt-1">
                      Create unlimited playlists with unlimited sounds for personalized therapy sessions
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      Full Sound Library
                    </h3>
                    <p className="text-white/70 text-sm mt-1">
                      Access the entire therapeutic sound library with more personalized listening features
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      Advanced Customization
                    </h3>
                    <p className="text-white/70 text-sm mt-1">
                      Equalizer, audio compensation, personalized notch filters and much more
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => {
                  setShowPremiumModal(false);
                  navigate('/premium');
                }}
                className="w-full bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] hover:from-[#5F32DA] hover:to-[#7B4CE6] text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-[0_8px_30px_rgba(111,66,234,0.5)] hover:shadow-[0_12px_40px_rgba(111,66,234,0.7)] hover:scale-105 flex items-center justify-center gap-3"
              >
                <Crown className="w-5 h-5" />
                <span className="text-lg">
                  {localStorage.getItem('hearen_lang') === 'en' ? 'Upgrade to Premium Now' : 'Passer à Premium maintenant'}
                </span>
              </button>

              <p className="text-center text-white/50 text-xs">
                {localStorage.getItem('hearen_lang') === 'en' ? 'You will be redirected to subscription settings' : 'Vous serez redirigé vers les paramètres d\'abonnement'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Unified Audio Controls - Fixed at bottom (only in player mode) */}
      {(() => {
        console.log('🎮 [TherapyPlayer] Controls render check:', {
          viewMode,
          selectedSound: !!selectedSound,
          isPlaying,
          shouldRender: viewMode === 'player' && selectedSound
        });
        
        if (viewMode === 'player' && selectedSound) {
          return (
            <>
            {/* Audio Controls - Fixed at bottom */}
<div className="fixed bottom-0 left-0 right-0 z-[200] bg-gradient-to-t from-black/90 via-black/70 to-transparent pb-safe pt-4 px-4">
  <div className="max-w-4xl mx-auto space-y-3">

    <div className="flex items-center justify-center">
      {/* PLAY/PAUSE Button */}
      <button
        onClick={handlePlayPause}
        disabled={isLoadingPlayback}
        className="group flex flex-col items-center gap-2 transition-all duration-200 hover:scale-110 active:scale-95"
      >
        <div
          className={`w-14 h-14 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center group-hover:bg-white/30 group-hover:border-white/50 transition-all duration-200 shadow-lg ${isLoadingPlayback ? 'cursor-wait' : ''}`}
          style={isLoadingPlayback ? { animation: 'pulsePlay 1.5s ease-in-out infinite' } : undefined}
        >
          {isLoadingPlayback ? (
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
          ) : isPlaying ? (
            <Pause size={24} className="text-white" fill="currentColor" />
          ) : (
            <Play size={24} className="text-white ml-1" fill="currentColor" />
          )}
        </div>

        <span className="text-xs text-white/80 font-medium">
          {isLoadingPlayback ? '...' : isPlaying ? 'Pause' : 'Play'}
        </span>
      </button>
    </div>
  </div>
</div>
            </>
          );
        }
        return null;
      })()}
      {/* V1.1.1: Debug overlay — only with ?debug=transitions */}
      {searchParams.get('debug') === 'transitions' && <TransitionDebug />}
    </div>
  );
};

export default TherapyPlayer;
